// apps/ystnj/src/components/ai/AdmissionsAIAssistant.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  Bot,
  FileCode2,
  FileSpreadsheet,
  FileText,
  History,
} from "lucide-react";
import {
  AIAssistant,
  AIAssistantPrototypeRouteCanvas,
  AIAssistantPrototypeShell,
  AIAssistantSchoolShellContextMenuContent,
  type AssistantInputAction,
  type AssistantPresentationModel,
  AIAssistantModelSelector,
  DesignSystemProvider,
  IntellioAuthorityBanner,
  IntellioStatusStrip,
  IntellioTaskShelf,
  type AssistantPrompt as AssistantPromptUi,
  type AIAssistantPrototypePreviewPane,
  type AIAssistantPrototypeSurface,
  type AIAssistantPrototypeUtilityTab,
  type AIAssistantPrototypeWidthMode,
  type AIAssistantRecentItem,
  type AIAssistantWorkspaceState,
  type AIAssistantWorkspaceTabId,
  type IntellioTaskRecord,
  normalizeAssistantPresentation,
  useAssistantPanel,
  useAssistantConversation,
  useModelSelector,
  type AIAssistantMessageProps,
  type AssistantMessage,
  type AssistantPresentationArtifactItem,
} from "@triedge/ai-assistant-ui";
import { projectAssistantConversationRecentItems } from "@triedge/ai-assistant-ui/feature-bundle";
import {
  buildAssistantGoogleSheetsExportInput,
  isBlankGoogleSheetsUrl,
} from "@triedge/domain-core";
import type {
  IntellioAssistantConversationMessageRecord,
  IntellioAssistantConversationSessionRecord,
  IntellioAssistantWorkItemRecord,
} from "@triedge/domain-core";
import {
  selectAssistantRealtimeState,
  selectAssistantRealtimeLatestWorkItems,
  selectAssistantRealtimeQueue,
} from "@triedge/rtk-abstractions";
import {
  buildDataSourcesLabel,
  getAIAssistantModuleConfig,
  resolveAIAssistantModule,
} from "./aiModuleConfig";
import {
  parseAssistantResponseVisualFixtureScenarioFromSearch,
  resolveAssistantResponseVisualFixtureState,
} from "./assistantResponseVisualFixtures";
import { buildAdmissionsContextSummary } from "./admissionsContextSummary";
import { assistantInputActions } from "./assistantInputActions";
import { createAssistantToolRegistry } from "./assistantToolRegistry";
import { useAdmissionsAssistantContext } from "./useAdmissionsAssistantContext";
import { useAssistantSceneController } from "./useAssistantSceneController";
import {
  useDomainRouterQuery,
  useDomainRouterMutation,
  useLazyDomainRouterQuery,
} from "../../state/domainRouterHooks";
import { readDomainRouterErrorMessage } from "../../state/domainRouterError";
import { runAssistantRequestWithTransientRetry } from "./assistantTransientRetry";
import { buildLiveAssistantResponseRenderMetadata } from "./assistantLiveResponseRenderMetadata";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import {
  hydrateAssistantSessionState,
  retainYstnjAssistantRealtimeBridge,
} from "../../state/assistantRealtimeBridge";
import type { RootState } from "../../state/store";
import { trackLogRocketEvent } from "../../telemetry/logrocket";
import {
  ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY,
  ADMISSIONS_BEDROCK_MODEL_SELECTION_OPTIONS,
} from "../../config/ai-assistant/bedrock-model-routing";
import {
  listAssistantCapabilitiesForContext,
  normalizeAssistantPersona,
  resolveAssistantCapabilityById,
  resolveAssistantCapabilityScenarioKey,
  type AssistantCapabilityCatalogScope,
  type AssistantCapabilityDefinition,
} from "../../config/ai-assistant/capability-matrix";
import {
  collectBrowserCapabilities,
  resolveAssistantExecutionPlan,
  resolveAssistantPreferredRuntime,
  type AssistantRuntimePolicyHint,
} from "../../config/ai-assistant/execution-policy";
import { listAssistantToolsForContext } from "../../config/ai-assistant/tool-registry";
import {
  isAssistantRouteTemplatePathname,
  routeMatchesAnyAssistantPattern,
} from "../../config/ai-assistant/route-matching";
import { type AssistantDataStateSignal } from "../../config/ai-assistant/prompt-matrix-snapshot";
import {
  SETTINGS_SPECIALIST_METHOD,
  isSettingsIntentTurn,
  isSettingsRouteFamilyPath,
} from "../../config/ai-assistant/settings-specialist-intent";
import {
  sanitizeAwsJsonArray,
  sanitizeAwsJsonObject,
} from "./awsJsonSanitizer";
import { AdmissionsAssistantMessage } from "./AdmissionsAssistantMessage";
import { AssistantCapabilityBrowserModal } from "./AssistantCapabilityBrowserModal";
import { AssistantToolToolbar } from "./AssistantToolToolbar";
import { AssistantChartWorkspace } from "./AssistantChartWorkspace";
import { AssistantVisualization3DWorkspace } from "./AssistantVisualization3DWorkspace";
import { AssistantSpreadsheetComposerStatus } from "./AssistantSpreadsheetComposerStatus";
import { AssistantSpreadsheetWorkspace } from "./AssistantSpreadsheetWorkspace";
import { resolveVisibleActiveSpreadsheetTask } from "./assistantSpreadsheetComposerState";
import { AssistantThreadsBrowser } from "./AssistantThreadsBrowser";
import { AssistantConversationSessionLead } from "./AssistantConversationSessionLead";
import { AssistantSettingsSpecialistLead } from "./AssistantSettingsSpecialistLead";
import {
  coerceChartPanels,
  resolveAssistantChartArtifactState,
} from "./assistantChartArtifactState";
import { coerceVisualization3DSpace } from "./assistantVisualization3DArtifactState";
import { collapseAssistantTaskThreadReplies } from "./assistantTaskThreadMessageConsolidation";
import {
  isConversationAnnouncementTaskTerminal,
  selectConversationAnnouncementTaskCandidates,
} from "./assistantConversationTaskAnnouncementCandidates";
import { resolveConversationThreadIdForTask } from "./assistantConversationTaskThreadIdentity";
import { resolveAssistantCapabilityBrowserActionState } from "./assistantCapabilityBrowser";
import { AssistantCitationDetail } from "./AssistantCitationDetail";
import { AssistantProactiveTray } from "./AssistantProactiveTray";
import { toUserSafeAssistantErrorMessage } from "./assistantErrorSanitization";
import { classifyAssistantTaskFailure } from "../../shared/assistant-task-failure-classification";
import {
  buildExplicitPromptBindingFromAssistantPrompt,
  buildExplicitPromptBindingFromPromptAction,
  formatExplicitPromptBindingRouteMismatchMessage,
  isStudentsFamiliesFamilyRouteSummaryPrompt,
  resolveConcreteAssistantExecutionPathname,
  type ExplicitPromptBinding,
  validateExplicitPromptBindingForRoute,
} from "./assistantPromptBinding";
import {
  buildAssistantFeedbackContext,
  generateAssistantEpisodeId,
} from "./assistantFeedback";
import { resolveAssistantTaskLaunchOriginSessionIdForStart } from "./assistantTaskLaunchOrigin";
import {
  buildSpreadsheetViewContextFromSession,
  useAssistantWorkspaceTaskLaneController,
} from "./useAssistantWorkspaceTaskLaneController";
import { useAssistantCapabilityPromptController } from "./useAssistantCapabilityPromptController";
import { useAssistantFeedbackController } from "./useAssistantFeedbackController";
import { useAssistantActiveRouteContext } from "./assistantActiveRouteContextRegistry";
import {
  detectAssistantTaskIntent,
  type AssistantTaskProposal,
} from "./assistantTaskIntents";
import { sanitizeAdmissionsAIResponseForPresentation } from "./assistantPresentationSanitization";
import { normalizeUnifiedAssistantResponseEnvelope } from "../../shared/assistantResponseEnvelope";
import {
  mergeAssistantTaskLaneItems,
  type AssistantOptimisticTaskSeed,
  type AssistantRealtimeWorkItemLike,
  type AssistantTaskListItem,
} from "./assistantTaskLaneState";
import {
  buildAssistantArtifactWorkflowProgressMessage,
  projectAssistantArtifactLongRunningWorkflow,
  toAssistantLongRunningWorkflowProgressBucket,
  toAssistantLongRunningWorkflowThreadState,
} from "./assistantArtifactLongRunningWorkflow";
import {
  buildArtifactPreviewFromResponse,
  countArtifactPreviewItems,
  formatArtifactExpiry,
  projectAssistantTaskThreadState,
  projectAssistantTaskWorkflow,
  resolveConversationTaskThreadState,
  shouldDeferCompletedArtifactAnnouncement,
  toArtifactWorkspaceRef,
} from "./assistantArtifactWorkspaceProjector";
import type { AssistantArtifactPanelFamily } from "./assistantArtifactFamiliesProjector";
import {
  clearAssistantConversationTaskLaunches,
  findAssistantConversationTaskLaunch,
  readAssistantConversationTaskLaunches,
  removeAssistantConversationTaskLaunch,
  upsertAssistantConversationTaskLaunch,
  writeAssistantConversationTaskLaunches,
  type AssistantConversationTaskLaunchRecord,
} from "./assistantConversationTaskLaunchState";
import { resolveCompletedTaskAnnouncementDisposition } from "./assistantCompletedTaskAnnouncementPolicy";
import { useProactiveTrayState } from "./useProactiveTrayState";
import {
  buildAssistantSettingsSpecialistGuide,
  resolveAssistantSettingsSpecialistSnapshot,
} from "./assistantSettingsSpecialist";
import { normalizeAssistantConversationHistoryForRequest } from "./assistantConversationHistory";
import { stripCurrentQuestionFromAssistantConversationHistory } from "./assistantConversationHistory";
import {
  clearPendingConversationPersistenceMessageIds,
  markMessagesPendingConversationPersistence,
  selectMessagesPendingConversationPersistence,
} from "./assistantConversationPersistence";
import {
  resolveCognitoProfile,
  resolveCognitoProfileFirstName,
} from "../../utils/cognito-profile";
import { store } from "../../state/store";
import { resolveAssistantRouteFamily } from "../../config/ai-assistant/route-family-registry";
import {
  ReportGenerationWorkspace,
  resolveRunReportsWorkspaceDraftActionForPrompt,
  type RunReportsDraftPayload,
} from "@triedge/ai-assistant-ui/components";
import { useReportGenerationWorkspaceModel } from "../../report-generation/useReportGenerationWorkspaceModel";
import {
  closeAssistantPrototypeThreadTray,
  type AssistantPanelV2ProofState as AssistantPanelV2ParityState,
  parseAssistantPanelV2ProofStateFromSearch,
  parseReportGenerationProofPresetFromSearch,
  REPORT_GENERATION_PROOF_PRESETS,
} from "./assistantProofPresets";
import {
  doesConversationSessionMatchRoute,
  isStudentsFamiliesFamilyDetailRoute,
  selectConversationSessionIdWithFreshSessionGuard,
  shouldHydrateConversationTimelineForFreshSessionGuard,
  type PendingFreshConversationSessionGuard,
} from "./assistantConversationSessionRouting";
import type {
  AdmissionsAIAssistantProps,
  AdmissionsAIContext,
  AdmissionsAIContextualAction,
  AssistantActionExecutionResponse,
  AssistantActionPreviewResponse,
  AssistantCapability,
  AssistantTool,
  AdmissionsAIDebugMetadata,
  AdmissionsAIAssistantModuleKey,
  AdmissionsAIModelKey,
  AdmissionsAIReportGenerationCommand,
  AdmissionsAIMessageFeedback,
  AdmissionsAIResponse,
  AdmissionsAssistantLessonSignal,
  AdmissionsAssistantFeedbackSubmission,
  AdmissionsAssistantArtifactPreview,
  AdmissionsAssistantMessageMetadata,
  AdmissionsAssistantTaskProposalCard,
  AdmissionsAssistantTaskThreadStatus,
  AdmissionsCitation,
  AdmissionsRouteAssistantContext,
  AssistantRuntimePreferences,
} from "./types";
import type { AssistantHistoryMessage } from "@triedge/ai-assistant-ui";

type AssistantPersonaRole = NonNullable<AdmissionsAIContext["personaRole"]>;
type TaskLaunchDraft = {
  id: string;
  createdAt: number;
  question: string;
  proposal: AssistantTaskProposal;
  explicitPromptBinding?: ExplicitPromptBinding | null;
};

type AssistantTranscriptScrollIntent = "append-follow-down" | "explicit-jump";
type AssistantPlatformFallbackStatus = "live" | "cached" | "unavailable";
const toTaskLaunchDraftFromProposalCard = (
  proposal: AdmissionsAssistantTaskProposalCard,
  input?: {
    createdAt?: number | null;
    explicitPromptBinding?: ExplicitPromptBinding | null;
  },
): TaskLaunchDraft | null => {
  if (!proposal.workflowKey || !proposal.taskClass) {
    return null;
  }
  return {
    id: proposal.proposalId,
    createdAt:
      typeof input?.createdAt === "number" ? input.createdAt : Date.now(),
    question: proposal.question,
    proposal: {
      workflowKey: proposal.workflowKey,
      taskClass: proposal.taskClass,
      title: proposal.title,
      summary: proposal.summary,
      recipientEmail: proposal.recipientEmail,
      familyId: proposal.familyId,
      academicYear: proposal.academicYear,
      disabledReason: proposal.disabledReason,
    },
    explicitPromptBinding: input?.explicitPromptBinding ?? null,
  };
};

const buildTaskProposalMessageIds = (proposalId: string) => ({
  userMessageId: `user-task-proposal-${proposalId}`,
  assistantMessageId: `assistant-task-proposal-${proposalId}`,
});

type AssistantConversationTimelineState = {
  session: IntellioAssistantConversationSessionRecord;
  messages: IntellioAssistantConversationMessageRecord[];
};

type AssistantConversationTimelineMeta = {
  status: "idle" | "loading" | "ready" | "error";
  errorMessage?: string | null;
};

type PrototypeTitleView = "home" | "chat" | "files" | "threads" | "preview";

const DEFAULT_ASSISTANT_PANEL_VIEW: AIAssistantWorkspaceTabId = "chat";
const ASSISTANT_NOTIFICATION_STREAM_KEY = "school-persona-feed";

const formatPrototypeTimestamp = (value: number | null | undefined): string => {
  if (!Number.isFinite(value)) {
    return "Just now";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(Number(value) * 1000);
};

const PROTOTYPE_THREAD_ACCENTS = [
  "#2697A2",
  "#08BFAE",
  "#7C9CFF",
  "#EB5106",
] as const;

const renderPrototypeUtilityFileIcon = (title: string): React.ReactElement => {
  const normalizedTitle = title.toLowerCase();
  if (
    /\.(xlsx|xls|csv|tsv)$/.test(normalizedTitle) ||
    normalizedTitle.includes("spreadsheet")
  ) {
    return <FileSpreadsheet size={15} />;
  }
  if (
    /\.(jsx|tsx|js|ts|json|mjs|cjs)$/.test(normalizedTitle) ||
    normalizedTitle.includes("chart")
  ) {
    return <FileCode2 size={15} />;
  }
  return <FileText size={15} />;
};

const SPREADSHEET_TASK_WORKFLOWS = new Set([
  "artifact.spreadsheet.generate",
  "artifact.spreadsheet.modify",
]);

const BACKGROUND_TASK_HISTORY_POLL_INTERVAL_MS = 5_000;
const BACKGROUND_TASK_OPTIMISTIC_RETENTION_MS = 10 * 60_000;
const LIVE_PROMPT_HYDRATION_PROTECTION_MS = 60_000;
const TERMINAL_TASK_THREAD_PROGRESS_SIGNATURE = "__terminal__";

const dedupe = <T,>(values: T[]): T[] => Array.from(new Set(values));

const toRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const resolveAssistantPlatformCompatibleModule = (
  module: AdmissionsAIAssistantModuleKey,
): AdmissionsAIAssistantModuleKey => module;

const resolveAssistantLaunchTargetPathname = (input: {
  targetRoute?: string | null;
  routePrefixes?: string[] | null;
  fallbackPathname: string;
}): string => resolveConcreteAssistantExecutionPathname({
  targetRoute: input.targetRoute,
  routePrefixes: input.routePrefixes,
  currentPathname: input.fallbackPathname,
  fallbackPathname: input.fallbackPathname,
});

const isConcreteFamilyId = (value: unknown): value is string => (
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim())
);

const resolveFamilyIdFromActiveRouteContext = (
  activeRouteContext: AdmissionsAIContext["activeRouteContext"] | null | undefined,
): string | null => {
  if (!activeRouteContext) {
    return null;
  }
  const facets = Array.isArray(activeRouteContext.facets)
    ? activeRouteContext.facets
    : [];
  for (const facet of facets) {
    if (!facet || typeof facet !== "object" || Array.isArray(facet)) {
      continue;
    }
    const record = facet as {
      kind?: unknown;
      state?: {
        entityKind?: unknown;
        entityId?: unknown;
        familyId?: unknown;
      };
    };
    const entityKind = String(record.state?.entityKind ?? "").trim();
    const candidate = String(
      record.state?.familyId ?? record.state?.entityId ?? "",
    ).trim();
    if (record.kind === "selection" && entityKind === "family" && isConcreteFamilyId(candidate)) {
      return candidate;
    }
  }
  return null;
};

type PromptBindableAssistantCapability =
  | AssistantCapability
  | AssistantCapabilityDefinition;

const resolvePromptBindableScenarioKey = (
  capability: PromptBindableAssistantCapability,
): string | undefined => {
  if ("scenarioKey" in capability) {
    return capability.scenarioKey ?? undefined;
  }
  return resolveAssistantCapabilityScenarioKey(capability);
};

const resolvePromptBindableTargetRoute = (
  capability: PromptBindableAssistantCapability,
): string | null => {
  if ("targetRoute" in capability) {
    return capability.targetRoute ?? null;
  }
  return null;
};

const toUnixMillis = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric * 1000 : null;
};

const toUnixSeconds = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildCompletedTaskAnnouncementKey = (input: {
  id: string;
  runId: string | null;
  workflowKey: string | null;
  updatedAt: number | null;
}): string => {
  if (input.runId) {
    return `run:${input.runId}`;
  }
  return [
    "task",
    input.id,
    input.workflowKey ?? "",
    String(input.updatedAt ?? ""),
  ].join("::");
};

const formatRelativeTaskTime = (updatedAtEpochMs: number | null): string => {
  if (!updatedAtEpochMs || Number.isNaN(updatedAtEpochMs)) {
    return "Updated just now";
  }
  const deltaMs = Math.max(0, Date.now() - updatedAtEpochMs);
  const deltaMinutes = Math.round(deltaMs / (60 * 1000));
  if (deltaMinutes < 1) return "Updated just now";
  if (deltaMinutes < 60) return `Updated ${deltaMinutes}m ago`;
  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) return `Updated ${deltaHours}h ago`;
  const deltaDays = Math.round(deltaHours / 24);
  return `Updated ${deltaDays}d ago`;
};

const toConversationMessageRecordFromAssistantMessage = (
  sessionId: string,
  message: AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>,
  sequence: number,
): IntellioAssistantConversationMessageRecord => {
  const messageRecord = toRecord(message);
  const baseMetadataRecord = toRecord(messageRecord?.metadata);
  const metadataRecord =
    message.role === "assistant" && message.presentation
      ? {
          ...baseMetadataRecord,
          presentation: message.presentation,
        }
      : baseMetadataRecord;
  return {
    sessionId,
    messageId: String(message.messageId ?? `${sessionId}:local:${sequence}`),
    sequence,
    role: message.role,
    content: message.content,
    citations: (message.citations ??
      null) as IntellioAssistantConversationMessageRecord["citations"],
    actions: (message.actions ??
      null) as IntellioAssistantConversationMessageRecord["actions"],
    metadata:
      metadataRecord as IntellioAssistantConversationMessageRecord["metadata"],
    source: "client",
    threadId:
      typeof metadataRecord?.taskThread === "object" &&
      metadataRecord.taskThread &&
      typeof (metadataRecord.taskThread as Record<string, unknown>).threadId ===
        "string"
        ? String(
            (metadataRecord.taskThread as Record<string, unknown>).threadId,
          )
        : null,
    runId:
      typeof metadataRecord?.taskThread === "object" &&
      metadataRecord.taskThread &&
      typeof (metadataRecord.taskThread as Record<string, unknown>).runId ===
        "string"
        ? String((metadataRecord.taskThread as Record<string, unknown>).runId)
        : null,
    workItemId:
      typeof metadataRecord?.taskThread === "object" &&
      metadataRecord.taskThread &&
      typeof (metadataRecord.taskThread as Record<string, unknown>)
        .workItemId === "string"
        ? String(
            (metadataRecord.taskThread as Record<string, unknown>).workItemId,
          )
        : null,
    createdAt: (toUnixSeconds(messageRecord?.createdAt) ??
      Math.floor(
        Date.now() / 1000,
      )) as IntellioAssistantConversationMessageRecord["createdAt"],
  };
};

const buildPersistableAssistantMessageMetadata = (
  message: AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>,
): Record<string, unknown> | undefined => {
  const metadataRecord = toRecord(message.metadata);
  const metadataWithoutLiveReveal = metadataRecord
    ? Object.fromEntries(
        Object.entries(metadataRecord).filter(([key]) => key !== "textReveal"),
      )
    : null;
  if (message.role !== "assistant" || !message.presentation) {
    return metadataWithoutLiveReveal &&
      Object.keys(metadataWithoutLiveReveal).length > 0
      ? metadataWithoutLiveReveal
      : undefined;
  }
  return {
    ...metadataWithoutLiveReveal,
    presentation: message.presentation,
  };
};

const isUnsupportedAssistantConversationSessionOperation = (
  error: unknown,
  operationName:
    | "listAssistantConversationSessions"
    | "startAssistantConversationSession",
): boolean => {
  const message = readDomainRouterErrorMessage(
    error,
    error instanceof Error ? error.message : String(error ?? ""),
  );
  return message.includes(`unsupported field ${operationName}`);
};

const toConversationSessionRecord = (
  value: unknown,
): IntellioAssistantConversationSessionRecord | null => {
  const record = toRecord(value);
  if (!record || typeof record.id !== "string") {
    return null;
  }
  return {
    id: record.id,
    actorId: typeof record.actorId === "string" ? record.actorId : "",
    actorRole:
      typeof record.actorRole === "string" ? record.actorRole : "staff",
    tenantId: typeof record.tenantId === "string" ? record.tenantId : "",
    tenantScopeStatus:
      record.tenantScopeStatus === "legacy_placeholder"
        ? "legacy_placeholder"
        : "scoped",
    status: record.status === "closed" ? "closed" : "open",
    module:
      typeof record.module === "string"
        ? (record.module as IntellioAssistantConversationSessionRecord["module"])
        : "general",
    route:
      typeof record.route === "string"
        ? record.route
        : "/school/admissions/dashboard",
    persona: typeof record.persona === "string" ? record.persona : null,
    title: typeof record.title === "string" ? record.title : null,
    routeLabel:
      typeof record.routeLabel === "string" ? record.routeLabel : null,
    firstUserMessageId:
      typeof record.firstUserMessageId === "string"
        ? record.firstUserMessageId
        : null,
    firstUserPreview:
      typeof record.firstUserPreview === "string"
        ? record.firstUserPreview
        : null,
    lastMessageId:
      typeof record.lastMessageId === "string" ? record.lastMessageId : null,
    lastMessagePreview:
      typeof record.lastMessagePreview === "string"
        ? record.lastMessagePreview
        : null,
    lastAssistantMessageId:
      typeof record.lastAssistantMessageId === "string"
        ? record.lastAssistantMessageId
        : null,
    lastAssistantPreview:
      typeof record.lastAssistantPreview === "string"
        ? record.lastAssistantPreview
        : null,
    messageCount:
      typeof record.messageCount === "number" ? record.messageCount : 0,
    sourceAuthority:
      record.sourceAuthority === "legacy-import"
        ? "legacy-import"
        : "persisted",
    startedAt: (toUnixSeconds(record.startedAt) ??
      Math.floor(
        Date.now() / 1000,
      )) as IntellioAssistantConversationSessionRecord["startedAt"],
    updatedAt: (toUnixSeconds(record.updatedAt) ??
      Math.floor(
        Date.now() / 1000,
      )) as IntellioAssistantConversationSessionRecord["updatedAt"],
    closedAt: (toUnixSeconds(record.closedAt) ??
      null) as IntellioAssistantConversationSessionRecord["closedAt"],
  };
};

const toConversationMessageRecord = (
  value: unknown,
): IntellioAssistantConversationMessageRecord | null => {
  const record = toRecord(value);
  if (
    !record ||
    typeof record.sessionId !== "string" ||
    typeof record.messageId !== "string"
  ) {
    return null;
  }
  return {
    sessionId: record.sessionId,
    messageId: record.messageId,
    sequence: typeof record.sequence === "number" ? record.sequence : 0,
    role: typeof record.role === "string" ? record.role : "assistant",
    content: typeof record.content === "string" ? record.content : "",
    citations: (Array.isArray(record.citations)
      ? record.citations
      : null) as IntellioAssistantConversationMessageRecord["citations"],
    actions: (Array.isArray(record.actions)
      ? record.actions
      : null) as IntellioAssistantConversationMessageRecord["actions"],
    metadata: (toRecord(record.metadata) ??
      null) as IntellioAssistantConversationMessageRecord["metadata"],
    source:
      typeof record.source === "string"
        ? (record.source as IntellioAssistantConversationMessageRecord["source"])
        : "client",
    threadId: typeof record.threadId === "string" ? record.threadId : null,
    runId: typeof record.runId === "string" ? record.runId : null,
    workItemId:
      typeof record.workItemId === "string" ? record.workItemId : null,
    createdAt: (toUnixSeconds(record.createdAt) ??
      Math.floor(
        Date.now() / 1000,
      )) as IntellioAssistantConversationMessageRecord["createdAt"],
  };
};

const applyPersistedChartHydrationPolicy = (
  _role: IntellioAssistantConversationMessageRecord["role"],
  metadata: IntellioAssistantConversationMessageRecord["metadata"],
): IntellioAssistantConversationMessageRecord["metadata"] => metadata;

const toAssistantMessageFromConversationRecord = (
  message: IntellioAssistantConversationMessageRecord,
): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction> => ({
  messageId: message.messageId,
  role: message.role,
  content: message.content,
  citations: Array.isArray(message.citations)
    ? (message.citations as unknown as AdmissionsCitation[])
    : undefined,
  actions: Array.isArray(message.actions)
    ? (message.actions as unknown as AdmissionsAIContextualAction[])
    : undefined,
  metadata:
    applyPersistedChartHydrationPolicy(message.role, message.metadata) ??
    undefined,
  presentation:
    normalizeAssistantPresentation(
      toRecord(message.metadata)?.presentation as never,
    ) ?? undefined,
  timestamp:
    (toUnixSeconds(message.createdAt) ?? Math.floor(Date.now() / 1000)) * 1000,
});

const mergeConversationTimelineMessages = (
  existingMessages: IntellioAssistantConversationMessageRecord[],
  incomingMessages: IntellioAssistantConversationMessageRecord[],
): IntellioAssistantConversationMessageRecord[] => {
  const merged = new Map<string, IntellioAssistantConversationMessageRecord>();
  for (const message of [...existingMessages, ...incomingMessages]) {
    const current = merged.get(message.messageId);
    if (!current) {
      merged.set(message.messageId, message);
      continue;
    }
    merged.set(
      message.messageId,
      Number(message.createdAt ?? 0) >= Number(current.createdAt ?? 0)
        ? message
        : current,
    );
  }
  return [...merged.values()].sort((left, right) => {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
    return Number(left.createdAt ?? 0) - Number(right.createdAt ?? 0);
  });
};

const mergeConversationTimelineState = (
  existing: AssistantConversationTimelineState | null | undefined,
  incoming: AssistantConversationTimelineState,
): AssistantConversationTimelineState => {
  const nextMessages = mergeConversationTimelineMessages(
    existing?.messages ?? [],
    incoming.messages,
  );
  const latestMessage = nextMessages[nextMessages.length - 1] ?? null;
  const firstUserMessage =
    nextMessages.find((message) => message.role === "user") ?? null;
  const latestAssistantMessage =
    [...nextMessages]
      .reverse()
      .find((message) => message.role === "assistant") ?? null;
  const existingSession = existing?.session ?? null;
  const incomingSession = incoming.session;
  const preferredSession =
    existingSession &&
    Number(existingSession.updatedAt ?? 0) >
      Number(incomingSession.updatedAt ?? 0)
      ? existingSession
      : incomingSession;

  return {
    session: {
      ...preferredSession,
      firstUserMessageId:
        firstUserMessage?.messageId ?? preferredSession.firstUserMessageId,
      firstUserPreview:
        firstUserMessage?.content ?? preferredSession.firstUserPreview,
      lastMessageId: latestMessage?.messageId ?? preferredSession.lastMessageId,
      lastMessagePreview:
        latestMessage?.content ?? preferredSession.lastMessagePreview,
      lastAssistantMessageId:
        latestAssistantMessage?.messageId ??
        preferredSession.lastAssistantMessageId,
      lastAssistantPreview:
        latestAssistantMessage?.content ??
        preferredSession.lastAssistantPreview,
      messageCount: Math.max(
        preferredSession.messageCount ?? 0,
        nextMessages.length,
      ),
      updatedAt: Math.max(
        Number(preferredSession.updatedAt ?? 0),
        Number(existingSession?.updatedAt ?? 0),
        Number(incomingSession.updatedAt ?? 0),
        Number(latestMessage?.createdAt ?? 0),
      ) as IntellioAssistantConversationSessionRecord["updatedAt"],
    },
    messages: nextMessages,
  };
};

const upsertConversationSessionList = (
  sessions: IntellioAssistantConversationSessionRecord[],
  nextSession: IntellioAssistantConversationSessionRecord,
): IntellioAssistantConversationSessionRecord[] =>
  [
    ...sessions.filter((session) => session.id !== nextSession.id),
    nextSession,
  ].sort(
    (left, right) => Number(right.updatedAt ?? 0) - Number(left.updatedAt ?? 0),
  );

const coerceAssistantTaskListItems = (
  payload: unknown,
): AssistantTaskListItem[] => {
  const root = toRecord(payload);
  const items = Array.isArray(root?.items) ? root.items : [];
  return items
    .map((item) => toAssistantTaskListItemFromRecord(toRecord(item) ?? {}))
    .filter(
      (item): item is AssistantTaskListItem =>
        item !== null && item.id.length > 0,
    );
};

const toAssistantTaskListItemFromRecord = (
  record: Record<string, unknown>,
): AssistantTaskListItem | null => {
  const workflowKey =
    typeof record.workflowKey === "string" ? record.workflowKey : null;
  const runId = typeof record.runId === "string" ? record.runId : null;
  const updatedAt = toUnixMillis(record.updatedAt);
  const tenantScopeStatus =
    record.tenantScopeStatus === "legacy_placeholder"
      ? "legacy_placeholder"
      : "scoped";
  const degradedReason =
    tenantScopeStatus === "legacy_placeholder"
      ? "Historical assistant record used placeholder tenant scope. Read-only only; do not use as deployed-live proof."
      : null;
  if (!workflowKey && !runId) {
    return null;
  }
  const artifactRefs = Array.isArray(record.artifactRefs)
    ? record.artifactRefs
    : [];
  const originRecord = toRecord(record.origin);
  return {
    id: String(record.id ?? ""),
    runId,
    workflowKey,
    taskClass: typeof record.taskClass === "string" ? record.taskClass : null,
    origin: originRecord
      ? {
          sessionId:
            typeof originRecord.sessionId === "string"
              ? originRecord.sessionId
              : null,
          threadId:
            typeof originRecord.threadId === "string"
              ? originRecord.threadId
              : null,
          userMessageId:
            typeof originRecord.userMessageId === "string"
              ? originRecord.userMessageId
              : null,
          assistantMessageId:
            typeof originRecord.assistantMessageId === "string"
              ? originRecord.assistantMessageId
              : null,
        }
      : null,
    tenantScopeStatus,
    degradedReason,
    state: String(record.state ?? "opened"),
    title: String(record.title ?? "Assistant task"),
    phaseLabel:
      typeof record.phaseLabel === "string" ? record.phaseLabel : null,
    statusMessage:
      typeof record.statusMessage === "string" ? record.statusMessage : null,
    progressPercent:
      typeof record.progressPercent === "number"
        ? record.progressPercent
        : null,
    resultSummary:
      typeof record.resultSummary === "string" ? record.resultSummary : null,
    route: typeof record.route === "string" ? record.route : null,
    updatedAt,
    launchedAt: updatedAt,
    canCancel:
      tenantScopeStatus !== "legacy_placeholder" &&
      Boolean(record.cancelable) &&
      !["completed", "failed", "cancelled"].includes(
        String(record.state ?? ""),
      ),
    canConfirm:
      tenantScopeStatus !== "legacy_placeholder" &&
      String(record.phaseKey ?? "") === "waiting_for_confirmation" &&
      workflowKey === "finance.invoice.send",
    isNew: false,
    isOptimistic: false,
    artifacts: artifactRefs
      .map((artifact) => {
        const artifactRecord = toRecord(artifact);
        if (!artifactRecord || typeof artifactRecord.downloadUrl !== "string") {
          return null;
        }
        return {
          id: String(artifactRecord.id ?? ""),
          label: String(
            artifactRecord.label ?? artifactRecord.fileName ?? "Artifact",
          ),
          fileName: String(artifactRecord.fileName ?? "artifact.txt"),
          artifactType: String(artifactRecord.artifactType ?? "report"),
          downloadUrl: artifactRecord.downloadUrl,
          expiresAtLabel: formatArtifactExpiry(artifactRecord.expiresAt),
          mimeType:
            typeof artifactRecord.mimeType === "string"
              ? artifactRecord.mimeType
              : null,
          sourceRef:
            typeof artifactRecord.sourceRef === "string"
              ? artifactRecord.sourceRef
              : null,
        };
      })
      .filter((artifact): artifact is NonNullable<typeof artifact> =>
        Boolean(artifact),
      ),
  };
};

const toAssistantRealtimeWorkItemLike = (
  item: IntellioAssistantWorkItemRecord,
): AssistantRealtimeWorkItemLike => ({
  id: item.id,
  state: item.state,
  route: item.route,
  title: item.title,
  runId: item.runId ?? null,
  workflowKey: item.workflowKey ?? null,
  taskClass: item.taskClass ?? null,
  statusMessage: item.statusMessage ?? null,
  phaseLabel: item.phaseLabel ?? null,
  progressPercent: item.progressPercent ?? null,
  resultSummary: item.resultSummary ?? null,
  cancelable: item.cancelable ?? false,
  origin: item.origin ?? null,
  tenantScopeStatus:
    item.tenantScopeStatus === "legacy_placeholder"
      ? "legacy_placeholder"
      : "scoped",
  artifactRefs: item.artifactRefs ?? [],
  updatedAtEpochMs: toUnixMillis(item.updatedAt) ?? Date.now(),
});

const toIntellioTaskRecord = (
  task: AssistantTaskListItem,
): IntellioTaskRecord => ({
  id: task.id,
  title: task.title,
  workflowKey: task.workflowKey,
  taskClass: task.taskClass,
  state: task.state,
  phaseLabel: task.phaseLabel,
  statusMessage: task.statusMessage,
  progressPercent: task.progressPercent,
  resultSummary: task.resultSummary,
  routeLabel: task.route,
  updatedAtLabel: formatRelativeTaskTime(task.updatedAt),
  canCancel: task.canCancel,
  canConfirm: task.canConfirm,
  isNew: task.isNew,
  isOptimistic: task.isOptimistic,
  launchedAtEpochMs: task.launchedAt,
  artifacts: task.artifacts,
});

const isSchoolFinancialAidPath = (path: string): boolean =>
  path.startsWith("/school/financialaid") ||
  path.startsWith("/school/financial-aid");

const resolveScenarioTabFromPath = (pathname: string): string | null => {
  const path = pathname.toLowerCase();
  if (path.startsWith("/super-administrator/control-plane"))
    return "Control Plane";
  if (path.startsWith("/school/settings/general")) return "General";
  if (path.startsWith("/school/settings/users")) return "Users";
  if (path.startsWith("/school/settings/admissions")) return "Admissions";
  if (
    path.startsWith("/school/settings/financialaid") ||
    path.startsWith("/school/settings/financial-aid")
  ) {
    return "Financial Aid";
  }
  if (
    path === "/school/students" ||
    path.startsWith("/school/students/dashboard")
  )
    return "Dashboard";
  if (path.startsWith("/school/students/family/")) {
    if (path.endsWith("/family")) return "Family";
    if (path.endsWith("/tuition")) return "Tuition";
    if (path.endsWith("/documents")) return "Documents";
    if (path.endsWith("/notes")) return "Notes";
    return "Children";
  }
  if (path === "/school/faculty" || path === "/school/faculty/directory")
    return "Directory";
  if (path.includes("/faculty/") && path.endsWith("/teaching-assignment"))
    return "Teaching";
  if (path.includes("/faculty/profile/") && path.endsWith("/teaching"))
    return "Teaching";
  if (path.endsWith("/faculty/profile/teaching")) return "Teaching";
  if (path.includes("/faculty/") && path.endsWith("/notes")) return "Notes";
  if (path.includes("/faculty/") && path.endsWith("/communications"))
    return "Communications";
  if (path.startsWith("/school/faculty/")) return "Profile";
  if (
    path === "/school/admissions" ||
    path.startsWith("/school/admissions/dashboard")
  )
    return "Dashboard";
  if (
    path.startsWith("/school/admissions/children/") ||
    path.startsWith("/school/admissions/family/") ||
    path.startsWith("/school/admissions/documents/") ||
    path.startsWith("/school/admissions/notes/") ||
    path.startsWith("/school/admissions/tuition/")
  ) {
    return "Family Detail";
  }
  if (
    path === "/school/financialaid" ||
    path === "/school/financialaid/staff" ||
    path.startsWith("/school/financialaid/dashboard") ||
    path === "/school/financial-aid" ||
    path === "/school/financial-aid/staff" ||
    path.startsWith("/school/financial-aid/dashboard")
  ) {
    return "Dashboard";
  }
  if (
    path.startsWith("/school/financialaid/staff/") ||
    path.startsWith("/school/financial-aid/staff/")
  ) {
    return "Application Detail";
  }
  if (path === "/school/academics/summary") return "Summary";
  if (path === "/school/academics/approvals") return "Approvals";
  if (path === "/school/substitutes") return "Dashboard";
  if (path === "/school/substitutes/payroll") return "Payroll";
  if (path === "/school/finance") return "Overview";
  if (path === "/school/finance/reconciliation") return "Reconciliation";
  if (path === "/school/payroll") return "Dashboard";
  if (path.startsWith("/school/payroll/employee/")) return "Employee Detail";
  if (path === "/school/camps") return "Dashboard";
  if (path.startsWith("/school/camps/") && path !== "/school/camps/new")
    return "Program Detail";
  if (path === "/school/donations") return "Dashboard";
  if (path === "/school/donations/ledger") return "Ledger";
  if (path === "/school/tuition") return "Accounts";
  if (path === "/administrator/tuition") return "Operations";
  if (path.startsWith("/parent/portal") || path.startsWith("/portal/family"))
    return "Dashboard";
  return null;
};

const resolveRouteDefaultPersona = (pathname: string): AssistantPersonaRole => {
  const path = pathname.toLowerCase();
  if (path.startsWith("/parent") || path.startsWith("/portal/family")) {
    return "parent";
  }
  return "staff";
};

const PERSONA_TOKEN_MAP: Record<string, AssistantPersonaRole> = {
  staff: "staff",
  nurse: "nurse",
  parent: "parent",
  admin: "admin",
  owner: "owner",
  editor: "editor",
  viewer: "viewer",
  "super-administrator": "super-administrator",
};

const normalizeAssistantPersonaRole = (
  value: unknown,
): AssistantPersonaRole | null => {
  const normalized = normalizeAssistantPersona(
    typeof value === "string" ? value : null,
  );
  return PERSONA_TOKEN_MAP[normalized] ?? null;
};

const resolveEffectivePersonaRole = (
  pathname: string,
  contextPersona?: AdmissionsAIContext["personaRole"] | null,
): AssistantPersonaRole => {
  const normalizedContextPersona =
    normalizeAssistantPersonaRole(contextPersona);
  if (normalizedContextPersona) {
    return normalizedContextPersona;
  }
  if (typeof window !== "undefined") {
    const storedPersonaToken =
      window.localStorage.getItem("ystnj_persona") ??
      window.localStorage.getItem("triedge_persona") ??
      null;
    const normalizedStoredPersona =
      normalizeAssistantPersonaRole(storedPersonaToken);
    if (normalizedStoredPersona) {
      return normalizedStoredPersona;
    }
  }
  return resolveRouteDefaultPersona(pathname);
};

const derivePromptDataStateSignals = (input: {
  pathname: string;
  context: AdmissionsAIContext;
}): AssistantDataStateSignal[] => {
  const path = input.pathname.toLowerCase();
  const signals: AssistantDataStateSignal[] = [];

  if (path.startsWith("/school/settings/users"))
    signals.push("pending_invites");
  if (path.includes("/documents")) signals.push("missing_documents");
  if (path.includes("/notes")) signals.push("overdue_commitments");
  if (isSchoolFinancialAidPath(path)) {
    signals.push("budget_pressure", "committee_readiness");
  }

  const statusTokens = (input.context.status ?? []).map((status) =>
    status.toLowerCase(),
  );
  if (
    statusTokens.some(
      (status) =>
        status.includes("wait") ||
        status.includes("pending") ||
        status.includes("in_progress"),
    )
  ) {
    signals.push("stalled_pipeline");
  }
  if (input.context.search?.trim()) signals.push("stalled_pipeline");
  if (
    (input.context.selectedIds?.length ?? 0) > 0 ||
    Boolean(input.context.activeApplicationId)
  ) {
    signals.push("committee_readiness");
  }

  return dedupe(signals.length > 0 ? signals : ["baseline"]);
};

const sanitizeEntityUiLabel = (
  value: unknown,
  fallback = "Reference",
): string => {
  const normalized = String(value ?? "")
    .replace(/\brecord\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return normalized.length > 0 ? normalized : fallback;
};

const getActionPayload = (
  action: AdmissionsAIContextualAction,
): Record<string, unknown> =>
  action.payload && typeof action.payload === "object" ? action.payload : {};

const RUN_REPORTS_PATHNAME = "/school/run-reports";
const REPORT_GENERATION_SIDE_CAR_SAMPLE_CAP = 5;
type RunReportsRouteLocalPromptDefinition = {
  prompt: AssistantPromptUi;
  actionId: string;
  actionLabel: string;
  payload: RunReportsDraftPayload;
};

const runReportsPromptProvenance = (input: {
  capabilityId: string;
  scenarioKey: string;
  method: string;
  likelySources: string[];
}): AssistantPromptUi["provenance"] => ({
  capabilityId: input.capabilityId,
  sourceOrigin: "run-reports-route-local",
  scenarioKey: input.scenarioKey,
  method: input.method,
  likelySources: input.likelySources,
  routePrefixes: [RUN_REPORTS_PATHNAME],
});

const RUN_REPORTS_ROUTE_LOCAL_PROMPTS: RunReportsRouteLocalPromptDefinition[] = [
  {
    prompt: {
      id: "run-reports-build-from-aging-report",
      label: "Start Aging Report",
      prompt:
        "Open the Aging Report template and apply it to the Run Reports workspace.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-build-from-aging-report",
        scenarioKey: "run-reports.route-local.template",
        method: "runReports.template.apply",
        likelySources: [
          "runReportsWorkspace.templates",
          "previewAssistantReportGeneration",
        ],
      }),
    },
    actionId: "run-reports-apply-template-aging-report",
    actionLabel: "Apply template",
    payload: {
      draftOperation: "apply-template",
      templateId: "aging-report",
      source: "gallery",
    },
  },
  {
    prompt: {
      id: "run-reports-show-best-visual",
      label: "Show recommended charts",
      prompt:
        "Show the recommended Run Reports charts for the current tuition draft.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-show-best-visual",
        scenarioKey: "run-reports.route-local.visual",
        method: "runReports.visual.policyDefault",
        likelySources: [
          "runReportsWorkspace.visualPolicy",
          "runReportsWorkspace.previewRows",
          "previewAssistantReportGeneration",
        ],
      }),
    },
    actionId: "run-reports-show-visual",
    actionLabel: "Show visual",
    payload: {
      draftOperation: "set-visual",
      bodySurface: "visual",
      view: "chart",
      chartType: "bar",
      fieldIds: [
        "due_date",
        "academic_year",
        "payment_status",
        "balance_remaining",
        "amount_due",
        "amount_paid",
        "plan_name",
        "family_name",
      ],
    },
  },
  {
    prompt: {
      id: "run-reports-add-collection-risk-filters",
      label: "Add collection-risk filters",
      prompt:
        "Add a Balance Remaining greater than zero filter to the active Run Reports draft.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-add-collection-risk-filters",
        scenarioKey: "run-reports.route-local.filters",
        method: "runReports.filters.collectionRisk",
        likelySources: [
          "runReportsWorkspace.filters",
          "runReportsWorkspace.availableFields",
          "previewAssistantReportGeneration",
        ],
      }),
    },
    actionId: "run-reports-add-risk-filter",
    actionLabel: "Add filters",
    payload: {
      draftOperation: "add-filter",
      filter: {
        fieldId: "balance_remaining",
        operator: "gt",
        value: "0",
      },
    },
  },
  {
    prompt: {
      id: "run-reports-choose-columns",
      label: "Tune tuition columns",
      prompt:
        "Select the columns that make balances, due dates, student names, payment status, and plans easy to review.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-choose-columns",
        scenarioKey: "run-reports.route-local.columns",
        method: "runReports.columns.recommend",
        likelySources: [
          "runReportsWorkspace.availableFields",
          "runReportsWorkspace.selectedFields",
          "previewAssistantReportGeneration",
        ],
      }),
    },
    actionId: "run-reports-select-columns",
    actionLabel: "Apply columns",
    payload: {
      draftOperation: "select-fields",
      fieldIds: [
        "family_name",
        "student_full_name",
        "due_date",
        "balance_remaining",
        "amount_due",
        "payment_status",
        "plan_name",
        "academic_year",
      ],
    },
  },
  {
    prompt: {
      id: "run-reports-export-current-report",
      label: "Prepare export bundle",
      prompt:
        "Prepare the current Run Reports draft for Excel, CSV, and PDF export.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-export-current-report",
        scenarioKey: "run-reports.route-local.export",
        method: "runReports.export.bundle",
        likelySources: [
          "runReportsWorkspace.definition",
          "runReportsWorkspace.previewRows",
          "previewAssistantReportGeneration",
        ],
      }),
    },
    actionId: "run-reports-export-bundle",
    actionLabel: "Run export",
    payload: {
      draftOperation: "run-export-bundle",
    },
  },
  {
    prompt: {
      id: "run-reports-save-template",
      label: "Save as template",
      prompt: "Save the current Run Reports definition as a private template.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-save-template",
        scenarioKey: "run-reports.route-local.save",
        method: "runReports.template.savePrivate",
        likelySources: [
          "runReportsWorkspace.definition",
          "listAssistantReportTemplates",
        ],
      }),
    },
    actionId: "run-reports-save-private-template",
    actionLabel: "Save template",
    payload: {
      draftOperation: "save-template",
      visibility: "private",
    },
  },
  {
    prompt: {
      id: "run-reports-schedule-report",
      label: "Schedule report",
      prompt:
        "Schedule the current Run Reports definition for daily generation.",
      isDynamic: true,
      provenance: runReportsPromptProvenance({
        capabilityId: "run-reports-schedule-report",
        scenarioKey: "run-reports.route-local.schedule",
        method: "runReports.schedule.daily",
        likelySources: [
          "runReportsWorkspace.definition",
          "runReportsWorkspace.schedules",
          "listAssistantReportSchedules",
        ],
      }),
    },
    actionId: "run-reports-schedule-current",
    actionLabel: "Schedule report",
    payload: {
      draftOperation: "schedule-report",
    },
  },
];

const RUN_REPORTS_ROUTE_LOCAL_PROMPT_BY_ID = new Map(
  RUN_REPORTS_ROUTE_LOCAL_PROMPTS.map((entry) => [
    entry.prompt.id ?? entry.actionId,
    entry,
  ]),
);

const REPORT_GENERATION_SIDECAR_PROMPTS: Record<string, string> = {
  "run-reports-sidecar-explain":
    "Explain the active Run Reports definition, selected entities, fields, filters, date range, and preview evidence.",
  "run-reports-sidecar-overdue-filter":
    "Tighten this report to overdue tuition only by applying the appropriate overdue filter.",
  "run-reports-sidecar-add-fields":
    "Add useful tuition context fields to this report, especially plan name and academic year when available.",
  "run-reports-sidecar-save-shared":
    "Save the current Run Reports definition as a shared template.",
  "run-reports-sidecar-schedule-daily":
    "Schedule the current Run Reports definition to run daily with the standard export bundle.",
  "run-reports-sidecar-export-bundle":
    "Run the current report and generate PDF, XLSX, and CSV exports.",
};

const isRunReportsPath = (path: string): boolean => {
  const normalized = path.toLowerCase().replace(/\/+$/, "");
  return (
    normalized === RUN_REPORTS_PATHNAME ||
    normalized.startsWith(`${RUN_REPORTS_PATHNAME}/`)
  );
};

const REPORT_GENERATION_SELECTION_REQUIRED_COMMANDS = new Set<
  AdmissionsAIReportGenerationCommand["commandId"]
>([
  "save_private_template",
  "save_shared_template",
  "toggle_daily_schedule",
  "run_export",
  "run_export_bundle",
]);

const isReportGenerationCommand = (
  value: unknown,
): value is AdmissionsAIReportGenerationCommand => {
  const record = toRecord(value);
  return (
    record?.type === "execute" &&
    typeof record.commandId === "string" &&
    record.commandId.length > 0
  );
};

const resolveReportGenerationActionCommand = (
  action: AdmissionsAIContextualAction,
): AdmissionsAIReportGenerationCommand | null => {
  const payload = getActionPayload(action);
  const command = payload.reportGenerationCommand;
  return isReportGenerationCommand(command) ? command : null;
};

const toRunReportsDraftPayload = (
  command: AdmissionsAIReportGenerationCommand,
): RunReportsDraftPayload | null => {
  switch (command.commandId) {
    case "set_title":
      return { draftOperation: "set-title", title: command.title };
    case "apply_template":
      return {
        draftOperation: "apply-template",
        templateId: command.templateId,
        source: command.templateSource,
      };
    case "select_field":
      return command.fieldId
        ? { draftOperation: "select-fields", fieldIds: [command.fieldId] }
        : null;
    case "deselect_field":
      return command.fieldId
        ? { draftOperation: "toggle-field", fieldId: command.fieldId }
        : null;
    case "add_filter_condition":
      return {
        draftOperation: "add-filter",
        filter: {
          fieldId: command.fieldId ?? command.condition?.fieldId,
          operator: command.condition?.operator,
          value: command.condition?.value,
        },
      };
    case "update_filter_condition":
      return {
        draftOperation: "update-filter",
        conditionId:
          command.conditionId ??
          (command.fieldId ? `cond-${command.fieldId}` : undefined),
        filter: {
          fieldId: command.fieldId ?? command.conditionUpdates?.fieldId,
          operator: command.conditionUpdates?.operator,
          value: command.conditionUpdates?.value,
        },
      };
    case "remove_filter_condition":
      return {
        draftOperation: "remove-filter",
        conditionId:
          command.conditionId ??
          (command.fieldId ? `cond-${command.fieldId}` : undefined),
      };
    case "update_date_range":
      return {
        draftOperation: "set-date-range",
        dateRange: command.dateRange
          ? {
              preset: command.dateRange.preset,
              from: command.dateRange.from ?? undefined,
              to: command.dateRange.to ?? undefined,
            }
          : undefined,
      };
    case "set_sort":
      return {
        draftOperation: "set-sort",
        sort: {
          fieldId: command.fieldId ?? command.sort?.fieldId,
          direction: command.sort?.direction,
        },
      };
    case "toggle_sort":
      return command.fieldId
        ? {
            draftOperation: "set-sort",
            sort: { fieldId: command.fieldId, direction: "desc" },
          }
        : null;
    case "clear_report":
      return { draftOperation: "clear-selections" };
    case "start_new_report":
      return { draftOperation: "start-new-report" };
    case "save_private_template":
      return { draftOperation: "save-template", visibility: "private" };
    case "save_shared_template":
      return { draftOperation: "save-template", visibility: "shared" };
    case "toggle_daily_schedule":
      return { draftOperation: "schedule-report" };
    case "run_export":
      return {
        draftOperation: "run-export",
        format: command.exportFormat ?? "xlsx",
      };
    case "run_export_bundle":
      return { draftOperation: "run-export-bundle" };
    default:
      return null;
  }
};

const getActionText = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const joinAssistantPromptAttributeList = (
  values: string[] | undefined,
): string | undefined => {
  if (!Array.isArray(values) || values.length === 0) {
    return undefined;
  }
  const filtered = values
    .map((value) => String(value ?? "").trim())
    .filter((value) => value.length > 0);
  return filtered.length > 0 ? filtered.join(",") : undefined;
};

const resolveAssistantActionRoute = (
  action: AdmissionsAIContextualAction,
): string | null => {
  const payload = getActionPayload(action);
  return (
    getActionText(action.targetRoute) ??
    getActionText(payload.targetRoute) ??
    getActionText(payload.href)
  );
};

const resolveAssistantActionPrompt = (
  action: AdmissionsAIContextualAction,
): string | null => {
  const payload = getActionPayload(action);
  return getActionText(payload.prompt);
};

const resolveAssistantActionCapabilityId = (
  action: AdmissionsAIContextualAction,
): string | null => {
  const payload = getActionPayload(action);
  return (
    getActionText(action.capabilityId) ?? getActionText(payload.capabilityId)
  );
};

const resolveAssistantActionRequiresConfirmation = (
  action: AdmissionsAIContextualAction,
): boolean => {
  const payload = getActionPayload(action);
  return Boolean(action.requiresConfirmation ?? payload.requiresConfirmation);
};

const resolveAssistantActionEntityLabel = (
  action: AdmissionsAIContextualAction,
): string | null => {
  const payload = getActionPayload(action);
  return (
    getActionText(action.entityRef?.label) ??
    getActionText(payload.entityLabel) ??
    getActionText(action.label)
  );
};

const MODEL_STORAGE_KEY = "ystnj_ai_model";
const CURATED_ASSISTANT_MODEL_KEYS: AdmissionsAIModelKey[] = [
  "nova-2-lite",
  "haiku-4-5",
  "sonnet-4-6",
  "opus-4-6",
];
const MODEL_OPTIONS: Array<{
  value: AdmissionsAIModelKey;
  label: string;
  badges: string[];
}> = (() => {
  const byKey = new Map(
    ADMISSIONS_BEDROCK_MODEL_SELECTION_OPTIONS.map((option) => [
      option.value,
      {
        value: option.value,
        label: option.label,
        badges: option.badges,
      },
    ]),
  );
  const ordered = CURATED_ASSISTANT_MODEL_KEYS.map((key) =>
    byKey.get(key),
  ).filter(
    (
      option,
    ): option is {
      value: AdmissionsAIModelKey;
      label: string;
      badges: string[];
    } => Boolean(option),
  );
  return ordered.length > 0
    ? ordered
    : ADMISSIONS_BEDROCK_MODEL_SELECTION_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        badges: option.badges,
      }));
})();

const MODEL_VALUES: AdmissionsAIModelKey[] = MODEL_OPTIONS.map(
  (option) => option.value,
);
type ModelOption = (typeof MODEL_OPTIONS)[number];
type PrototypeModelVendor =
  | "amazon"
  | "claude"
  | "chatgpt"
  | "gemini"
  | "generic";
type PrototypeModelMenuMeta = {
  description: string;
  latency: string;
  context: string;
  vendor: PrototypeModelVendor;
  available?: boolean;
};

const resolvePrototypeModelVendor = (label: string): PrototypeModelVendor => {
  const normalizedLabel = label.toLowerCase();
  if (normalizedLabel.includes("amazon") || normalizedLabel.includes("nova")) {
    return "amazon";
  }
  if (normalizedLabel.includes("claude")) {
    return "claude";
  }
  if (normalizedLabel.includes("gpt") || normalizedLabel.includes("openai")) {
    return "chatgpt";
  }
  if (normalizedLabel.includes("gemini")) {
    return "gemini";
  }
  return "generic";
};

const PROTOTYPE_MODEL_MENU_META_BY_KEY: Partial<
  Record<AdmissionsAIModelKey, PrototypeModelMenuMeta>
> = {
  "haiku-4-5": {
    description: "Fast & capable",
    latency: "220ms",
    context: "200k",
    vendor: "claude",
  },
  haiku: {
    description: "Fast & capable",
    latency: "240ms",
    context: "200k",
    vendor: "claude",
  },
  "haiku-3": {
    description: "Quick, efficient",
    latency: "190ms",
    context: "200k",
    vendor: "claude",
  },
  sonnet: {
    description: "Long context, nuanced",
    latency: "420ms",
    context: "200k",
    vendor: "claude",
  },
  "sonnet-4-6": {
    description: "Deep reasoning, balanced",
    latency: "450ms",
    context: "200k",
    vendor: "claude",
  },
  "opus-4-6": {
    description: "Highest reasoning depth",
    latency: "780ms",
    context: "200k",
    vendor: "claude",
  },
  nova: {
    description: "Balanced, lightweight",
    latency: "200ms",
    context: "300k",
    vendor: "amazon",
  },
  "nova-2-lite": {
    description: "Newer lightweight model",
    latency: "180ms",
    context: "300k",
    vendor: "amazon",
  },
  "nova-micro": {
    description: "Fastest for quick tasks",
    latency: "140ms",
    context: "128k",
    vendor: "amazon",
  },
  "phi-3-5-mini": {
    description: "Compact fallback",
    latency: "190ms",
    context: "128k",
    vendor: "generic",
  },
};

const resolvePrototypeModelMenuMeta = (
  option: ModelOption,
): PrototypeModelMenuMeta =>
  PROTOTYPE_MODEL_MENU_META_BY_KEY[option.value] ?? {
    description: option.badges.includes("fastest")
      ? "Fast & capable"
      : "General purpose",
    latency: "240ms",
    context: "128k",
    vendor: resolvePrototypeModelVendor(option.label),
  };

const SCHOOL_SHELL_MODEL_OPTIONS: ModelOption[] = MODEL_OPTIONS;

const normalizeDebugMetadata = (
  value: unknown,
): AdmissionsAIDebugMetadata | null => {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object"
        ? (parsed as AdmissionsAIDebugMetadata)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof value === "object") {
    return value as AdmissionsAIDebugMetadata;
  }
  return null;
};

const extractAssistantRenderHints = (
  debug: AdmissionsAIDebugMetadata | null,
): AdmissionsAssistantMessageMetadata["renderHints"] | undefined => {
  const renderHints = toRecord(debug?.renderHints);
  if (renderHints?.resultCardMode === "exact_targets") {
    return {
      resultCardMode: "exact_targets",
    };
  }
  return undefined;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractDebugTokenUsage = (
  debug: AdmissionsAIDebugMetadata | null,
): {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
} | null => {
  const candidate = (
    debug as {
      usage?: {
        inputTokens?: unknown;
        outputTokens?: unknown;
        totalTokens?: unknown;
      };
    } | null
  )?.usage;
  if (!candidate || typeof candidate !== "object") {
    return null;
  }
  const inputTokens = toFiniteNumber(candidate.inputTokens) ?? 0;
  const outputTokens = toFiniteNumber(candidate.outputTokens) ?? 0;
  const totalTokens =
    toFiniteNumber(candidate.totalTokens) ?? inputTokens + outputTokens;
  return {
    inputTokens,
    outputTokens,
    totalTokens,
  };
};

const truncateRecentText = (value: string, maxChars: number): string =>
  value.length <= maxChars
    ? value
    : `${value.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;

const isSchoolShellPath = (pathname: string): boolean => {
  const path = pathname.toLowerCase();
  return (
    path.startsWith("/school/") ||
    path.startsWith("/admissions") ||
    path.startsWith("/financial-aid") ||
    path.startsWith("/bridged/admissions") ||
    path.startsWith("/bridged/financial-aid") ||
    path.startsWith("/bridged/settings")
  );
};

const CONTEXT_TOKEN_CHAR_RATIO = 4;
const estimateTokensFromChars = (chars: number): number =>
  Math.ceil(chars / CONTEXT_TOKEN_CHAR_RATIO);
const ROUTE_METADATA_TOKEN_PATTERN = /--[a-z0-9-]+/i;
const normalizeAssistantText = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ");
const ASSISTANT_MODULE_LABELS: Record<AssistantCapability["module"], string> = {
  admissions: "Admissions",
  "financial-aid": "Financial Aid",
  faculty: "Faculty",
  settings: "Settings",
  "students-families": "Students & Families",
  tuition: "Tuition",
  attendance: "Attendance",
  "parent-portal": "Parent Portal",
  general: "Operations",
};
const resolveRouteMetadataToken = (pathname: string): "normalized" | "none" =>
  ROUTE_METADATA_TOKEN_PATTERN.test(pathname) ? "normalized" : "none";

const summarizeContextForDebug = (context?: AdmissionsAIContext) => {
  const sanitized = sanitizeAwsJsonObject(context ?? {});
  const serialized = JSON.stringify(sanitized);
  return {
    currentView: context?.currentView ?? null,
    personaRole: context?.personaRole ?? null,
    personaName: context?.personaName ?? null,
    pathname: context?.pathname ?? null,
    routeMetadataToken: context?.routeMetadataToken ?? null,
    capabilityModule: context?.capabilityModule ?? null,
    activeCapabilityId: context?.activeCapabilityId ?? null,
    activeScenarioKey: context?.activeScenarioKey ?? null,
    activeCapabilityMethod: context?.activeCapabilityMethod ?? null,
    selectedIdsCount: context?.selectedIds?.length ?? 0,
    activeApplicationId: context?.activeApplicationId ?? null,
    activeStudentId: context?.activeStudentId ?? null,
    statusCount: context?.status?.length ?? 0,
    gradeCount: context?.grade?.length ?? 0,
    assignedTo: context?.assignedTo ?? null,
    searchChars: context?.search?.length ?? 0,
    faqReferenceIncluded: Boolean(context?.faqReference),
    payloadChars: serialized.length,
    payloadBytes: new TextEncoder().encode(serialized).byteLength,
    payloadTokensEstimate: estimateTokensFromChars(serialized.length),
  };
};

const coerceAdmissionsAIResponse = (payload: unknown): AdmissionsAIResponse => {
  const root =
    (payload as { askSchoolAI?: unknown; askAdmissionsAI?: unknown })
      ?.askSchoolAI ??
    (payload as { askSchoolAI?: unknown; askAdmissionsAI?: unknown })
      ?.askAdmissionsAI ??
    (payload as { response?: unknown })?.response ??
    payload;
  if (!root || typeof root !== "object") {
    throw new Error("askSchoolAI returned an invalid payload shape.");
  }
  const candidate = root as {
    answer?: unknown;
    citations?: unknown;
    debug?: unknown;
    actions?: unknown;
    module?: unknown;
    presentation?: unknown;
  };
  if (typeof candidate.answer !== "string") {
    throw new Error("askSchoolAI returned a payload without a valid answer.");
  }
  return {
    answer: candidate.answer,
    citations: Array.isArray(candidate.citations)
      ? (candidate.citations as AdmissionsCitation[])
      : [],
    debug: candidate.debug as AdmissionsAIDebugMetadata | null | undefined,
    actions: Array.isArray(candidate.actions)
      ? (candidate.actions as AdmissionsAIContextualAction[])
      : [],
    module: typeof candidate.module === "string" ? candidate.module : undefined,
    presentation: normalizeAssistantPresentation(
      candidate.presentation as never,
    ),
  };
};

type AssistantFeedbackAck = {
  accepted: boolean;
  feedbackId: string;
  replayHash: string;
  artifactRefs: string[];
};

type AssistantFeedbackAuthority = {
  feedback: {
    feedbackId: string;
    feedbackArtifactRef: string;
    replayHash: string | null;
    artifactStatus: "present" | "missing" | "mismatch";
    route: string | null;
    persona: string | null;
    projectionPlanHash: string | null;
    outcomeRef: string | null;
    submittedAt: string | null;
  };
  learning: {
    status: "collecting" | "ready";
    artifactBacked: boolean;
    statusSource: "episode" | "route" | "global" | "unavailable";
    updatedAt: string | null;
    artifactUpdatedAt: string | null;
    replayMatchRate: number | null;
  };
  runtimePolicy: {
    policyId: string;
    revision: number;
    executionMode: "shadow" | "live";
    policySource:
      | "s3-object"
      | "artifact-file"
      | "env-json"
      | "embedded-default";
    updatedAt: string | null;
  };
  lessonSignals: AdmissionsAssistantFeedbackSubmission["lessonSignals"];
  sourceRefs: AdmissionsAssistantFeedbackSubmission["authoritySourceRefs"];
};

type AssistantFeedbackAuthorityRequest = {
  feedbackId: string;
  feedbackArtifactRef: string;
  replayHash?: string | null;
};

type AssistantFeedbackAuthorityResult = {
  authority: AssistantFeedbackAuthority | null;
  errorMessage: string | null;
};

const FEEDBACK_EVENT_ARTIFACT_PREFIX = "artifacts/hrm-ace/feedback-events/";

const toLessonFailureClass = (
  value: unknown,
): AdmissionsAssistantLessonSignal["failureClass"] =>
  value === "execution-context" ||
  value === "policy-contract" ||
  value === "domain-readiness" ||
  value === "evidence-coherence" ||
  value === "security" ||
  value === "lineage"
    ? value
    : null;

const toLessonLifecycleStage = (
  value: unknown,
): AdmissionsAssistantLessonSignal["lifecycleStage"] =>
  value === "A" || value === "B" || value === "C" ? value : null;

const toLessonSeverity = (
  value: unknown,
): AdmissionsAssistantLessonSignal["severity"] =>
  value === "low" ||
  value === "medium" ||
  value === "high" ||
  value === "critical"
    ? value
    : null;

const selectFeedbackArtifactRef = (artifactRefs: string[]): string | null =>
  artifactRefs.find((entry) =>
    entry.startsWith(FEEDBACK_EVENT_ARTIFACT_PREFIX),
  ) ??
  artifactRefs[0] ??
  null;

const toAssistantFeedbackAuthority = (
  payload: unknown,
): AssistantFeedbackAuthority | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const candidate = payload as {
    feedback?: unknown;
    learning?: unknown;
    runtimePolicy?: unknown;
    lessonSignals?: unknown;
    sourceRefs?: unknown;
  };
  const feedback = toRecord(candidate.feedback);
  const learning = toRecord(candidate.learning);
  const runtimePolicy = toRecord(candidate.runtimePolicy);
  if (!feedback || !learning || !runtimePolicy) {
    return null;
  }

  const artifactStatus = feedback.artifactStatus;
  const learningStatus = learning.status;
  const learningStatusSource = learning.statusSource;
  const executionMode = runtimePolicy.executionMode;
  const policySource = runtimePolicy.policySource;
  if (
    typeof feedback.feedbackId !== "string" ||
    typeof feedback.feedbackArtifactRef !== "string" ||
    (feedback.replayHash != null && typeof feedback.replayHash !== "string") ||
    (artifactStatus !== "present" &&
      artifactStatus !== "missing" &&
      artifactStatus !== "mismatch") ||
    (feedback.route != null && typeof feedback.route !== "string") ||
    (feedback.persona != null && typeof feedback.persona !== "string") ||
    (feedback.projectionPlanHash != null &&
      typeof feedback.projectionPlanHash !== "string") ||
    (feedback.outcomeRef != null && typeof feedback.outcomeRef !== "string") ||
    (feedback.submittedAt != null &&
      typeof feedback.submittedAt !== "string") ||
    (learningStatus !== "collecting" && learningStatus !== "ready") ||
    typeof learning.artifactBacked !== "boolean" ||
    (learningStatusSource !== "episode" &&
      learningStatusSource !== "route" &&
      learningStatusSource !== "global" &&
      learningStatusSource !== "unavailable") ||
    (learning.updatedAt != null && typeof learning.updatedAt !== "string") ||
    (learning.artifactUpdatedAt != null &&
      typeof learning.artifactUpdatedAt !== "string") ||
    (learning.replayMatchRate != null &&
      typeof learning.replayMatchRate !== "number") ||
    typeof runtimePolicy.policyId !== "string" ||
    typeof runtimePolicy.revision !== "number" ||
    (executionMode !== "shadow" && executionMode !== "live") ||
    (policySource !== "s3-object" &&
      policySource !== "artifact-file" &&
      policySource !== "env-json" &&
      policySource !== "embedded-default") ||
    (runtimePolicy.updatedAt != null &&
      typeof runtimePolicy.updatedAt !== "string")
  ) {
    return null;
  }

  const lessonSignals = Array.isArray(candidate.lessonSignals)
    ? candidate.lessonSignals.flatMap((entry) => {
        const node = toRecord(entry);
        if (
          !node ||
          typeof node.lessonId !== "string" ||
          typeof node.verdict !== "string" ||
          typeof node.gateMode !== "string" ||
          typeof node.sourcePath !== "string"
        ) {
          return [];
        }
        if (
          node.verdict !== "PASS" &&
          node.verdict !== "FAIL" &&
          node.verdict !== "STALE" &&
          node.verdict !== "MISSING" &&
          node.verdict !== "ADVISORY_WARN"
        ) {
          return [];
        }
        if (node.gateMode !== "advisory" && node.gateMode !== "blocking") {
          return [];
        }
        return [
          {
            lessonId: node.lessonId,
            verdict: node.verdict,
            gateMode: node.gateMode,
            failureClass: toLessonFailureClass(node.failureClass),
            lifecycleStage: toLessonLifecycleStage(node.lifecycleStage),
            severity: toLessonSeverity(node.severity),
            sourcePath: node.sourcePath,
          },
        ] satisfies NonNullable<AssistantFeedbackAuthority["lessonSignals"]>;
      })
    : [];

  const sourceRefs = Array.isArray(candidate.sourceRefs)
    ? candidate.sourceRefs.flatMap((entry) => {
        const node = toRecord(entry);
        if (
          !node ||
          typeof node.kind !== "string" ||
          typeof node.ref !== "string" ||
          typeof node.present !== "boolean"
        ) {
          return [];
        }
        return [
          {
            kind: node.kind,
            ref: node.ref,
            present: node.present,
          },
        ] satisfies NonNullable<AssistantFeedbackAuthority["sourceRefs"]>;
      })
    : [];

  return {
    feedback: {
      feedbackId: feedback.feedbackId,
      feedbackArtifactRef: feedback.feedbackArtifactRef,
      replayHash:
        typeof feedback.replayHash === "string" ? feedback.replayHash : null,
      artifactStatus,
      route: typeof feedback.route === "string" ? feedback.route : null,
      persona: typeof feedback.persona === "string" ? feedback.persona : null,
      projectionPlanHash:
        typeof feedback.projectionPlanHash === "string"
          ? feedback.projectionPlanHash
          : null,
      outcomeRef:
        typeof feedback.outcomeRef === "string" ? feedback.outcomeRef : null,
      submittedAt:
        typeof feedback.submittedAt === "string" ? feedback.submittedAt : null,
    },
    learning: {
      status: learningStatus,
      artifactBacked: learning.artifactBacked,
      statusSource: learningStatusSource,
      updatedAt:
        typeof learning.updatedAt === "string" ? learning.updatedAt : null,
      artifactUpdatedAt:
        typeof learning.artifactUpdatedAt === "string"
          ? learning.artifactUpdatedAt
          : null,
      replayMatchRate:
        typeof learning.replayMatchRate === "number"
          ? learning.replayMatchRate
          : null,
    },
    runtimePolicy: {
      policyId: runtimePolicy.policyId,
      revision: runtimePolicy.revision,
      executionMode,
      policySource,
      updatedAt:
        typeof runtimePolicy.updatedAt === "string"
          ? runtimePolicy.updatedAt
          : null,
    },
    lessonSignals,
    sourceRefs,
  };
};

const toFeedbackSubmissionFromAuthority = (input: {
  feedback: AdmissionsAIMessageFeedback;
  ack: AssistantFeedbackAck;
  submittedAt: string;
  authority: AssistantFeedbackAuthority | null;
  authorityErrorMessage?: string | null;
}): AdmissionsAssistantFeedbackSubmission => ({
  feedback: input.feedback,
  submissionStatus: "accepted",
  feedbackId: input.ack.feedbackId,
  replayHash: input.ack.replayHash,
  artifactRefs: input.ack.artifactRefs,
  feedbackArtifactRef:
    input.authority?.feedback.feedbackArtifactRef ??
    selectFeedbackArtifactRef(input.ack.artifactRefs),
  feedbackArtifactStatus: input.authority?.feedback.artifactStatus ?? null,
  learningStatus: input.authority?.learning.status ?? null,
  learningUpdatedAt: input.authority?.learning.updatedAt ?? null,
  learningArtifactBacked: input.authority?.learning.artifactBacked ?? null,
  learningStatusSource: input.authority?.learning.statusSource ?? null,
  replayMatchRate: input.authority?.learning.replayMatchRate ?? null,
  runtimeExecutionMode: input.authority?.runtimePolicy.executionMode ?? null,
  runtimePolicySource: input.authority?.runtimePolicy.policySource ?? null,
  lessonSignals: input.authority?.lessonSignals ?? [],
  authoritySourceRefs: input.authority?.sourceRefs ?? [],
  authorityErrorMessage: input.authorityErrorMessage ?? null,
  authorityResolvedAt: input.authority ? new Date().toISOString() : null,
  errorMessage: null,
  submittedAt: input.authority?.feedback.submittedAt ?? input.submittedAt,
});

const buildFeedbackSuccessMessage = (
  submission: AdmissionsAssistantFeedbackSubmission,
): string => {
  if (submission.feedbackArtifactStatus === "missing") {
    return `Feedback captured (${submission.feedbackId ?? "unknown"}), but the authority artifact is missing.`;
  }
  if (submission.feedbackArtifactStatus === "mismatch") {
    return `Feedback captured (${submission.feedbackId ?? "unknown"}), but authority verification failed.`;
  }
  if (submission.learningArtifactBacked === false) {
    return `Feedback captured (${submission.feedbackId ?? "unknown"}). Learning status unavailable.`;
  }
  if (submission.learningStatus === "ready") {
    return `Feedback captured (${submission.feedbackId ?? "unknown"}). Learning status ready.`;
  }
  if (submission.learningStatus === "collecting") {
    return `Feedback captured (${submission.feedbackId ?? "unknown"}). Learning status collecting.`;
  }
  return `Feedback captured (${submission.feedbackId ?? "unknown"}). Authority details unavailable.`;
};

const toFeedbackMetadata = (
  message: AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>,
): AdmissionsAssistantMessageMetadata | null => {
  const metadata = message.metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }
  return metadata as AdmissionsAssistantMessageMetadata;
};

const toAssistantFeedbackAck = (
  payload: unknown,
): AssistantFeedbackAck | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const candidate = payload as {
    accepted?: unknown;
    feedbackId?: unknown;
    replayHash?: unknown;
    artifactRefs?: unknown;
  };
  if (
    typeof candidate.accepted !== "boolean" ||
    typeof candidate.feedbackId !== "string" ||
    typeof candidate.replayHash !== "string"
  ) {
    return null;
  }
  return {
    accepted: candidate.accepted,
    feedbackId: candidate.feedbackId,
    replayHash: candidate.replayHash,
    artifactRefs: Array.isArray(candidate.artifactRefs)
      ? candidate.artifactRefs.filter(
          (entry): entry is string =>
            typeof entry === "string" && entry.length > 0,
        )
      : [],
  };
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (entry): entry is string =>
          typeof entry === "string" && entry.length > 0,
      )
    : [];

const coerceAssistantModule = (
  value: unknown,
): AssistantCapability["module"] => {
  if (
    value === "admissions" ||
    value === "financial-aid" ||
    value === "faculty" ||
    value === "settings" ||
    value === "students-families" ||
    value === "attendance" ||
    value === "parent-portal" ||
    value === "general"
  ) {
    return value;
  }
  return "general";
};

const coerceAssistantCapabilities = (
  payload: unknown,
): AssistantCapability[] => {
  const root =
    (payload as { listAssistantCapabilities?: unknown })
      ?.listAssistantCapabilities ?? payload;
  if (!root || typeof root !== "object") {
    return [];
  }
  const items = (root as { items?: unknown }).items;
  if (!Array.isArray(items)) {
    return [];
  }
  return items
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      const item = entry as Record<string, unknown>;
      if (typeof item.id !== "string" || item.id.length === 0) {
        return null;
      }
      return {
        id: item.id,
        page: typeof item.page === "string" ? item.page : "",
        tab: typeof item.tab === "string" ? item.tab : "",
        sequence:
          typeof item.sequence === "number" && Number.isFinite(item.sequence)
            ? item.sequence
            : 0,
        title: typeof item.title === "string" ? item.title : item.id,
        type: item.type === "action" ? "action" : "insight",
        module: coerceAssistantModule(item.module),
        prompt: typeof item.prompt === "string" ? item.prompt : "",
        rationale: typeof item.rationale === "string" ? item.rationale : "",
        method: typeof item.method === "string" ? item.method : null,
        requiresConfirmation: item.requiresConfirmation === true,
        actionKey: typeof item.actionKey === "string" ? item.actionKey : null,
        targetRoute:
          typeof item.targetRoute === "string" ? item.targetRoute : null,
        sourceOrigin:
          typeof item.sourceOrigin === "string" ? item.sourceOrigin : null,
        scenarioKey:
          typeof item.scenarioKey === "string" ? item.scenarioKey : null,
        likelySources: toStringArray(item.likelySources),
        routePrefixes: toStringArray(item.routePrefixes),
        dataStateSignals: toStringArray(item.dataStateSignals),
      } satisfies AssistantCapability;
    })
    .filter((entry): entry is AssistantCapability => entry !== null);
};

const resolveAssistantInputActionPrompt = (
  action: AssistantInputAction,
  value?: string,
): string => {
  const trimmedValue = value?.trim() ?? "";
  const optionLabel =
    action.input?.options?.find((option) => option.value === trimmedValue)
      ?.label ?? "";
  const hasSafeHref =
    Boolean(trimmedValue) &&
    (trimmedValue.startsWith("/") || /^https?:\/\//.test(trimmedValue));
  const template =
    action.promptTemplate ??
    (typeof action.payload?.promptTemplate === "string"
      ? action.payload.promptTemplate
      : undefined);
  if (template) {
    return template
      .replace(/\{\{\s*value\s*\}\}/g, trimmedValue)
      .replace(
        /\{\{\s*label\s*\}\}/g,
        optionLabel || trimmedValue || action.label,
      );
  }
  if (action.type === "navigate") {
    if (trimmedValue && hasSafeHref) {
      return `Navigate to [${optionLabel || trimmedValue}](${trimmedValue}).`;
    }
    if (trimmedValue) {
      return `Navigate to ${optionLabel || trimmedValue}.`;
    }
    return `Navigate to ${action.label}.`;
  }
  return trimmedValue || action.label;
};

const coerceAssistantTools = (payload: unknown): AssistantTool[] => {
  const root =
    (payload as { listAssistantTools?: unknown })?.listAssistantTools ??
    payload;
  if (!root || typeof root !== "object") {
    return [];
  }
  const items = (root as { items?: unknown }).items;
  if (!Array.isArray(items)) {
    return [];
  }
  return items
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      const item = entry as Record<string, unknown>;
      if (typeof item.toolId !== "string" || item.toolId.length === 0) {
        return null;
      }
      const toolKind = item.toolKind;
      const executionPlane = item.executionPlane;
      const visibility = item.visibility;
      const promotion = item.promotion;
      const capabilityType = item.capabilityType;
      const taskClass = item.taskClass;
      const groupLabel = item.groupLabel;
      if (
        toolKind !== "provider.native" &&
        toolKind !== "provider.bedrock" &&
        toolKind !== "custom.augmented" &&
        toolKind !== "custom.codified" &&
        toolKind !== "custom.interpreter"
      ) {
        return null;
      }
      if (
        executionPlane !== "supervisor_query" &&
        executionPlane !== "ui_action" &&
        executionPlane !== "async_task"
      ) {
        return null;
      }
      if (visibility !== "visible" && visibility !== "hidden") {
        return null;
      }
      if (
        promotion !== "promoted" &&
        promotion !== "available" &&
        promotion !== "hidden"
      ) {
        return null;
      }
      if (
        capabilityType != null &&
        capabilityType !== "insight" &&
        capabilityType !== "action"
      ) {
        return null;
      }
      if (
        taskClass != null &&
        taskClass !== "artifact_generate" &&
        taskClass !== "compute_report" &&
        taskClass !== "draft_then_approve" &&
        taskClass !== "external_side_effect"
      ) {
        return null;
      }
      if (
        groupLabel !== "Insights" &&
        groupLabel !== "Actions" &&
        groupLabel !== "Custom Tools" &&
        groupLabel !== "Internal"
      ) {
        return null;
      }
      return {
        toolId: item.toolId,
        title: typeof item.title === "string" ? item.title : item.toolId,
        summary: typeof item.summary === "string" ? item.summary : "",
        toolKind,
        executionPlane,
        module: coerceAssistantModule(item.module),
        routePrefixes: toStringArray(item.routePrefixes),
        personas: toStringArray(item.personas),
        visibility,
        promotion,
        prompt: typeof item.prompt === "string" ? item.prompt : null,
        requiresConfirmation: item.requiresConfirmation === true,
        executorRef:
          typeof item.executorRef === "string" ? item.executorRef : null,
        providerToolName:
          typeof item.providerToolName === "string"
            ? item.providerToolName
            : null,
        capabilityId:
          typeof item.capabilityId === "string" ? item.capabilityId : null,
        capabilityType: capabilityType ?? null,
        actionKey: typeof item.actionKey === "string" ? item.actionKey : null,
        workflowKey:
          typeof item.workflowKey === "string" ? item.workflowKey : null,
        taskClass: taskClass ?? null,
        page: typeof item.page === "string" ? item.page : null,
        tab: typeof item.tab === "string" ? item.tab : null,
        sequence:
          typeof item.sequence === "number" && Number.isFinite(item.sequence)
            ? item.sequence
            : null,
        method: typeof item.method === "string" ? item.method : null,
        targetRoute:
          typeof item.targetRoute === "string" ? item.targetRoute : null,
        sourceOrigin:
          typeof item.sourceOrigin === "string" ? item.sourceOrigin : null,
        scenarioKey:
          typeof item.scenarioKey === "string" ? item.scenarioKey : null,
        likelySources: toStringArray(item.likelySources),
        dataStateSignals: toStringArray(item.dataStateSignals),
        groupLabel,
        supportsProactivePromotion: item.supportsProactivePromotion === true,
        userVisible: item.userVisible === true,
      } satisfies AssistantTool;
    })
    .filter((entry): entry is AssistantTool => entry !== null);
};

const readPreferenceFilters = (value: unknown): Record<string, unknown> => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
};

const coerceAssistantActionPreview = (
  payload: unknown,
): AssistantActionPreviewResponse => {
  const root =
    (payload as { previewAssistantAction?: unknown })?.previewAssistantAction ??
    payload;
  if (!root || typeof root !== "object") {
    throw new Error("previewAssistantAction returned an invalid payload.");
  }
  return root as AssistantActionPreviewResponse;
};

const coerceAssistantActionExecution = (
  payload: unknown,
): AssistantActionExecutionResponse => {
  const root =
    (payload as { executeAssistantAction?: unknown })?.executeAssistantAction ??
    payload;
  if (!root || typeof root !== "object") {
    throw new Error("executeAssistantAction returned an invalid payload.");
  }
  return root as AssistantActionExecutionResponse;
};

const coerceAssistantRuntimePolicyHint = (
  payload: unknown,
): AssistantRuntimePolicyHint | null => {
  const root =
    (payload as { getAssistantRuntimePolicy?: unknown })
      ?.getAssistantRuntimePolicy ?? payload;
  if (!root || typeof root !== "object") {
    return null;
  }
  const candidate = root as {
    executionMode?: unknown;
    revision?: unknown;
    policyJson?: unknown;
  };

  let policyJson: Record<string, unknown> | null = null;
  if (
    typeof candidate.policyJson === "string" &&
    candidate.policyJson.trim().length > 0
  ) {
    try {
      const parsed = JSON.parse(candidate.policyJson);
      policyJson =
        parsed && typeof parsed === "object"
          ? (parsed as Record<string, unknown>)
          : null;
    } catch {
      policyJson = null;
    }
  }

  const multimodalGrounding = (() => {
    const node = policyJson?.multimodalGrounding;
    if (!node || typeof node !== "object") {
      return undefined;
    }
    const value = node as Record<string, unknown>;
    return {
      visualExtractorModel:
        typeof value.visualExtractorModel === "string"
          ? value.visualExtractorModel
          : null,
      teacherModel:
        typeof value.teacherModel === "string" ? value.teacherModel : null,
    };
  })();

  const executionMode =
    candidate.executionMode === "shadow" || candidate.executionMode === "live"
      ? candidate.executionMode
      : undefined;
  const revision =
    typeof candidate.revision === "number" &&
    Number.isFinite(candidate.revision)
      ? candidate.revision
      : undefined;
  const lane = typeof policyJson?.lane === "string" ? policyJson.lane : null;

  return {
    executionMode,
    revision,
    lane,
    multimodalGrounding,
  };
};

export function AdmissionsAIAssistant({
  onCitationClick,
  initialContext,
  className,
  layout = "overlay",
  defaultExpanded = false,
  onRequestClose,
  forceSchoolShellDesign,
  fullPageAssistant = false,
  widthMode = "compact",
  onWidthModeChange,
  pathnameOverride,
  searchOverride,
  routeAssistantContext,
}: AdmissionsAIAssistantProps) {
  const pathname =
    pathnameOverride ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  const search =
    searchOverride ??
    (typeof window !== "undefined" ? window.location.search : "");
  const registeredActiveRouteContext = useAssistantActiveRouteContext();
  const moduleConfig = useMemo(
    () => getAIAssistantModuleConfig(pathname),
    [pathname],
  );
  const routeFamily = useMemo(
    () => resolveAssistantRouteFamily(pathname),
    [pathname],
  );
  const runReportsSidecarMode = isRunReportsPath(pathname);
  const routedActiveRouteAssistantContext =
    routeAssistantContext?.pathname === pathname ? routeAssistantContext : null;
  const registeredActiveRouteAssistantContext: AdmissionsRouteAssistantContext | null =
    registeredActiveRouteContext?.pathname === pathname
      ? {
          routeKey: registeredActiveRouteContext.contextRef,
          pathname,
          context: {},
          activeRouteContext: registeredActiveRouteContext,
          onAssistantPolicyEvent: null,
        }
      : null;
  const activeRouteAssistantContext: AdmissionsRouteAssistantContext | null =
    routedActiveRouteAssistantContext ?? registeredActiveRouteAssistantContext;
  const activeRouteAssistantContextRef =
    useRef<AdmissionsRouteAssistantContext | null>(activeRouteAssistantContext);
  activeRouteAssistantContextRef.current = activeRouteAssistantContext;
  const runReportsRouteLocalMode = Boolean(
    runReportsSidecarMode && activeRouteAssistantContext?.activeRouteContext,
  );
  const isAssistantHomeRoute = routeFamily.id === "assistant-home";
  const panelState = useAssistantPanel({
    layout,
    defaultExpanded,
    // App-shell controls panel visibility. Keep sidebar semantics on mobile so
    // we don't render the legacy floating assistant toggle.
    mobileBreakpoint: layout === "sidebar" ? 0 : 767,
  });
  const reportProofPreset = useMemo(
    () => parseReportGenerationProofPresetFromSearch(search),
    [search],
  );
  const assistantReportGenerationProofMode = Boolean(reportProofPreset);
  const reportGenerationWorkspaceModel = useReportGenerationWorkspaceModel({
    defaultTitle: runReportsSidecarMode
      ? "Tuition report"
      : `${routeFamily.label} report builder`,
    enabled:
      assistantReportGenerationProofMode ||
      (runReportsSidecarMode && !runReportsRouteLocalMode),
    pathname,
    module: runReportsSidecarMode ? "tuition" : moduleConfig.id,
  });
  const reportProofPresetRef = useRef<string | null>(null);
  const { value: modelKey, setValue: setModelKey } =
    useModelSelector<AdmissionsAIModelKey>({
      storageKey: MODEL_STORAGE_KEY,
      defaultValue: ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY,
      validValues: MODEL_VALUES,
    });
  const [prototypeParityState, setPrototypeParityState] =
    useState<AssistantPanelV2ParityState | null>(() => {
      return parseAssistantPanelV2ProofStateFromSearch(search);
    });
  const [prototypeUtilityTab, setPrototypeUtilityTab] =
    useState<AIAssistantPrototypeUtilityTab>("files");
  const [prototypeUtilityPaneOpen, setPrototypeUtilityPaneOpen] =
    useState(false);
  const [
    assistantResponseFixtureSelection,
    setAssistantResponseFixtureSelection,
  ] = useState(() => {
    const scenarioId =
      parseAssistantResponseVisualFixtureScenarioFromSearch(search);
    return scenarioId ? { routeFamilyId: routeFamily.id, scenarioId } : null;
  });
  const browserCapabilities = useMemo(() => collectBrowserCapabilities(), []);
  const handleModelSelection = useCallback(
    (nextModelKey: AdmissionsAIModelKey) => {
      const selected = MODEL_OPTIONS.find(
        (option) => option.value === nextModelKey,
      );
      const variant =
        (forceSchoolShellDesign ??
        (layout === "sidebar" && isSchoolShellPath(pathname)))
          ? "school-shell"
          : "default";
      console.info("[AdmissionsAI][Debug] Model selection changed", {
        previousModelKey: modelKey,
        nextModelKey,
        nextModelLabel: selected?.label ?? null,
      });
      trackLogRocketEvent("ai_assistant_model_selected", {
        route: pathname || null,
        module: moduleConfig.id,
        variant,
        previousModelKey: modelKey,
        nextModelKey,
        nextModelLabel: selected?.label ?? null,
        nextModelBadges: selected?.badges ?? [],
      });
      setModelKey(nextModelKey);
    },
    [
      forceSchoolShellDesign,
      layout,
      modelKey,
      moduleConfig.id,
      pathname,
      setModelKey,
    ],
  );
  const preferencesQuery = useDomainRouterQuery("getUserPreferences", {});
  const [triggerListAssistantCapabilities] =
    useLazyDomainRouterQuery<"listAssistantCapabilities">();
  const [triggerListAssistantTools] =
    useLazyDomainRouterQuery<"listAssistantTools">();
  const [triggerGetAssistantFeedbackAuthority] =
    useLazyDomainRouterQuery<"getAssistantFeedbackAuthority">();
  const [triggerGetAssistantSessionState] =
    useLazyDomainRouterQuery<"getAssistantSessionState">();
  const [triggerListAssistantConversationSessions] =
    useLazyDomainRouterQuery<"listAssistantConversationSessions">();
  const [triggerGetAssistantConversationSessionTimeline] =
    useLazyDomainRouterQuery<"getAssistantConversationSessionTimeline">();
  const [triggerGetAssistantRuntimePolicy] =
    useLazyDomainRouterQuery<"getAssistantRuntimePolicy">();
  const [triggerGetAssistantSpreadsheetBridgeStatus] =
    useLazyDomainRouterQuery<"getAssistantSpreadsheetBridgeStatus">();
  const [triggerGetAssistantSpreadsheetWorkbookDraftState] =
    useLazyDomainRouterQuery<"getAssistantSpreadsheetWorkbookDraftState">();
  const [triggerListAssistantWorkItems] =
    useLazyDomainRouterQuery<"listAssistantWorkItems">();
  const [triggerPreviewAssistantAction] =
    useLazyDomainRouterQuery<"previewAssistantAction">();
  const [triggerAskSchoolAI] = useDomainRouterMutation("askSchoolAI");
  const [triggerExecuteAssistantAction] = useDomainRouterMutation(
    "executeAssistantAction",
  );
  const [triggerStartAssistantSpreadsheetBridgeAuth] = useDomainRouterMutation(
    "startAssistantSpreadsheetBridgeAuth",
  );
  const [triggerExportAssistantSpreadsheetToGoogleDrive] =
    useDomainRouterMutation("exportAssistantSpreadsheetToGoogleDrive");
  const [triggerDisconnectAssistantSpreadsheetBridge] = useDomainRouterMutation(
    "disconnectAssistantSpreadsheetBridge",
  );
  const [triggerSaveAssistantSpreadsheetExportPreset] = useDomainRouterMutation(
    "saveAssistantSpreadsheetExportPreset",
  );
  const [triggerDeleteAssistantSpreadsheetExportPreset] =
    useDomainRouterMutation("deleteAssistantSpreadsheetExportPreset");
  const [triggerSubmitAssistantFeedback] = useDomainRouterMutation(
    "submitAssistantFeedback",
  );
  const [triggerMutateAssistantRun] =
    useDomainRouterMutation("mutateAssistantRun");
  const [triggerMutateAssistantWorkItems] = useDomainRouterMutation(
    "mutateAssistantWorkItems",
  );
  const [triggerStartAssistantConversationSession] = useDomainRouterMutation(
    "startAssistantConversationSession",
  );
  const [triggerCloseAssistantConversationSession] = useDomainRouterMutation(
    "closeAssistantConversationSession",
  );
  const [triggerAppendAssistantConversationMessages] = useDomainRouterMutation(
    "appendAssistantConversationMessages",
  );
  const [triggerUpdateUserPreferences] = useDomainRouterMutation(
    "updateUserPreferences",
  );
  const [dismissedSpreadsheetComposerKey, setDismissedSpreadsheetComposerKey] =
    useState<string | null>(null);
  const [lastActionFeedback, setLastActionFeedback] = useState<string | null>(
    null,
  );
  const [pendingTaskLaunch, setPendingTaskLaunch] =
    useState<TaskLaunchDraft | null>(null);
  const [startingTaskProposalId, setStartingTaskProposalId] = useState<
    string | null
  >(null);
  const [completedTasksCollapsed, setCompletedTasksCollapsed] = useState(false);
  const [taskHistoryItems, setTaskHistoryItems] = useState<
    AssistantTaskListItem[]
  >([]);
  const [taskHistoryFallbackStatus, setTaskHistoryFallbackStatus] =
    useState<AssistantPlatformFallbackStatus>("live");
  const [taskHistoryFallbackUpdatedAt, setTaskHistoryFallbackUpdatedAt] =
    useState<number | null>(null);
  const [optimisticTaskSeeds, setOptimisticTaskSeeds] = useState<
    AssistantOptimisticTaskSeed[]
  >([]);
  const [conversationTaskLaunches, setConversationTaskLaunches] = useState<
    AssistantConversationTaskLaunchRecord[]
  >(() => readAssistantConversationTaskLaunches());
  const [assistantEpisodeId, setAssistantEpisodeId] = useState<string>(() =>
    generateAssistantEpisodeId(),
  );
  const [assistantActorId, setAssistantActorId] = useState<string | null>(
    () => resolveCognitoProfile()?.userSub ?? null,
  );
  const [
    assistantConversationSessionMode,
    setAssistantConversationSessionMode,
  ] = useState<"unknown" | "supported" | "unsupported">("unknown");
  const [conversationSessions, setConversationSessions] = useState<
    IntellioAssistantConversationSessionRecord[]
  >([]);
  const [
    conversationSessionsFallbackStatus,
    setConversationSessionsFallbackStatus,
  ] = useState<AssistantPlatformFallbackStatus>("live");
  const [
    conversationSessionsFallbackUpdatedAt,
    setConversationSessionsFallbackUpdatedAt,
  ] = useState<number | null>(null);
  const [currentConversationSessionId, setCurrentConversationSessionId] =
    useState<string | null>(null);
  const [selectedThreadsSessionId, setSelectedThreadsSessionId] = useState<
    string | null
  >(null);
  const [transcriptDisclosureState, setTranscriptDisclosureState] = useState<
    Record<string, boolean>
  >({});
  const [
    conversationTimelinesBySessionId,
    setConversationTimelinesBySessionId,
  ] = useState<Record<string, AssistantConversationTimelineState>>({});
  const [
    conversationTimelineMetaBySessionId,
    setConversationTimelineMetaBySessionId,
  ] = useState<Record<string, AssistantConversationTimelineMeta>>({});
  const [conversationMessagesHydrated, setConversationMessagesHydrated] =
    useState(false);
  const [assistantSurfaceView, setAssistantSurfaceView] =
    useState<AIAssistantWorkspaceTabId>(DEFAULT_ASSISTANT_PANEL_VIEW);
  const workspaceTaskLaneController = useAssistantWorkspaceTaskLaneController({
    pathname,
    search,
    panelState,
    reportWorkspace: {
      isOpen:
        assistantReportGenerationProofMode &&
        reportGenerationWorkspaceModel.workspace.isOpen,
      openWorkspace: assistantReportGenerationProofMode
        ? reportGenerationWorkspaceModel.actions.openWorkspace
        : () => undefined,
      closeWorkspace: reportGenerationWorkspaceModel.actions.closeWorkspace,
    },
    setAssistantSurfaceView,
    triggerGetAssistantSpreadsheetWorkbookDraftState,
    triggerGetAssistantSpreadsheetBridgeStatus,
    triggerStartAssistantSpreadsheetBridgeAuth,
    triggerExportAssistantSpreadsheetToGoogleDrive,
    triggerDisconnectAssistantSpreadsheetBridge,
    triggerSaveAssistantSpreadsheetExportPreset,
    triggerDeleteAssistantSpreadsheetExportPreset,
  });
  const {
	    activeSpreadsheetWorkspace,
	    currentSpreadsheetSession,
	    activeChartWorkspace,
	    activeVisualization3DWorkspace,
    spreadsheetBridgeConnected,
    spreadsheetBridgeStatusLabel,
    spreadsheetBridgeState,
    spreadsheetBridgeSupportedEnvironment,
    spreadsheetBridgePresets,
    spreadsheetExportState,
    spreadsheetExportMessage,
    activeWorkspaceKind,
    storeSpreadsheetSession,
    handleSpreadsheetWorkspaceSessionChange,
    openSpreadsheetWorkspace,
    closeSpreadsheetWorkspace,
	    openReportWorkspace,
	    openChartWorkspace,
	    closeChartWorkspace,
	    openVisualization3DWorkspace,
	    closeVisualization3DWorkspace,
	    closeActiveWorkspace,
	    refreshChartArtifactState,
	    refreshVisualization3DArtifactState,
    refreshSpreadsheetBridgeStatus,
    exportSpreadsheetWorkspace,
    startSpreadsheetBridgeAuth,
    disconnectSpreadsheetBridge,
    saveSpreadsheetExportPreset,
    deleteSpreadsheetExportPreset,
    resetWorkspaceTaskLaneState,
  } = workspaceTaskLaneController;
  const assistantSpreadsheetBridgeStatusPrimedRef = useRef(false);
  useEffect(() => {
    if (assistantSpreadsheetBridgeStatusPrimedRef.current) {
      return;
    }
    if (panelState.isOverlay && !panelState.isExpanded) {
      return;
    }
    assistantSpreadsheetBridgeStatusPrimedRef.current = true;
    void refreshSpreadsheetBridgeStatus();
  }, [
    panelState.isExpanded,
    panelState.isOverlay,
    refreshSpreadsheetBridgeStatus,
  ]);
  const openAssistantArtifactInGoogleSheets = useCallback(
    async (item: AssistantPresentationArtifactItem): Promise<string | null> => {
      const googleWorkspace = item.googleWorkspace;
      if (googleWorkspace?.kind !== "google_sheets") {
        return null;
      }
      const existingFileUrl = String(googleWorkspace.fileUrl ?? "").trim();
      if (existingFileUrl) {
        if (isBlankGoogleSheetsUrl(existingFileUrl)) {
          throw new Error(
            "Google Sheets export returned a blank-sheet URL instead of an artifact-backed file URL.",
          );
        }
        return existingFileUrl;
      }

      const input = buildAssistantGoogleSheetsExportInput(item);
      const result = await triggerExportAssistantSpreadsheetToGoogleDrive({
        input: {
          fileName: input.fileName,
          workbookArtifactPath: input.workbookArtifactPath ?? undefined,
          artifactDownloadUrl: input.artifactDownloadUrl ?? undefined,
          mimeType: input.mimeType ?? undefined,
        },
      });
      const record = toRecord(result);
      let parsedPayload: Record<string, unknown> | null = toRecord(record?.payload);
      if (!parsedPayload && typeof record?.payload === "string") {
        try {
          parsedPayload = toRecord(JSON.parse(record.payload));
        } catch {
          parsedPayload = null;
        }
      }
      const fileUrl = String(
        record?.fileUrl ?? parsedPayload?.fileUrl ?? "",
      ).trim();
      if (!fileUrl) {
        throw new Error("Google Sheets export completed without a file URL.");
      }
      if (isBlankGoogleSheetsUrl(fileUrl)) {
        throw new Error(
          "Google Sheets export returned a blank-sheet URL instead of an artifact-backed file URL.",
        );
      }
      return fileUrl;
    },
    [triggerExportAssistantSpreadsheetToGoogleDrive],
  );
  const announcedCompletedTaskIdsRef = useRef<Set<string>>(new Set());
  const processingCompletedTaskIdsRef = useRef<Set<string>>(new Set());
  const announcedTaskProgressSignaturesRef = useRef<Record<string, string>>({});
  const launchedConversationTaskRunIdsRef = useRef<Set<string>>(new Set());
  const launchedConversationTaskIdsRef = useRef<Set<string>>(new Set());
  const processedReportGenerationActionIdsRef = useRef<Set<string>>(
    new Set(),
  );
  const conversationTaskLaunchesRef = useRef<
    AssistantConversationTaskLaunchRecord[]
  >(conversationTaskLaunches);
  const conversationTimelinesBySessionIdRef = useRef<
    Record<string, AssistantConversationTimelineState>
  >({});
  const persistedConversationMessageIdsRef = useRef<
    Record<string, Set<string>>
  >({});
  const pendingConversationMessagePersistenceIdsRef = useRef<
    Record<string, Set<string>>
  >({});
  const sessionConversationMutationEpochRef = useRef<Record<string, number>>(
    {},
  );
  const activeConversationSessionIdRef = useRef<string | null>(null);
  const conversationTranscriptSessionIdRef = useRef<string | null>(null);
  const conversationResetEpochRef = useRef(0);
  const pendingFreshConversationSessionGuardRef =
    useRef<PendingFreshConversationSessionGuard | null>(null);
  const startFreshConversationSessionPromiseRef =
    useRef<Promise<IntellioAssistantConversationSessionRecord | null> | null>(
      null,
    );
  const startFreshConversationSessionCloseCurrentRef = useRef(false);
  const conversationTimelineLoadPromisesRef = useRef<
    Record<string, Promise<AssistantConversationTimelineState | null>>
  >({});
  const pendingAnchorRef = useRef<{
    sessionId: string;
    messageId?: string | null;
    intent: AssistantTranscriptScrollIntent;
  } | null>(null);
  const pendingTaskLaunchRef = useRef<TaskLaunchDraft | null>(
    pendingTaskLaunch,
  );
  const completedTaskHydrationRef = useRef(false);
  const dispatch = useAppDispatch();
  const queuedAssistantWorkItems = useAppSelector(selectAssistantRealtimeQueue);
  const latestRealtimeWorkItems = useAppSelector(
    selectAssistantRealtimeLatestWorkItems,
  );
  const assistantRealtimeState = useAppSelector(selectAssistantRealtimeState);
  const proactiveTray = useProactiveTrayState({
    items: queuedAssistantWorkItems,
    pathname,
  });
  const activeProactivePromptEntry = proactiveTray.activeEntry;
  const activeProactivePrompt =
    activeProactivePromptEntry?.assistantPrompt ?? null;
  const {
    feedbackSubmissionByMessageId,
    feedbackToastMessage,
    handleAssistantFeedbackRequest,
    setFeedbackMessages,
    resetFeedbackControllerState,
  } = useAssistantFeedbackController({
    triggerSubmitAssistantFeedback,
    triggerGetAssistantFeedbackAuthority,
    onSetLastActionFeedback: setLastActionFeedback,
  });

  const {
    context,
    setContext,
    summary,
    resultCount,
    isLoading: isContextLoading,
    error: contextError,
    refresh: refreshContext,
    readStoredContext,
  } = useAdmissionsAssistantContext({
    initialContext,
    isActive: !panelState.isOverlay || panelState.isExpanded,
    moduleConfig,
    pathname,
  });
  const capabilityModule = moduleConfig.id;
  const effectivePersonaRole = useMemo(
    () => resolveEffectivePersonaRole(pathname, context.personaRole),
    [context.personaRole, pathname],
  );
  const assistantCatalogScope: AssistantCapabilityCatalogScope =
    effectivePersonaRole === "parent" || routeFamily.id === "parent-portal"
      ? "route"
      : "staff-global";
  const localAssistantCapabilityFixture = useMemo<AssistantCapability[]>(
    () =>
      listAssistantCapabilitiesForContext({
        pathname,
        module: capabilityModule,
        persona: effectivePersonaRole,
        catalogScope: assistantCatalogScope,
        strictRouteMatch: assistantCatalogScope === "route",
      }).map((capability) => ({
        id: capability.id,
        page: capability.page,
        tab: capability.tab,
        sequence: capability.sequence,
        title: capability.title,
        type: capability.type,
        module: capability.module,
        prompt: capability.prompt,
        rationale: capability.rationale,
        method: capability.method ?? null,
        requiresConfirmation: capability.requiresConfirmation === true,
        actionKey: capability.actionKey ?? null,
        routePrefixes: [...capability.routePrefixes],
        targetRoute: resolveAssistantLaunchTargetPathname({
          routePrefixes: capability.routePrefixes,
          fallbackPathname: pathname,
        }),
        sourceOrigin: null,
        scenarioKey: null,
        likelySources: [],
        dataStateSignals: [],
      })),
    [assistantCatalogScope, capabilityModule, effectivePersonaRole, pathname],
  );
  const localAssistantToolFixture = useMemo<AssistantTool[]>(
    () =>
      listAssistantToolsForContext({
        pathname,
        module: capabilityModule,
        persona: effectivePersonaRole,
        limit: assistantCatalogScope === "staff-global" ? undefined : 24,
        catalogScope: assistantCatalogScope,
      }),
    [assistantCatalogScope, capabilityModule, effectivePersonaRole, pathname],
  );
  const settingsSpecialistSnapshot = useMemo(
    () => resolveAssistantSettingsSpecialistSnapshot(pathname),
    [pathname],
  );
  const showAmbientSettingsSpecialist = useMemo(
    () => isSettingsRouteFamilyPath(pathname),
    [pathname],
  );
  const {
    actionContextPayload,
    assistantCatalogLoadErrors,
    assistantCapabilitiesLoading,
    assistantToolsLoading,
    capabilityBrowserPreviewById,
    capabilityById,
    capabilityByPromptId,
    capabilityCatalogQuery,
    capabilityCatalogSections,
    capabilityCatalogTotalCount,
    capabilityPrompts,
    filteredSettingsSpecialistCases,
    hideAllToolsFromToolbar,
    hideToolFromToolbar,
    loadCapabilityBrowserPreview,
    orderedCapabilities,
    rankedSnapshotById,
    resolvedAssistantCapabilities,
    resolvedAssistantTools,
    runtimePolicyHint,
    setCapabilityCatalogQuery,
    setShowCapabilityCatalog,
    settingsSpecialistPromptSections,
    showAllToolsInToolbar,
    showCapabilityCatalog,
    toggleToolToolbarCollapsed,
    toolbarTools,
    toolCatalogSections,
    toolToolbarPreferences,
  } = useAssistantCapabilityPromptController({
    pathname,
    capabilityModule,
    effectivePersonaRole,
    catalogScope: assistantCatalogScope,
    context,
    preferencesData: (
      preferencesQuery.data as { filters?: unknown } | undefined
    )?.filters,
    localAssistantCapabilityFixture,
    localAssistantToolFixture,
    settingsSpecialistSnapshot,
    showAmbientSettingsSpecialist,
    triggerListAssistantCapabilities,
    triggerListAssistantTools,
    triggerGetAssistantRuntimePolicy,
    triggerUpdateUserPreferences,
    triggerPreviewAssistantAction,
    onSetLastActionFeedback: setLastActionFeedback,
  });
  const reportGenerationSidecarContext = useMemo<
    AdmissionsAIContext["reportGenerationSidecar"] | undefined
  >(() => {
    if (!runReportsSidecarMode) {
      return undefined;
    }
    const routeLocalSidecar = toRecord(
      activeRouteAssistantContext?.context.reportGenerationSidecar,
    );
    if (runReportsRouteLocalMode) {
      return routeLocalSidecar
        ? (routeLocalSidecar as AdmissionsAIContext["reportGenerationSidecar"])
        : undefined;
    }
    const definition = reportGenerationWorkspaceModel.definition;
    const selectedEntityIds = new Set(definition.selectedEntities);
    const selectedFieldIds = new Set(definition.selectedFields);
    const visibleColumnIds = reportGenerationWorkspaceModel.previewColumns
      .map((column) => column.fieldId)
      .filter(
        (fieldId) =>
          !reportGenerationWorkspaceModel.workspace.hiddenColumns.includes(
            fieldId,
          ),
      );
    const visibleColumnIdSet = new Set(visibleColumnIds);
    const columns = reportGenerationWorkspaceModel.previewColumns
      .filter((column) => visibleColumnIdSet.has(column.fieldId))
      .slice(0, Math.max(1, REPORT_GENERATION_SIDE_CAR_SAMPLE_CAP))
      .map((column) => ({
        fieldId: column.fieldId,
        label: column.label,
        moduleLabel: column.moduleLabel,
        entityId: column.entityId ?? null,
      }));
    const selectedEntities = reportGenerationWorkspaceModel.modules
      .flatMap((module) => module.entities)
      .filter((entity) => selectedEntityIds.has(entity.id))
      .map((entity) => ({
        id: entity.id,
        label: entity.label,
        moduleLabel: entity.moduleLabel,
      }));
    const selectedFields = reportGenerationWorkspaceModel.availableFields
      .filter((field) => selectedFieldIds.has(field.fieldId))
      .map((field) => ({
        id: field.fieldId,
        label: field.fieldLabel,
        entityId: field.entityId ?? null,
        moduleLabel: field.moduleLabel,
      }));
    return {
      routeContext: {
        pathname: RUN_REPORTS_PATHNAME,
        module: "tuition",
        routeFamily: "tuition",
        routeLabel: "Tuition",
      },
      currentDefinition: {
        ...definition,
        pathname: RUN_REPORTS_PATHNAME,
        module: "tuition",
        routeFamily: "tuition",
      },
      selectedEntities,
      selectedFields,
      filters: definition.conditions,
      dateRange: definition.dateRange,
      sorts: definition.sorts,
      templates: {
        currentTemplateId:
          reportGenerationWorkspaceModel.filterState.selectedTemplateId,
        currentTemplateSource:
          reportGenerationWorkspaceModel.filterState.selectedTemplateSource,
        gallery: reportGenerationWorkspaceModel.galleryTemplates.map(
          (template) => ({
            id: template.id,
            name: template.name,
            description: template.description,
            fields: [...template.fields],
            entities: [...template.entities],
          }),
        ),
        saved: reportGenerationWorkspaceModel.myTemplates.map((template) => ({
          id: template.id,
          name: template.name,
          visibility: template.visibility ?? null,
          isScheduled: template.isScheduled ?? null,
          fields: [...template.fields],
          entities: [...template.entities],
        })),
      },
      schedule: {
        hasActiveSchedule: reportGenerationWorkspaceModel.hasActiveSchedule,
      },
      history: {
        recent: reportGenerationWorkspaceModel.historyEntries
          .slice(0, 5)
          .map((entry) => ({
            id: entry.id,
            templateName: entry.templateName,
            status: entry.status,
            runDate: entry.runDate,
          })),
      },
      preview: {
        sampleCap: REPORT_GENERATION_SIDE_CAR_SAMPLE_CAP,
        visibleColumnIds,
        columns,
        rows: reportGenerationWorkspaceModel.previewRows
          .slice(0, REPORT_GENERATION_SIDE_CAR_SAMPLE_CAP)
          .map((row) => ({
            id: row.id,
            cells: Object.fromEntries(
              columns.map((column) => [
                column.fieldId,
                row.cells[column.fieldId] ?? null,
              ]),
            ),
          })),
        totalRowCount: reportGenerationWorkspaceModel.totalRowCount,
        warnings: [...reportGenerationWorkspaceModel.previewWarnings],
        blockedEntityCount:
          reportGenerationWorkspaceModel.inaccessibleEntities.length,
        canRun: reportGenerationWorkspaceModel.canPreview,
        loading: reportGenerationWorkspaceModel.previewLoading,
        error: reportGenerationWorkspaceModel.previewError,
      },
    };
  }, [
    activeRouteAssistantContext?.context,
    reportGenerationWorkspaceModel.availableFields,
    reportGenerationWorkspaceModel.canPreview,
    reportGenerationWorkspaceModel.definition,
    reportGenerationWorkspaceModel.filterState.selectedTemplateId,
    reportGenerationWorkspaceModel.filterState.selectedTemplateSource,
    reportGenerationWorkspaceModel.galleryTemplates,
    reportGenerationWorkspaceModel.hasActiveSchedule,
    reportGenerationWorkspaceModel.historyEntries,
    reportGenerationWorkspaceModel.inaccessibleEntities.length,
    reportGenerationWorkspaceModel.modules,
    reportGenerationWorkspaceModel.myTemplates,
    reportGenerationWorkspaceModel.previewColumns,
    reportGenerationWorkspaceModel.previewError,
    reportGenerationWorkspaceModel.previewLoading,
    reportGenerationWorkspaceModel.previewRows,
    reportGenerationWorkspaceModel.previewWarnings,
    reportGenerationWorkspaceModel.totalRowCount,
    reportGenerationWorkspaceModel.workspace.hiddenColumns,
    runReportsRouteLocalMode,
    runReportsSidecarMode,
  ]);
  const reportGenerationSidecarPrompts = useMemo<AssistantPromptUi[]>(() => {
    if (!runReportsSidecarMode) {
      return [];
    }
    return [
      ...RUN_REPORTS_ROUTE_LOCAL_PROMPTS.map((entry) => entry.prompt),
      {
        id: "run-reports-sidecar-explain",
        label: "Explain this report",
        isDynamic: true,
        provenance: {
          capabilityId: "run-reports-sidecar-explain",
          sourceOrigin: "run-reports-sidecar",
          scenarioKey: "run-reports.sidecar.explain",
          method:
            "Summarize the current report definition and backend-preview evidence.",
          likelySources: ["previewAssistantReportGeneration"],
          routePrefixes: [RUN_REPORTS_PATHNAME],
        },
      },
      {
        id: "run-reports-sidecar-overdue-filter",
        label: "Overdue only",
        isDynamic: true,
        provenance: {
          capabilityId: "run-reports-sidecar-overdue-filter",
          sourceOrigin: "run-reports-sidecar",
          scenarioKey: "run-reports.sidecar.filter-overdue",
          method: "Apply the tuition overdue boolean filter.",
          likelySources: ["previewAssistantReportGeneration"],
          routePrefixes: [RUN_REPORTS_PATHNAME],
        },
      },
    ];
  }, [runReportsSidecarMode]);
  useEffect(() => {
    conversationTaskLaunchesRef.current = conversationTaskLaunches;
  }, [conversationTaskLaunches]);

  useEffect(() => {
    pendingTaskLaunchRef.current = pendingTaskLaunch;
  }, [pendingTaskLaunch]);

  useEffect(() => {
    conversationTimelinesBySessionIdRef.current =
      conversationTimelinesBySessionId;
  }, [conversationTimelinesBySessionId]);

  const persistConversationTaskLaunches = useCallback(
    (nextLaunches: AssistantConversationTaskLaunchRecord[]) => {
      conversationTaskLaunchesRef.current = nextLaunches;
      setConversationTaskLaunches(nextLaunches);
      writeAssistantConversationTaskLaunches(nextLaunches);
    },
    [],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => {};
    }
    let attempts = 0;
    const refreshActorId = () => {
      attempts += 1;
      const nextActorId = resolveCognitoProfile()?.userSub ?? null;
      setAssistantActorId((current) =>
        current === nextActorId ? current : nextActorId,
      );
      if (nextActorId || attempts >= 8) {
        window.clearInterval(intervalId);
      }
    };
    const intervalId = window.setInterval(refreshActorId, 1500);
    refreshActorId();

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) {
        return;
      }
      if (
        event.key.endsWith(".idToken") ||
        event.key.includes("triedge_persona")
      ) {
        refreshActorId();
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    if (!assistantActorId) {
      return;
    }
    const subscription = retainYstnjAssistantRealtimeBridge({
      dispatch,
      getState: store.getState as () => RootState,
      actorId: assistantActorId,
      familyId: ASSISTANT_NOTIFICATION_STREAM_KEY,
      pathname,
      persona: effectivePersonaRole,
      listQueryArgs: {
        actorId: assistantActorId,
        familyId: ASSISTANT_NOTIFICATION_STREAM_KEY,
        includeRead: true,
        limit: 50,
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [assistantActorId, dispatch, effectivePersonaRole, pathname]);

  useEffect(() => {
    let cancelled = false;
    triggerGetAssistantSessionState(
      "getAssistantSessionState",
      {
        pathname,
        module: capabilityModule,
      },
      false,
    )
      .then((payload) => {
        if (cancelled) {
          return;
        }
        hydrateAssistantSessionState({
          dispatch,
          payload,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn(
            "[AdmissionsAI] Failed to hydrate assistant session state",
            error,
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [capabilityModule, dispatch, pathname, triggerGetAssistantSessionState]);

  useEffect(() => {
    let cancelled = false;
    const loadTaskHistory = () =>
      triggerListAssistantWorkItems(
        "listAssistantWorkItems",
        {
          input: {
            includeClosed: true,
            pathname,
            limit: 30,
          },
        },
        false,
      )
        .then((payload) => {
          if (cancelled) {
            return;
          }
          const nextTaskHistoryItems = coerceAssistantTaskListItems(payload);
          setTaskHistoryItems(nextTaskHistoryItems);
          setTaskHistoryFallbackStatus("live");
          setTaskHistoryFallbackUpdatedAt(null);
        })
        .catch((error) => {
          if (!cancelled) {
            const message = readDomainRouterErrorMessage(
              error,
              "Failed to load assistant task history.",
            );
            setTaskHistoryFallbackStatus("unavailable");
            setTaskHistoryFallbackUpdatedAt(null);
            console.warn(
              "[AdmissionsAI] Failed to load assistant task history",
              {
                message,
                error,
              },
            );
          }
        });

    void loadTaskHistory();
    return () => {
      cancelled = true;
    };
  }, [
    assistantRealtimeState.lastEventAtEpochMs,
    pathname,
    triggerListAssistantWorkItems,
  ]);

  const refreshLaunchedRunSessionState = useCallback(
    async (launches: readonly AssistantConversationTaskLaunchRecord[]) => {
      const runIds = [
        ...new Set(
          launches
            .map((launch) =>
              typeof launch.runId === "string" ? launch.runId.trim() : "",
            )
            .filter((runId) => runId.length > 0),
        ),
      ].slice(0, 4);

      for (const runId of runIds) {
        const payload = await triggerGetAssistantSessionState(
          "getAssistantSessionState",
          {
            pathname,
            module: capabilityModule,
            runId,
          },
          false,
        );
        hydrateAssistantSessionState({
          dispatch,
          payload,
        });
      }
    },
    [capabilityModule, dispatch, pathname, triggerGetAssistantSessionState],
  );

  useEffect(() => {
    if (conversationTaskLaunches.length === 0) {
      return;
    }
    let cancelled = false;
    const syncTaskHistory = async () => {
      try {
        const payload = await triggerListAssistantWorkItems(
          "listAssistantWorkItems",
          {
            input: {
              includeClosed: true,
              pathname,
              limit: 30,
            },
          },
          false,
        );
        if (!cancelled) {
          const nextTaskHistoryItems = coerceAssistantTaskListItems(payload);
          setTaskHistoryItems(nextTaskHistoryItems);
          setTaskHistoryFallbackStatus("live");
          setTaskHistoryFallbackUpdatedAt(null);
        }
      } catch (error) {
        if (!cancelled) {
          const message = readDomainRouterErrorMessage(
            error,
            "Failed to refresh assistant task history.",
          );
          setTaskHistoryFallbackStatus("unavailable");
          setTaskHistoryFallbackUpdatedAt(null);
          console.warn(
            "[AdmissionsAI] Failed to refresh assistant task history while awaiting background task progress",
            {
              message,
              error,
            },
          );
        }
      }

      try {
        await refreshLaunchedRunSessionState(
          conversationTaskLaunchesRef.current,
        );
      } catch (error) {
        if (!cancelled) {
          console.warn(
            "[AdmissionsAI] Failed to refresh launched assistant run session state",
            error,
          );
        }
      }
    };

    void syncTaskHistory();
    const intervalId = window.setInterval(() => {
      void syncTaskHistory();
    }, BACKGROUND_TASK_HISTORY_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    conversationTaskLaunches,
    refreshLaunchedRunSessionState,
    pathname,
    triggerListAssistantWorkItems,
  ]);

  useEffect(() => {
    setLastActionFeedback(null);
  }, [pathname]);

  const resetSessionScopedUiState = useCallback(
    (input?: {
      preservePendingTaskLaunch?: boolean;
      clearWorkspaceTaskLane?: boolean;
    }) => {
      const preservedPendingTaskLaunch = input?.preservePendingTaskLaunch
        ? pendingTaskLaunchRef.current
        : null;
      resetFeedbackControllerState();
      setAssistantEpisodeId(generateAssistantEpisodeId());
      announcedCompletedTaskIdsRef.current.clear();
      processingCompletedTaskIdsRef.current.clear();
      announcedTaskProgressSignaturesRef.current = {};
      launchedConversationTaskRunIdsRef.current.clear();
      launchedConversationTaskIdsRef.current.clear();
      conversationTaskLaunchesRef.current = [];
      setConversationTaskLaunches([]);
      clearAssistantConversationTaskLaunches();
      completedTaskHydrationRef.current = false;
      setOptimisticTaskSeeds([]);
      if (input?.clearWorkspaceTaskLane) {
        resetWorkspaceTaskLaneState();
        setDismissedSpreadsheetComposerKey(null);
      }
      setPendingTaskLaunch(preservedPendingTaskLaunch);
      setStartingTaskProposalId(null);
      setTranscriptDisclosureState({});
      setConversationMessagesHydrated(false);
    },
    [resetFeedbackControllerState, resetWorkspaceTaskLaneState],
  );

  const validateRouteBoundPromptBinding = useCallback(
    (binding: ExplicitPromptBinding | null | undefined) =>
      validateExplicitPromptBindingForRoute({
        binding,
        pathname,
        persona: effectivePersonaRole,
      }),
    [effectivePersonaRole, pathname],
  );

  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return;
    }
    previousPathnameRef.current = pathname;
    resetWorkspaceTaskLaneState();
    setDismissedSpreadsheetComposerKey(null);
    if (
      !validateRouteBoundPromptBinding(pendingExplicitPromptBindingRef.current)
        .isValid
    ) {
      pendingExplicitPromptBindingRef.current = null;
    }
    setPendingTaskLaunch((current) => {
      if (!current?.explicitPromptBinding) {
        return current;
      }
      return validateRouteBoundPromptBinding(current.explicitPromptBinding)
        .isValid
        ? current
        : null;
    });
  }, [pathname, resetWorkspaceTaskLaneState, validateRouteBoundPromptBinding]);

  useEffect(() => {
    if (!activeProactivePromptEntry || !activeProactivePrompt?.autoOpen) {
      return;
    }
    if (panelState.isOverlay && !panelState.isExpanded) {
      panelState.setExpanded(true);
    }
  }, [
    activeProactivePrompt?.autoOpen,
    activeProactivePromptEntry,
    panelState.isExpanded,
    panelState.isOverlay,
    panelState.setExpanded,
  ]);

  const cancelInteractiveAssistantResponse =
    useCallback(async (): Promise<void> => {
      setLastActionFeedback("Assistant response stopped.");
    }, []);
  const pendingExplicitPromptBindingRef = useRef<ExplicitPromptBinding | null>(
    null,
  );
  const pendingExplicitPromptBindingByQuestionRef = useRef(
    new Map<string, ExplicitPromptBinding>(),
  );
  const lastAssistantRequestIntentRef = useRef<{ settingsIntent: boolean }>({
    settingsIntent: false,
  });

  const askAI = useCallback(
    (
      question: string,
      historyForAPI: AssistantHistoryMessage[],
      contextOverride?: AdmissionsAIContext,
    ): Promise<AdmissionsAIResponse> => {
      const normalizedHistory =
        normalizeAssistantConversationHistoryForRequest(historyForAPI);
      const requestHistory =
        stripCurrentQuestionFromAssistantConversationHistory(
          normalizedHistory,
          question,
        );
      const historyPayload = sanitizeAwsJsonArray(requestHistory);
      const serializedHistory = JSON.stringify(historyPayload);
      const effectiveContext = contextOverride ?? context;
      const trimmedQuestion = question.trim();
      const pendingExplicitPromptBinding =
        pendingExplicitPromptBindingRef.current ??
        pendingExplicitPromptBindingByQuestionRef.current.get(trimmedQuestion) ??
        null;
      const effectiveRequestPathname =
        String((effectiveContext as AdmissionsAIContext | undefined)?.pathname ?? pathname);
      const effectiveRequestModule =
        resolveAssistantPlatformCompatibleModule(
          resolveAIAssistantModule(effectiveRequestPathname),
        );
      const effectiveRequestPersona =
        (effectiveContext as AdmissionsAIContext | undefined)?.personaRole ??
        effectivePersonaRole;
      const requestRouteCapabilities = listAssistantCapabilitiesForContext({
        pathname: effectiveRequestPathname,
        module: effectiveRequestModule,
        persona: effectiveRequestPersona,
        catalogScope: "route",
        strictRouteMatch: true,
      });
      const capabilityMatchesQuestion = (
        capability: PromptBindableAssistantCapability,
      ): boolean => {
        const promptText = (capability.prompt || capability.title).trim();
        return (
          promptText === trimmedQuestion ||
          capability.title.trim() === trimmedQuestion
        );
      };
      const capabilityMatchesRequestRoute = (
        capability: PromptBindableAssistantCapability,
      ): boolean =>
        routeMatchesAnyAssistantPattern(
          effectiveRequestPathname,
          capability.routePrefixes,
        );
      const inferredCapabilityBinding =
        pendingExplicitPromptBinding
          ? null
          : dedupe([
              ...requestRouteCapabilities,
              ...orderedCapabilities.filter(capabilityMatchesRequestRoute),
              ...localAssistantCapabilityFixture.filter(
                capabilityMatchesRequestRoute,
              ),
            ]).find(capabilityMatchesQuestion) ?? null;
      const explicitPromptBinding =
        pendingExplicitPromptBinding ??
        (inferredCapabilityBinding
          ? {
              module: inferredCapabilityBinding.module,
              activeCapabilityId: inferredCapabilityBinding.id,
              activeScenarioKey:
                resolvePromptBindableScenarioKey(inferredCapabilityBinding),
	              activeCapabilityMethod:
	                inferredCapabilityBinding.method ?? undefined,
	              executionPathname: resolveConcreteAssistantExecutionPathname({
	                targetRoute: resolvePromptBindableTargetRoute(
	                  inferredCapabilityBinding,
	                ),
	                routePrefixes: inferredCapabilityBinding.routePrefixes,
	                currentPathname: effectiveRequestPathname,
	                activeRoutePathname:
	                  activeRouteAssistantContext?.activeRouteContext?.pathname,
	                fallbackPathname: pathname,
	              }),
	              originPathname: pathname,
	              catalogScope: assistantCatalogScope,
	            }
          : null);
      const explicitPromptBindingValidation = validateRouteBoundPromptBinding(
        explicitPromptBinding,
      );
      const routeBoundPromptBinding = explicitPromptBindingValidation.isValid
        ? explicitPromptBinding
        : null;
      pendingExplicitPromptBindingRef.current = null;
      pendingExplicitPromptBindingByQuestionRef.current.delete(trimmedQuestion);
      if (explicitPromptBinding && !explicitPromptBindingValidation.isValid) {
        setLastActionFeedback(
          formatExplicitPromptBindingRouteMismatchMessage(
            explicitPromptBindingValidation,
          ),
        );
      }
      const {
        activeCapabilityId: _ignoredCapabilityId,
        activeScenarioKey: _ignoredScenarioKey,
        activeCapabilityMethod: _ignoredCapabilityMethod,
        ...requestContextBase
      } = (effectiveContext ?? {}) as AdmissionsAIContext;
	      const executionPathname =
	        routeBoundPromptBinding?.executionPathname ?? pathname;
	      const currentRequestPathname = resolveConcreteAssistantExecutionPathname({
	        targetRoute: executionPathname,
	        currentPathname: effectiveRequestPathname,
	        activeRoutePathname:
	          activeRouteAssistantContext?.activeRouteContext?.pathname,
	        fallbackPathname: pathname,
	      });
	      const executionActiveRouteContext =
	        activeRouteAssistantContext?.activeRouteContext?.pathname ===
	        currentRequestPathname
	          ? activeRouteAssistantContext.activeRouteContext
	          : null;
	      const routeContextFamilyId =
	        resolveFamilyIdFromActiveRouteContext(executionActiveRouteContext);
	      const executionModule = resolveAssistantPlatformCompatibleModule(
	        routeBoundPromptBinding?.module ??
	          resolveAIAssistantModule(currentRequestPathname),
	      );
	      const requestContext: AdmissionsAIContext = {
	        ...requestContextBase,
	        personaRole: effectiveContext?.personaRole ?? effectivePersonaRole,
	        pathname: currentRequestPathname,
	        originPathname:
	          routeBoundPromptBinding?.originPathname ??
	          (currentRequestPathname !== pathname ? pathname : undefined),
	        executionPathname:
	          executionPathname !== currentRequestPathname &&
	          !isAssistantRouteTemplatePathname(executionPathname)
	            ? executionPathname
	            : undefined,
	        catalogScope: assistantCatalogScope,
	        routeMetadataToken: resolveRouteMetadataToken(currentRequestPathname),
	        capabilityModule: executionModule,
	        module: executionModule,
	        ...(routeContextFamilyId
	          ? {
	              familyId: routeContextFamilyId,
	              familyIds: [routeContextFamilyId],
	            }
	          : {}),
      };
      if (!requestContext.activeCapabilityId) {
        const routeRequestCapabilities =
          listAssistantCapabilitiesForContext({
            pathname: requestContext.pathname,
            module: executionModule,
            persona: requestContext.personaRole,
            catalogScope: "route",
            strictRouteMatch: true,
          });
        const requestCapability =
          routeRequestCapabilities.find((capability) => (
            (capability.prompt || capability.title).trim() === trimmedQuestion ||
            capability.title.trim() === trimmedQuestion
          )) ??
          (/^flag at-risk applicants based on inactivity, missing docs, and stalled status\.?$/i.test(trimmedQuestion)
            ? resolveAssistantCapabilityById("admissions-dashboard-flag-at-risk-applicants")
            : null);
        const contextualFamilyCapability =
          requestCapability ??
          (isStudentsFamiliesFamilyRouteSummaryPrompt({
            pathname: requestContext.pathname,
            question: trimmedQuestion,
          })
            ? (
                routeRequestCapabilities.find((capability) =>
                  capability.id.startsWith("students-families-children-"),
                ) ??
                routeRequestCapabilities.find((capability) =>
                  capability.id.startsWith("students-families-family-"),
                ) ??
                routeRequestCapabilities.find((capability) =>
                  capability.id.startsWith("students-families-"),
                ) ??
                null
              )
            : null);
        if (contextualFamilyCapability) {
          requestContext.activeCapabilityId = contextualFamilyCapability.id;
          requestContext.activeScenarioKey =
            resolveAssistantCapabilityScenarioKey(contextualFamilyCapability);
          requestContext.activeCapabilityMethod =
            contextualFamilyCapability.method ?? undefined;
        }
      }
      const spreadsheetView = buildSpreadsheetViewContextFromSession(
        activeSpreadsheetWorkspace ?? currentSpreadsheetSession,
      );
      if (spreadsheetView) {
        requestContext.spreadsheetView = spreadsheetView;
      }
      if (reportGenerationSidecarContext) {
        requestContext.reportGenerationSidecar =
          reportGenerationSidecarContext;
      }
      if (activeRouteAssistantContext?.context) {
        requestContext.routeContext = activeRouteAssistantContext.context;
      }
      if (executionActiveRouteContext) {
        requestContext.activeRouteContext = executionActiveRouteContext;
      }
      if (routeBoundPromptBinding?.activeCapabilityId) {
        requestContext.activeCapabilityId =
          routeBoundPromptBinding.activeCapabilityId;
      }
      if (routeBoundPromptBinding?.activeScenarioKey) {
        requestContext.activeScenarioKey =
          routeBoundPromptBinding.activeScenarioKey;
      }
      if (routeBoundPromptBinding?.activeCapabilityMethod) {
        requestContext.activeCapabilityMethod =
          routeBoundPromptBinding.activeCapabilityMethod;
      }
      if (
        !requestContext.activeCapabilityId &&
        pathname.startsWith("/school/tuition")
      ) {
        requestContext.activeCapabilityId = "tuition-dashboard-summarize-account-risk";
        requestContext.activeScenarioKey = "tuition.dashboard.account-risk";
        requestContext.activeCapabilityMethod =
          "Review tuition accounts, billing context, and payment follow-up risk.";
      }
      lastAssistantRequestIntentRef.current = {
        settingsIntent: isSettingsIntentTurn({
          pathname: requestContext.pathname,
          resolvedModule: requestContext.module,
          activeCapabilityId: requestContext.activeCapabilityId,
          activeCapabilityMethod: requestContext.activeCapabilityMethod,
        }),
      };
      const requestContextPayload = sanitizeAwsJsonObject(requestContext);
      const runtimePreferences: AssistantRuntimePreferences = {
        preferredRuntime: resolveAssistantPreferredRuntime("auto", question),
        browser: browserCapabilities,
      };
      const runtimePreferencesPayload =
        sanitizeAwsJsonObject(runtimePreferences);
      const clientExecutionPlan = resolveAssistantExecutionPlan({
        question,
        preferences: runtimePreferences,
        policy: runtimePolicyHint ?? undefined,
        serverSupportsBrowserLocal: true,
      });
      const requestDebug = {
        requestedModelKey: modelKey,
        questionChars: question.length,
        questionTokensEstimate: estimateTokensFromChars(question.length),
        historyMessages: requestHistory.length,
        historyChars: serializedHistory.length,
        historyBytes: new TextEncoder().encode(serializedHistory).byteLength,
        historyTokensEstimate: estimateTokensFromChars(
          serializedHistory.length,
        ),
        context: summarizeContextForDebug(requestContext),
        activeRouteContextRef:
          requestContext.activeRouteContext?.contextRef ?? null,
        explicitPromptBinding: explicitPromptBindingValidation.hasBinding
          ? {
              isValid: explicitPromptBindingValidation.isValid,
              reasonCode: explicitPromptBindingValidation.reasonCode,
              targetLabel: explicitPromptBindingValidation.targetLabel,
            }
          : null,
        runtimePreferences,
        clientExecutionPlan,
      };
      console.log("[AdmissionsAI][Debug] Request", requestDebug);

      const requestArgs = {
        question,
        conversationHistory: historyPayload,
        context: requestContextPayload,
        model: modelKey,
        activeRouteContext: requestContext.activeRouteContext ?? null,
        runtimePreferences: runtimePreferencesPayload,
        routingHints: sanitizeAwsJsonObject({
          routeMetadataToken: requestContext.routeMetadataToken ?? null,
          activeCapabilityId: requestContext.activeCapabilityId ?? null,
          activeScenarioKey: requestContext.activeScenarioKey ?? null,
          activeCapabilityMethod: requestContext.activeCapabilityMethod ?? null,
          activeRouteContextRef:
            requestContext.activeRouteContext?.contextRef ?? null,
        }),
      };

      return runAssistantRequestWithTransientRetry(
        () => triggerAskSchoolAI(requestArgs),
        {
          maxAttempts: 2,
          retryDelayMs: 750,
          onRetry: ({ attempt, nextDelayMs, error }) => {
            console.warn(
              "[AdmissionsAI] Retrying transient assistant request",
              {
                attempt,
                nextDelayMs,
                message: readDomainRouterErrorMessage(
                  error,
                  error instanceof Error ? error.message : String(error ?? ""),
                ),
                pathname,
                capabilityId: requestContext.activeCapabilityId ?? null,
              },
            );
          },
        },
      )
        .then((payload) => coerceAdmissionsAIResponse(payload))
        .then((response) => {
          const debug = normalizeDebugMetadata(response.debug);
          const selectedModelKey =
            typeof debug?.model?.key === "string" ? debug.model.key : null;
          const datasetSummary = (debug?.datasets ?? []).map((dataset) => ({
            name: dataset?.name ?? null,
            domainModelType: dataset?.domainModelType ?? null,
            sources: dataset?.sources ?? [],
            availableCount: dataset?.availableCount ?? null,
            includedCount: dataset?.includedCount ?? null,
            limitCount: dataset?.limitCount ?? null,
            projectionFields: dataset?.projectionFields ?? [],
            selectedByMethod: dataset?.selectedByMethod ?? null,
            payload: dataset?.payload ?? null,
            truncated: dataset?.truncated ?? null,
          }));
          const responseDebug = {
            requestedModelKey: modelKey,
            selectedModelKey,
            modelLabel: debug?.model?.label ?? null,
            requestedModelSelectable:
              debug?.model?.requestedModelSelectable ?? null,
            availabilitySourceStatus:
              debug?.model?.availabilitySourceStatus ?? null,
            estimatedPromptTokens: debug?.model?.estimatedPromptTokens ?? null,
            modelBudgetTokens: debug?.model?.modelBudgetTokens ?? null,
            promptBudgetTokens: debug?.model?.promptBudgetTokens ?? null,
            overflow: debug?.model?.overflow ?? null,
            payloadSizes: debug?.payloadSizes ?? null,
            datasets: datasetSummary,
            sources: debug?.sources ?? null,
            methodProjection: debug?.methodProjection ?? null,
            backendExecutionPlan: debug?.execution ?? null,
            clientExecutionPlan,
            answerChars: response.answer.length,
            citationCount: response.citations.length,
          };
          console.log("[AdmissionsAI][Debug] Response", responseDebug);
          if (!debug) {
            console.warn(
              "[AdmissionsAI][Debug] Backend debug metadata unavailable",
              {
                requestedModelKey: modelKey,
                answerChars: response.answer.length,
                citationCount: response.citations.length,
              },
            );
          }
          if (
            debug?.responseDecision === "model_window_overflow" ||
            debug?.responseDecision === "prompt_budget_overflow"
          ) {
            console.error("[AdmissionsAI][Debug] Context overflow", {
              responseDecision: debug.responseDecision,
              requestedModelKey: modelKey,
              selectedModelKey,
              estimatedPromptTokens:
                debug?.model?.estimatedPromptTokens ?? null,
              modelBudgetTokens: debug?.model?.modelBudgetTokens ?? null,
              promptBudgetTokens: debug?.model?.promptBudgetTokens ?? null,
              payloadSizes: debug?.payloadSizes ?? null,
              datasets: datasetSummary,
              sources: debug?.sources ?? null,
            });
          }
          if (selectedModelKey && selectedModelKey !== modelKey) {
            console.warn(
              "[AdmissionsAI][Debug] Requested model key was not selected by backend",
              {
                requestedModelKey: modelKey,
                selectedModelKey,
                requestedModelSelectable:
                  debug?.model?.requestedModelSelectable ?? null,
                availabilitySourceStatus:
                  debug?.model?.availabilitySourceStatus ?? null,
              },
            );
          }
          if (debug?.execution?.fallbackReason) {
            console.warn("[AdmissionsAI][Debug] Runtime fallback", {
              fallbackReason: debug.execution.fallbackReason,
              selectedMode: debug.execution.selectedMode ?? null,
              recommendedMode: debug.execution.recommendedMode ?? null,
              requiresOptIn: debug.execution.requiresOptIn ?? null,
            });
          }
          const tokenUsage = extractDebugTokenUsage(debug);
          trackLogRocketEvent("ai_assistant_response_received", {
            route: pathname || null,
            module: capabilityModule,
            variant: isSchoolShellPath(pathname) ? "school-shell" : "default",
            requestedModelKey: modelKey,
            selectedModelKey,
            selectedModelLabel: debug?.model?.label ?? null,
            responseDecision:
              typeof debug?.responseDecision === "string"
                ? debug.responseDecision
                : null,
            citationCount: response.citations.length,
            answerChars: response.answer.length,
            preferredRuntime: runtimePreferences.preferredRuntime ?? "auto",
            tokenInput: tokenUsage?.inputTokens ?? null,
            tokenOutput: tokenUsage?.outputTokens ?? null,
            tokenTotal: tokenUsage?.totalTokens ?? null,
          });
          return response;
        });
    },
    [
      activeSpreadsheetWorkspace,
      activeRouteAssistantContext?.activeRouteContext,
      activeRouteAssistantContext?.context,
      assistantCatalogScope,
      browserCapabilities,
      capabilityModule,
      context,
      currentSpreadsheetSession,
      effectivePersonaRole,
      localAssistantCapabilityFixture,
      modelKey,
      orderedCapabilities,
      pathname,
      reportGenerationSidecarContext,
      runtimePolicyHint,
      triggerAskSchoolAI,
      validateRouteBoundPromptBinding,
    ],
  );

  const buildPendingRunReportsAssistantMessage = useCallback(
    ({
      assistantMessageId,
      question,
    }: {
      assistantMessageId: string;
      question: string;
    }): AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction> | null => {
      if (!runReportsSidecarMode) {
        return null;
      }
      const activeRouteContext =
        activeRouteAssistantContextRef.current?.activeRouteContext ?? null;
      if (activeRouteContext?.routeFamily !== "run-reports") {
        return null;
      }
      const actionResolution =
        resolveRunReportsWorkspaceDraftActionForPrompt(question);
      if (!actionResolution) {
        return null;
      }
      const action = {
        ...actionResolution.action,
        type: "draft",
        kind: "draft",
        payload: {
          ...(actionResolution.action.payload ?? {}),
        },
      } as AdmissionsAIContextualAction;
      const visibleMetrics = activeRouteContext.visibleStateSummary.metrics
        .slice(0, 4)
        .map((metric, index) => ({
          id: `run-reports-preview-metric-${index + 1}`,
          label: metric.label,
          value: metric.value,
          tone: "info" as const,
        }));
      const summary = `Preparing ${action.label.toLowerCase()} for the visible Run Reports workspace. Deterministic workspace checks are ready while the readable summary is generated.`;
      const presentation: AssistantPresentationModel = {
        version: "1.0.0",
        routeFamily: "run-reports",
        module: "tuition",
        responseKind: "draft_creator",
        sections: [
          {
            type: "summary",
            title: "Run Reports workspace action",
            text: summary,
          },
          {
            type: "metric_grid",
            title: "Visible workspace signal",
            items: visibleMetrics.length > 0
              ? visibleMetrics
              : [
                  {
                    id: "run-reports-preview-context",
                    label: "Workspace",
                    value: "Captured",
                    detail: activeRouteContext.visibleStateSummary.summary,
                    tone: "info",
                  },
                ],
          },
          {
            type: "checklist",
            title: "Action flow",
            items: [
              {
                id: "run-reports-preview-deterministic-ready",
                label: "Show deterministic workspace indicators immediately.",
                status: "ready",
              },
              {
                id: "run-reports-preview-manual-apply",
                label: `${action.label} remains manual and can be applied or re-applied from this response.`,
                status: "todo",
              },
              {
                id: "run-reports-preview-llm-summary",
                label: "Generate the readable summary without delaying workspace controls.",
                status: "watch",
              },
            ],
          },
        ],
      };
      return {
        messageId: assistantMessageId,
        role: "assistant",
        content: summary,
        actions: [action],
        presentation,
        metadata: {
          assistantResponsePhase: "deterministic-preview",
          transientConversationMessage: true,
          presentation,
        } satisfies AdmissionsAssistantMessageMetadata,
        timestamp: Date.now(),
      };
    },
    [runReportsSidecarMode],
  );

  const conversation = useAssistantConversation<
    AdmissionsAIContext,
    AdmissionsAIResponse,
    AdmissionsCitation,
    AdmissionsAIContextualAction
  >({
    context,
    setContext,
    readStoredContext,
    ask: askAI,
    buildPendingAssistantMessage: buildPendingRunReportsAssistantMessage,
    cancelCurrentResponse: cancelInteractiveAssistantResponse,
    onErrorMessage: toUserSafeAssistantErrorMessage,
    responseMapper: (response) => {
      const sanitizedResponse = sanitizeAdmissionsAIResponseForPresentation({
        response,
        module: capabilityModule,
      });
      const debug = normalizeDebugMetadata(sanitizedResponse.debug);
      return {
        answer: sanitizedResponse.answer,
        citations: sanitizedResponse.citations,
        actions: sanitizedResponse.actions,
        presentation: sanitizedResponse.presentation,
        metadata: {
          feedbackContext: buildAssistantFeedbackContext({
            assistantEpisodeId,
            pathname,
            routeMetadataToken: resolveRouteMetadataToken(pathname),
            persona: effectivePersonaRole,
            debug,
          }),
          ...buildLiveAssistantResponseRenderMetadata(
            sanitizedResponse.presentation,
          ),
          renderHints: extractAssistantRenderHints(debug),
          presentation: sanitizedResponse.presentation,
          presentationDiagnostics: sanitizedResponse.presentationDiagnostics,
          settingsIntent: isSettingsIntentTurn({
            pathname,
            resolvedModule: debug?.module,
            activeCapabilityId: debug?.context?.activeCapabilityId ?? null,
            activeCapabilityMethod:
              debug?.context?.activeCapabilityMethod ?? null,
            settingsIntent:
              lastAssistantRequestIntentRef.current.settingsIntent,
          }),
        } satisfies AdmissionsAssistantMessageMetadata,
      };
    },
    onFeedbackRequest: handleAssistantFeedbackRequest,
    allowLocalFeedbackFallback: false,
  });

  const currentConversationSession = useMemo(
    () =>
      conversationSessions.find(
        (session) => session.id === currentConversationSessionId,
      ) ?? null,
    [conversationSessions, currentConversationSessionId],
  );
  const previousConversationSession = useMemo(() => {
    if (conversationSessions.length === 0) {
      return null;
    }
    const currentIndex = conversationSessions.findIndex(
      (session) => session.id === currentConversationSessionId,
    );
    if (currentIndex >= 0) {
      return conversationSessions[currentIndex + 1] ?? null;
    }
    return conversationSessions[1] ?? null;
  }, [conversationSessions, currentConversationSessionId]);
  const shouldPreferFreshSchoolShellConversationSession = useMemo(() => {
    const usesSchoolShellSurface = Boolean(
      forceSchoolShellDesign ??
        (layout === "sidebar" && isSchoolShellPath(pathname)),
    );
    if (!usesSchoolShellSurface) {
      return false;
    }
    const normalizedPathname = pathname.toLowerCase().replace(/\/+$/, "");
    return (
      isStudentsFamiliesFamilyDetailRoute(pathname) ||
      routeFamily.suggestedRoutes.some(
        (entry) =>
          entry.path.toLowerCase().replace(/\/+$/, "") === normalizedPathname,
      )
    );
  }, [forceSchoolShellDesign, layout, pathname, routeFamily.suggestedRoutes]);

  useEffect(() => {
    activeConversationSessionIdRef.current = currentConversationSessionId;
  }, [currentConversationSessionId]);

  const conversationIsLoadingRef = useRef(conversation.isLoading);
  const conversationMessagesRef = useRef(conversation.messages);
  const pendingQuestionRef = useRef<string | null>(
    conversation.pendingQuestion,
  );
  const livePromptHydrationProtectionUntilRef = useRef(0);

  useEffect(() => {
    conversationIsLoadingRef.current = conversation.isLoading;
  }, [conversation.isLoading]);

  useEffect(() => {
    conversationMessagesRef.current = conversation.messages;
  }, [conversation.messages]);

  useEffect(() => {
    pendingQuestionRef.current = conversation.pendingQuestion;
  }, [conversation.pendingQuestion]);

  const isLivePromptHydrationProtected = useCallback(
    () =>
      Date.now() < livePromptHydrationProtectionUntilRef.current &&
      (conversationIsLoadingRef.current ||
        Boolean(pendingQuestionRef.current) ||
        conversationMessagesRef.current.length > 0),
    [],
  );

  const markConversationSessionMutation = useCallback(
    (sessionId?: string | null) => {
      const resolvedSessionId =
        sessionId ?? activeConversationSessionIdRef.current;
      if (!resolvedSessionId) {
        return;
      }
      sessionConversationMutationEpochRef.current[resolvedSessionId] =
        Date.now();
    },
    [],
  );

  const replaceConversationMessagesForSession = useCallback(
    (
      sessionId: string | null,
      messages: AssistantMessage<
        AdmissionsCitation,
        AdmissionsAIContextualAction
      >[],
    ) => {
      conversationMessagesRef.current = messages;
      conversation.replaceMessages(messages);
      conversationTranscriptSessionIdRef.current = sessionId;
    },
    [conversation.replaceMessages],
  );

  useEffect(() => {
    if (!currentConversationSessionId) {
      return;
    }
    if (
      conversationTranscriptSessionIdRef.current !==
      currentConversationSessionId
    ) {
      return;
    }
    const persistedMessageIds =
      persistedConversationMessageIdsRef.current[
        currentConversationSessionId
      ] ?? new Set<string>();
    const hasUnpersistedLocalMessages = conversation.messages.some(
      (message) =>
        Boolean(message.messageId) &&
        !persistedMessageIds.has(String(message.messageId)),
    );
    if (!hasUnpersistedLocalMessages) {
      return;
    }
    if (
      !conversation.pendingQuestion &&
      !conversation.isLoading &&
      persistedMessageIds.size === 0
    ) {
      return;
    }
    markConversationSessionMutation(currentConversationSessionId);
  }, [
    conversation.isLoading,
    conversation.messages,
    conversation.pendingQuestion,
    currentConversationSessionId,
    markConversationSessionMutation,
  ]);

  const persistConversationTimelineCache = useCallback(
    (timeline: AssistantConversationTimelineState) => {
      const mergedTimeline = mergeConversationTimelineState(
        conversationTimelinesBySessionIdRef.current[timeline.session.id],
        timeline,
      );
      conversationTimelinesBySessionIdRef.current = {
        ...conversationTimelinesBySessionIdRef.current,
        [timeline.session.id]: mergedTimeline,
      };
      setConversationTimelinesBySessionId((current) => ({
        ...current,
        [timeline.session.id]: mergedTimeline,
      }));
      setConversationTimelineMetaBySessionId((current) => ({
        ...current,
        [timeline.session.id]: {
          status: "ready",
          errorMessage: null,
        },
      }));
      persistedConversationMessageIdsRef.current[timeline.session.id] = new Set(
        mergedTimeline.messages.map((message) => message.messageId),
      );
      setConversationSessions((current) => {
        const nextSessions = upsertConversationSessionList(
          current,
          mergedTimeline.session,
        );
        return nextSessions;
      });
    },
    [],
  );

  const setConversationTimelineMeta = useCallback(
    (
      sessionId: string,
      status: AssistantConversationTimelineMeta["status"],
      errorMessage?: string | null,
    ) => {
      setConversationTimelineMetaBySessionId((current) => ({
        ...current,
        [sessionId]: {
          status,
          errorMessage: errorMessage ?? null,
        },
      }));
    },
    [],
  );

  const loadConversationTimeline = useCallback(
    async (sessionId: string) => {
      const cached = conversationTimelinesBySessionId[sessionId];
      if (cached) {
        setConversationTimelineMeta(sessionId, "ready", null);
        return cached;
      }
      const inFlight = conversationTimelineLoadPromisesRef.current[sessionId];
      if (inFlight) {
        return inFlight;
      }
      setConversationTimelineMeta(sessionId, "loading", null);
      const requestPromise = (async () => {
        try {
          const payload = await triggerGetAssistantConversationSessionTimeline(
            "getAssistantConversationSessionTimeline",
            {
              sessionId,
            },
          );
          const root = toRecord(payload);
          const session = toConversationSessionRecord(root?.session);
          const messages = Array.isArray(root?.messages)
            ? root.messages
                .map((message) => toConversationMessageRecord(message))
                .filter(
                  (
                    message,
                  ): message is IntellioAssistantConversationMessageRecord =>
                    Boolean(message),
                )
            : [];
          if (!session) {
            setConversationTimelineMeta(
              sessionId,
              "error",
              "The conversation timeline is unavailable for this session.",
            );
            return null;
          }
          const timeline = {
            session,
            messages,
          } satisfies AssistantConversationTimelineState;
          persistConversationTimelineCache(timeline);
          return timeline;
        } catch (error) {
          const errorMessage = readDomainRouterErrorMessage(
            error,
            error instanceof Error
              ? error.message
              : "Unable to load the selected conversation timeline.",
          );
          setConversationTimelineMeta(sessionId, "error", errorMessage);
          throw error;
        } finally {
          delete conversationTimelineLoadPromisesRef.current[sessionId];
        }
      })();
      conversationTimelineLoadPromisesRef.current[sessionId] = requestPromise;
      return requestPromise;
    },
    [
      conversationTimelinesBySessionId,
      persistConversationTimelineCache,
      setConversationTimelineMeta,
      triggerGetAssistantConversationSessionTimeline,
    ],
  );

  const startFreshConversationSession = useCallback(
    async (closeCurrent: boolean) => {
      if (
        startFreshConversationSessionPromiseRef.current &&
        (!closeCurrent || startFreshConversationSessionCloseCurrentRef.current)
      ) {
        return startFreshConversationSessionPromiseRef.current;
      }
      const operationStartedResetEpoch = conversationResetEpochRef.current;
      const operation = (async () => {
        if (assistantConversationSessionMode === "unsupported") {
          setConversationMessagesHydrated(true);
          return null;
        }
        if (!assistantActorId) {
          return null;
        }
        if (closeCurrent && currentConversationSessionId) {
          try {
            const closedPayload =
              await triggerCloseAssistantConversationSession({
                input: {
                  sessionId: currentConversationSessionId,
                },
              });
            const closedSession = toConversationSessionRecord(
              toRecord(closedPayload)?.session,
            );
            if (closedSession) {
              setConversationSessions((current) =>
                upsertConversationSessionList(current, closedSession),
              );
            }
          } catch (error) {
            console.warn(
              "[AdmissionsAI] Failed to close assistant conversation session",
              error,
            );
          }
        }

        let payload: unknown;
        try {
          payload = await triggerStartAssistantConversationSession({
            input: {
              pathname,
              module: capabilityModule,
              persona: effectivePersonaRole,
              title: "Current session",
              routeLabel: moduleConfig.label,
            },
          });
        } catch (error) {
          if (
            isUnsupportedAssistantConversationSessionOperation(
              error,
              "startAssistantConversationSession",
            )
          ) {
            setAssistantConversationSessionMode("unsupported");
            setConversationMessagesHydrated(true);
            return null;
          }
          throw error;
        }
        const session = toConversationSessionRecord(toRecord(payload)?.session);
        if (!session) {
          throw new Error("Assistant conversation session creation failed.");
        }
        if (
          !closeCurrent &&
          operationStartedResetEpoch !== conversationResetEpochRef.current
        ) {
          return session;
        }
        setAssistantConversationSessionMode("supported");
        persistConversationTimelineCache({
          session,
          messages: [],
        });
        const freshSessionGuard =
          pendingFreshConversationSessionGuardRef.current;
        if (freshSessionGuard) {
          pendingFreshConversationSessionGuardRef.current = {
            ...freshSessionGuard,
            pendingFreshSessionId: session.id,
          };
        }
        const shouldResetExistingSessionState = Boolean(
          closeCurrent ||
            currentConversationSessionId ||
            activeConversationSessionIdRef.current,
        );
        if (shouldResetExistingSessionState) {
          resetSessionScopedUiState({
            preservePendingTaskLaunch: Boolean(pendingTaskLaunchRef.current),
          });
        }
        if (
          !shouldResetExistingSessionState &&
          (conversationMessagesRef.current.length > 0 ||
            Boolean(pendingQuestionRef.current) ||
            conversationIsLoadingRef.current)
        ) {
          conversationTranscriptSessionIdRef.current = session.id;
        }
        pendingAnchorRef.current = null;
        setCurrentConversationSessionId(session.id);
        setSelectedThreadsSessionId(session.id);
        setAssistantSurfaceView("chat");
        return session;
      })();
      startFreshConversationSessionPromiseRef.current = operation;
      startFreshConversationSessionCloseCurrentRef.current = closeCurrent;
      try {
        return await operation;
      } finally {
        if (startFreshConversationSessionPromiseRef.current === operation) {
          startFreshConversationSessionPromiseRef.current = null;
          startFreshConversationSessionCloseCurrentRef.current = false;
        }
      }
    },
    [
      assistantActorId,
      assistantConversationSessionMode,
      capabilityModule,
      currentConversationSessionId,
      effectivePersonaRole,
      moduleConfig.label,
      pathname,
      persistConversationTimelineCache,
      resetSessionScopedUiState,
      triggerCloseAssistantConversationSession,
      triggerStartAssistantConversationSession,
    ],
  );

  const loadConversationSessions = useCallback(async () => {
    if (assistantConversationSessionMode === "unsupported") {
      setConversationMessagesHydrated(true);
      return;
    }
    if (!assistantActorId) {
      return;
    }
    const requestResetEpoch = conversationResetEpochRef.current;
    let payload: unknown;
    try {
      payload = await triggerListAssistantConversationSessions(
        "listAssistantConversationSessions",
        {
          input: {
            includeClosed: true,
            limit: 40,
          },
        },
      );
    } catch (error) {
      if (requestResetEpoch !== conversationResetEpochRef.current) {
        return;
      }
      if (
        isUnsupportedAssistantConversationSessionOperation(
          error,
          "listAssistantConversationSessions",
        )
      ) {
        setAssistantConversationSessionMode("unsupported");
        setConversationMessagesHydrated(true);
        setConversationSessions([]);
        setCurrentConversationSessionId(null);
        setConversationSessionsFallbackStatus("live");
        setConversationSessionsFallbackUpdatedAt(null);
        return;
      }
      const message = readDomainRouterErrorMessage(
        error,
        error instanceof Error
          ? error.message
          : "Unable to load assistant conversation sessions.",
      );
      setConversationMessagesHydrated(true);
      setConversationSessionsFallbackStatus("unavailable");
      setConversationSessionsFallbackUpdatedAt(null);
      console.warn(
        "[AdmissionsAI] Failed to load assistant conversation sessions",
        {
          message,
          error,
        },
      );
      return;
    }
    if (requestResetEpoch !== conversationResetEpochRef.current) {
      return;
    }
    const record = toRecord(payload);
    const items = Array.isArray(record?.items)
      ? record.items
          .map((entry) => toConversationSessionRecord(entry))
          .filter(
            (entry): entry is IntellioAssistantConversationSessionRecord =>
              Boolean(entry),
          )
      : [];
    setAssistantConversationSessionMode("supported");
    setConversationSessions(items);
    setConversationSessionsFallbackStatus("live");
    setConversationSessionsFallbackUpdatedAt(null);
    const pendingFreshSessionGuard =
      pendingFreshConversationSessionGuardRef.current;
    const preferredCurrentSessionId = selectConversationSessionIdWithFreshSessionGuard({
      sessions: items,
      currentConversationSessionId,
      pathname,
      moduleId: capabilityModule,
      preferFreshEmptySession: shouldPreferFreshSchoolShellConversationSession,
      pendingFreshSessionGuard,
    });

    if (isLivePromptHydrationProtected()) {
      setConversationMessagesHydrated(true);
      return;
    }

    if (pendingFreshSessionGuard && !preferredCurrentSessionId) {
      setCurrentConversationSessionId(null);
      setSelectedThreadsSessionId(null);
      setConversationMessagesHydrated(true);
      return;
    }

    if (!preferredCurrentSessionId) {
      const session = await startFreshConversationSession(false);
      if (requestResetEpoch !== conversationResetEpochRef.current) {
        return;
      }
      if (session) {
        setConversationSessions([session]);
      }
      return;
    }

    setCurrentConversationSessionId(preferredCurrentSessionId);
    if (
      pendingFreshSessionGuard?.pendingFreshSessionId ===
        preferredCurrentSessionId &&
      items.some((session) => session.id === preferredCurrentSessionId)
    ) {
      pendingFreshConversationSessionGuardRef.current = null;
    }
    if (
      preferredCurrentSessionId !== activeConversationSessionIdRef.current &&
      activeConversationSessionIdRef.current
    ) {
      resetSessionScopedUiState({
        preservePendingTaskLaunch: Boolean(pendingTaskLaunchRef.current),
      });
      return;
    }

    setSelectedThreadsSessionId((current) => {
      if (!current) {
        return preferredCurrentSessionId;
      }
      const selectedSession = items.find((session) => session.id === current);
      if (
        selectedSession &&
        doesConversationSessionMatchRoute(
          selectedSession,
          pathname,
          capabilityModule,
        )
      ) {
        return current;
      }
      return preferredCurrentSessionId;
    });
    return;
  }, [
    assistantActorId,
    assistantConversationSessionMode,
    capabilityModule,
    currentConversationSessionId,
    isLivePromptHydrationProtected,
    pathname,
    resetSessionScopedUiState,
    shouldPreferFreshSchoolShellConversationSession,
    startFreshConversationSession,
    triggerListAssistantConversationSessions,
  ]);

  const closePrototypeRecentsTray = useCallback(() => {
    setPrototypeUtilityPaneOpen(false);
    setPrototypeParityState((current) =>
      current ? closeAssistantPrototypeThreadTray(current) : current,
    );
  }, []);

  const openConversationSession = useCallback(
    async (
      sessionId: string,
      messageId: string | null | undefined,
      view: AIAssistantWorkspaceTabId = "chat",
    ) => {
      const scrollToSessionMessage = (targetMessageId: string | null) => {
        if (typeof window === "undefined") {
          return;
        }
        window.setTimeout(() => {
          const selector = targetMessageId
            ? `[data-message-id="${targetMessageId}"]`
            : '[data-testid="ai-session-block-current"]';
          const target = document.querySelector(selector) as HTMLElement | null;
          target?.scrollIntoView({ block: "start", behavior: "smooth" });
        }, 40);
      };

      const nextSessionId = sessionId;
      const nextMessageId = messageId ?? null;
      const isSameActiveSession =
        sessionId === activeConversationSessionIdRef.current;
      pendingFreshConversationSessionGuardRef.current = null;
      pendingAnchorRef.current = {
        sessionId: nextSessionId,
        messageId: nextMessageId,
        intent: "explicit-jump",
      };
      closePrototypeRecentsTray();
      if (!isSameActiveSession) {
        resetSessionScopedUiState({
          clearWorkspaceTaskLane: true,
        });
        setConversationMessagesHydrated(false);
        const cachedTimeline =
          conversationTimelinesBySessionIdRef.current[sessionId] ?? null;
        if (cachedTimeline) {
          replaceConversationMessagesForSession(
            sessionId,
            cachedTimeline.messages.map((message) =>
              toAssistantMessageFromConversationRecord(message),
            ),
          );
          setConversationMessagesHydrated(true);
        } else {
          replaceConversationMessagesForSession(null, []);
        }
      }
      setSelectedThreadsSessionId(nextSessionId);
      setCurrentConversationSessionId(nextSessionId);
      setAssistantSurfaceView(view);
      if (isSameActiveSession) {
        scrollToSessionMessage(nextMessageId);
      }
    },
    [
      closePrototypeRecentsTray,
      replaceConversationMessagesForSession,
      resetSessionScopedUiState,
    ],
  );

  useEffect(() => {
    if (!assistantActorId) {
      return;
    }
    let cancelled = false;
    void loadConversationSessions().catch((error) => {
      if (!cancelled) {
        console.warn(
          "[AdmissionsAI] Failed to load assistant conversation sessions",
          error,
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [assistantActorId, loadConversationSessions]);

  useEffect(() => {
    if (assistantConversationSessionMode === "unsupported") {
      setConversationMessagesHydrated(true);
      return;
    }
    if (!currentConversationSessionId) {
      return;
    }
    let cancelled = false;
    const sessionId = currentConversationSessionId;
    const hydrationResetEpoch = conversationResetEpochRef.current;
    setConversationMessagesHydrated(false);
    const hydrationRequestStartedAt = Date.now();

    const hydrateTimeline = async () => {
      const timeline = await loadConversationTimeline(sessionId);
      if (cancelled) {
        return;
      }
      if (!timeline) {
        return;
      }
      if (
        hydrationResetEpoch !== conversationResetEpochRef.current ||
        !shouldHydrateConversationTimelineForFreshSessionGuard({
          sessionId,
          pendingFreshSessionGuard:
            pendingFreshConversationSessionGuardRef.current,
        })
      ) {
        setConversationMessagesHydrated(true);
        return;
      }
      if (isLivePromptHydrationProtected()) {
        setConversationMessagesHydrated(true);
        return;
      }
      const persistedMessageIds =
        persistedConversationMessageIdsRef.current[sessionId] ??
        new Set<string>();
      const currentConversationMessages = conversationMessagesRef.current;
      const transcriptBelongsToSession =
        conversationTranscriptSessionIdRef.current === sessionId;
      const hasUnpersistedLocalMessages =
        transcriptBelongsToSession &&
        currentConversationMessages.some(
          (message) =>
            Boolean(message.messageId) &&
            !persistedMessageIds.has(String(message.messageId)),
        );
      const hasInFlightTaskProposal =
        transcriptBelongsToSession && Boolean(pendingTaskLaunchRef.current);
      // Timeline refreshes can complete after a live prompt has already started.
      // Do not clobber in-flight local assistant state with a stale persisted snapshot.
      const pendingAnchor = pendingAnchorRef.current;
      const isExplicitSessionJump =
        pendingAnchor?.intent === "explicit-jump" &&
        pendingAnchor.sessionId === sessionId;
      if (
        activeConversationSessionIdRef.current === sessionId &&
        transcriptBelongsToSession &&
        (conversationIsLoadingRef.current ||
          pendingQuestionRef.current ||
          hasUnpersistedLocalMessages ||
          hasInFlightTaskProposal)
      ) {
        setConversationMessagesHydrated(true);
        return;
      }
      if (
        (sessionConversationMutationEpochRef.current[sessionId] ?? 0) >
        hydrationRequestStartedAt
      ) {
        setConversationMessagesHydrated(true);
        return;
      }
      replaceConversationMessagesForSession(
        sessionId,
        timeline.messages.map((message) =>
          toAssistantMessageFromConversationRecord(message),
        ),
      );
      setConversationMessagesHydrated(true);
    };

    void hydrateTimeline().catch((error) => {
      if (!cancelled) {
        if (hydrationResetEpoch !== conversationResetEpochRef.current) {
          setConversationMessagesHydrated(true);
          return;
        }
        console.warn(
          "[AdmissionsAI] Failed to hydrate assistant conversation timeline",
          error,
        );
        replaceConversationMessagesForSession(sessionId, []);
        setConversationMessagesHydrated(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    assistantConversationSessionMode,
    currentConversationSessionId,
    isLivePromptHydrationProtected,
    loadConversationTimeline,
    replaceConversationMessagesForSession,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const queryScenario =
      parseAssistantResponseVisualFixtureScenarioFromSearch(search);
    if (queryScenario) {
      setAssistantResponseFixtureSelection({
        routeFamilyId: routeFamily.id,
        scenarioId: queryScenario,
      });
      return;
    }
    // Proof fixtures are explicit proof-lane state only. Stale local storage must not
    // contaminate the normal assistant runtime on owned routes.
    setAssistantResponseFixtureSelection(null);
  }, [routeFamily.id, search]);

  const assistantResponseFixtureState = useMemo(() => {
    if (
      !assistantResponseFixtureSelection ||
      assistantResponseFixtureSelection.routeFamilyId !== routeFamily.id
    ) {
      return null;
    }
    return resolveAssistantResponseVisualFixtureState(
      assistantResponseFixtureSelection.routeFamilyId,
      assistantResponseFixtureSelection.scenarioId ?? "canonical",
    );
  }, [assistantResponseFixtureSelection, routeFamily.id]);

  useEffect(() => {
    if (!assistantResponseFixtureState?.messages?.length) {
      return;
    }
    replaceConversationMessagesForSession(
      null,
      assistantResponseFixtureState.messages,
    );
    setConversationMessagesHydrated(true);
  }, [assistantResponseFixtureState, replaceConversationMessagesForSession]);

  useEffect(() => {
    setPrototypeParityState(parseAssistantPanelV2ProofStateFromSearch(search));
  }, [search]);

  useEffect(() => {
    if (assistantReportGenerationProofMode || runReportsRouteLocalMode) {
      return;
    }
    reportProofPresetRef.current = null;
    reportGenerationWorkspaceModel.actions.closeWorkspace();
    reportGenerationWorkspaceModel.actions.closeVersionHistory();
    reportGenerationWorkspaceModel.actions.closeExport();
    reportGenerationWorkspaceModel.actions.setContextMenuField(null);
    reportGenerationWorkspaceModel.actions.setSelectedHistoryEntry(null);
  }, [
    assistantReportGenerationProofMode,
    pathname,
    reportGenerationWorkspaceModel.actions,
    runReportsRouteLocalMode,
  ]);

  useEffect(() => {
    if (!reportProofPreset) {
      reportProofPresetRef.current = null;
      return;
    }
    const presetSignature = [
      reportProofPreset,
      reportGenerationWorkspaceModel.galleryTemplates.length,
      reportGenerationWorkspaceModel.myTemplates.length,
      reportGenerationWorkspaceModel.historyEntries.length,
      reportGenerationWorkspaceModel.availableFields.length,
    ].join(":");
    if (reportProofPresetRef.current === presetSignature) {
      return;
    }
    reportProofPresetRef.current = presetSignature;

    const presetState = REPORT_GENERATION_PROOF_PRESETS[reportProofPreset];
    const matchingGalleryTemplate = presetState.reportTitle
      ? (reportGenerationWorkspaceModel.galleryTemplates.find(
          (template) => template.name === presetState.reportTitle,
        ) ?? null)
      : null;
    const matchingCustomTemplate = presetState.versionHistoryTemplateName
      ? (reportGenerationWorkspaceModel.myTemplates.find(
          (template) =>
            template.name === presetState.versionHistoryTemplateName,
        ) ?? null)
      : presetState.reportTitle
        ? (reportGenerationWorkspaceModel.myTemplates.find(
            (template) => template.name === presetState.reportTitle,
          ) ?? null)
        : null;
    const matchingHistoryEntry = presetState.historyTemplateName
      ? (reportGenerationWorkspaceModel.historyEntries.find(
          (entry) => entry.templateName === presetState.historyTemplateName,
        ) ?? null)
      : null;

    reportGenerationWorkspaceModel.actions.clearAll();
    reportGenerationWorkspaceModel.actions.closeVersionHistory();
    reportGenerationWorkspaceModel.actions.closeExport();
    reportGenerationWorkspaceModel.actions.setContextMenuField(null);
    reportGenerationWorkspaceModel.actions.setSelectedHistoryEntry(null);
    reportGenerationWorkspaceModel.actions.setCollapsed(
      Boolean(presetState.collapsed),
    );
    reportGenerationWorkspaceModel.actions.openWorkspace(
      presetState.activeTab ?? "templates",
    );
    reportGenerationWorkspaceModel.actions.setActiveTab(
      presetState.activeTab ?? "templates",
    );
    reportGenerationWorkspaceModel.actions.setTemplateView(
      presetState.templateView ?? "gallery",
    );
    reportGenerationWorkspaceModel.actions.setReportTitle(
      presetState.reportTitle ?? `${moduleConfig.label} report builder`,
    );

    if (matchingHistoryEntry && presetState.activeTab === "history") {
      reportGenerationWorkspaceModel.actions.setSelectedHistoryEntry(
        matchingHistoryEntry.id,
      );
    }

    if (matchingCustomTemplate && presetState.versionHistoryTemplateName) {
      reportGenerationWorkspaceModel.actions.openVersionHistory(
        matchingCustomTemplate.id,
      );
    }

    if (
      matchingCustomTemplate &&
      !presetState.versionHistoryTemplateName &&
      presetState.templateView === "my" &&
      presetState.runReport
    ) {
      reportGenerationWorkspaceModel.actions.applyTemplateById(
        matchingCustomTemplate.id,
        "custom",
      );
    } else if (matchingGalleryTemplate && presetState.runReport) {
      reportGenerationWorkspaceModel.actions.applyTemplateById(
        matchingGalleryTemplate.id,
        "gallery",
      );
    }

    if (presetState.showExport) {
      reportGenerationWorkspaceModel.actions.openExport();
    }

    if (presetState.contextMenuFieldLabel) {
      const matchingField = reportGenerationWorkspaceModel.availableFields.find(
        (field) => field.fieldLabel === presetState.contextMenuFieldLabel,
      );
      if (matchingField) {
        reportGenerationWorkspaceModel.actions.setContextMenuField(
          matchingField.fieldId,
        );
      }
    }
  }, [
    moduleConfig.label,
    reportGenerationWorkspaceModel.actions,
    reportGenerationWorkspaceModel.availableFields.length,
    reportGenerationWorkspaceModel.availableFields,
    reportGenerationWorkspaceModel.galleryTemplates.length,
    reportGenerationWorkspaceModel.galleryTemplates,
    reportGenerationWorkspaceModel.historyEntries.length,
    reportGenerationWorkspaceModel.historyEntries,
    reportGenerationWorkspaceModel.myTemplates.length,
    reportGenerationWorkspaceModel.myTemplates,
    reportProofPreset,
  ]);

  useEffect(() => {
    if (
      !selectedThreadsSessionId ||
      selectedThreadsSessionId === currentConversationSessionId
    ) {
      return;
    }
    if (conversationTimelinesBySessionId[selectedThreadsSessionId]) {
      setConversationTimelineMeta(selectedThreadsSessionId, "ready", null);
      return;
    }
    let cancelled = false;
    void loadConversationTimeline(selectedThreadsSessionId)
      .then((timeline) => {
        if (cancelled) {
          return;
        }
        if (!timeline) {
          return;
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn(
            "[AdmissionsAI] Failed to load selected assistant conversation timeline",
            error,
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    conversationTimelinesBySessionId,
    currentConversationSessionId,
    loadConversationTimeline,
    selectedThreadsSessionId,
    setConversationTimelineMeta,
  ]);

  useEffect(() => {
    if (!currentConversationSessionId || !conversationMessagesHydrated) {
      return;
    }
    const persistedMessageIds =
      persistedConversationMessageIdsRef.current[
        currentConversationSessionId
      ] ?? new Set<string>();
    persistedConversationMessageIdsRef.current[currentConversationSessionId] =
      persistedMessageIds;
    const pendingMessageIds =
      pendingConversationMessagePersistenceIdsRef.current[
        currentConversationSessionId
      ] ?? new Set<string>();
    pendingConversationMessagePersistenceIdsRef.current[
      currentConversationSessionId
    ] = pendingMessageIds;
    const pendingMessages = selectMessagesPendingConversationPersistence(
      conversation.messages,
      persistedMessageIds,
      pendingMessageIds,
    );
    if (pendingMessages.length === 0) {
      return;
    }

    let cancelled = false;
    const appendTargetSessionId = currentConversationSessionId;
    const markedPendingMessageIds = markMessagesPendingConversationPersistence(
      pendingMessages,
      pendingMessageIds,
    );
    void triggerAppendAssistantConversationMessages({
      input: {
        sessionId: appendTargetSessionId,
        messages: pendingMessages
          .filter(
            (
              message,
            ): message is AssistantMessage<
              AdmissionsCitation,
              AdmissionsAIContextualAction
            > & { messageId: string } => Boolean(message.messageId),
          )
          .map((message) => ({
            messageId: message.messageId,
            role: String(message.role),
            content: message.content,
            citations: (message.citations ?? undefined) as never,
            actions: (message.actions ?? undefined) as never,
            metadata: buildPersistableAssistantMessageMetadata(
              message,
            ) as never,
            source: (message.metadata as Record<string, unknown> | undefined)
              ?.taskThread
              ? "task-runtime"
              : "client",
            threadId:
              typeof (message.metadata as Record<string, unknown> | undefined)
                ?.taskThread === "object"
                ? String(
                    (
                      (message.metadata as Record<string, unknown>)
                        .taskThread as Record<string, unknown>
                    ).threadId ?? "",
                  ).trim() || undefined
                : undefined,
            runId:
              typeof (message.metadata as Record<string, unknown> | undefined)
                ?.taskThread === "object"
                ? String(
                    (
                      (message.metadata as Record<string, unknown>)
                        .taskThread as Record<string, unknown>
                    ).runId ?? "",
                  ).trim() || undefined
                : undefined,
            workItemId:
              typeof (message.metadata as Record<string, unknown> | undefined)
                ?.taskThread === "object"
                ? String(
                    (
                      (message.metadata as Record<string, unknown>)
                        .taskThread as Record<string, unknown>
                    ).workItemId ?? "",
                  ).trim() || undefined
                : undefined,
            createdAt: Math.floor(
              Number(message.timestamp ?? Date.now()) / 1000,
            ) as never,
          })),
      },
    })
      .then((payload) => {
        clearPendingConversationPersistenceMessageIds(
          pendingConversationMessagePersistenceIdsRef.current[
            appendTargetSessionId
          ],
          markedPendingMessageIds,
        );
        if (cancelled) {
          return;
        }
        const root = toRecord(payload);
        const session = toConversationSessionRecord(root?.session);
        const appendedMessages = Array.isArray(root?.appendedMessages)
          ? root.appendedMessages
              .map((message) => toConversationMessageRecord(message))
              .filter(
                (
                  message,
                ): message is IntellioAssistantConversationMessageRecord =>
                  Boolean(message),
              )
          : [];
        if (session) {
          setConversationSessions((current) =>
            upsertConversationSessionList(current, session),
          );
        }
        if (appendedMessages.length > 0) {
          const latestPersistedMessageIds =
            persistedConversationMessageIdsRef.current[
              appendTargetSessionId
            ] ?? persistedMessageIds;
          persistedConversationMessageIdsRef.current[appendTargetSessionId] =
            new Set([
              ...latestPersistedMessageIds,
              ...appendedMessages.map((message) => message.messageId),
            ]);
          setConversationTimelinesBySessionId((current) => {
            const existing = current[appendTargetSessionId];
            const nextMessages = [
              ...(existing?.messages ?? []),
              ...appendedMessages.filter(
                (message) =>
                  !(existing?.messages ?? []).some(
                    (entry) => entry.messageId === message.messageId,
                  ),
              ),
            ].sort((left, right) => left.sequence - right.sequence);
            return {
              ...current,
              [appendTargetSessionId]: {
                session: session ??
                  existing?.session ??
                  currentConversationSession ?? {
                    id: appendTargetSessionId,
                    actorId: assistantActorId ?? "",
                    actorRole: effectivePersonaRole,
                    tenantId: "",
                    tenantScopeStatus: "scoped",
                    status: "open",
                    module: capabilityModule,
                    route: pathname,
                    persona: effectivePersonaRole,
                    title: "Current session",
                    routeLabel: moduleConfig.label,
                    firstUserMessageId: null,
                    firstUserPreview: null,
                    lastMessageId: null,
                    lastMessagePreview: null,
                    lastAssistantMessageId: null,
                    lastAssistantPreview: null,
                    messageCount: existing?.messages.length ?? 0,
                    sourceAuthority: "persisted",
                    startedAt: Math.floor(Date.now() / 1000),
                    updatedAt: Math.floor(Date.now() / 1000),
                    closedAt: null,
                  },
                messages: nextMessages,
              },
            };
          });
          setConversationTimelineMeta(
            appendTargetSessionId,
            "ready",
            null,
          );
        } else {
          const latestPersistedMessageIds =
            persistedConversationMessageIdsRef.current[
              appendTargetSessionId
            ] ?? persistedMessageIds;
          persistedConversationMessageIdsRef.current[appendTargetSessionId] =
            new Set([
              ...latestPersistedMessageIds,
              ...markedPendingMessageIds,
            ]);
        }
      })
      .catch((error) => {
        clearPendingConversationPersistenceMessageIds(
          pendingConversationMessagePersistenceIdsRef.current[
            appendTargetSessionId
          ],
          markedPendingMessageIds,
        );
        if (!cancelled) {
          console.warn(
            "[AdmissionsAI] Failed to persist assistant conversation messages",
            error,
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    conversation.messages,
    conversationMessagesHydrated,
    assistantActorId,
    capabilityModule,
    currentConversationSession,
    currentConversationSessionId,
    effectivePersonaRole,
    moduleConfig.label,
    pathname,
    setConversationTimelineMeta,
    triggerAppendAssistantConversationMessages,
  ]);

  const buildAssistantMessageMetadata = useCallback(
    (
      message: AssistantMessage<
        AdmissionsCitation,
        AdmissionsAIContextualAction
      >,
    ): AdmissionsAssistantMessageMetadata => {
      const metadata = toFeedbackMetadata(message) ?? {};
      const presentation =
        message.presentation ??
        (metadata.presentation as
          | AdmissionsAssistantMessageMetadata["presentation"]
          | undefined);
      return {
        ...metadata,
        ...buildLiveAssistantResponseRenderMetadata(presentation),
        presentation,
        feedbackContext: buildAssistantFeedbackContext({
          assistantEpisodeId,
          pathname,
          routeMetadataToken: resolveRouteMetadataToken(pathname),
          persona: effectivePersonaRole,
          debug: null,
        }),
      } satisfies AdmissionsAssistantMessageMetadata;
    },
    [assistantEpisodeId, effectivePersonaRole, pathname],
  );

  const appendConversationMessage = useCallback(
    (
      message: AssistantMessage<
        AdmissionsCitation,
        AdmissionsAIContextualAction
      >,
    ) => {
      conversationTranscriptSessionIdRef.current =
        currentConversationSessionId ?? activeConversationSessionIdRef.current;
      const nextMessage =
        message.role === "assistant"
          ? {
              ...message,
              metadata: buildAssistantMessageMetadata(message),
            }
          : message;
      conversationMessagesRef.current = [
        ...conversationMessagesRef.current,
        nextMessage,
      ];
      conversation.appendMessage(nextMessage);
    },
    [buildAssistantMessageMetadata, conversation, currentConversationSessionId],
  );

  const executeReportGenerationCommand = useCallback(
    async (
      command: AdmissionsAIReportGenerationCommand,
      sourceLabel: string,
    ): Promise<boolean> => {
      if (!runReportsSidecarMode) {
        setLastActionFeedback(
          "Report actions are only available inside Run Reports.",
        );
        return false;
      }
      if (runReportsRouteLocalMode) {
        const activeRouteContext = activeRouteAssistantContext?.activeRouteContext;
        const policyHandler = activeRouteAssistantContext?.onAssistantPolicyEvent;
        const payload = toRunReportsDraftPayload(command);
        if (!activeRouteContext || !policyHandler || !payload) {
          setLastActionFeedback(
            "Run Reports could not apply that action because the route-local workspace context is unavailable.",
          );
          return false;
        }
        try {
          const result = await policyHandler({
            eventId: `ystnj-run-reports-command-${command.commandId}-${Date.now()}`,
            type: "route_local_action",
            policyEventType: "route_local_action",
            routeContextRef: activeRouteContext.contextRef,
            routeRevision: activeRouteContext.revision,
            routeFamily: activeRouteContext.routeFamily,
            pathname: activeRouteContext.pathname,
            capabilityId: null,
            actionId: `run-reports-command-${command.commandId}`,
            artifactActionId: null,
            sourceRef: null,
            payload: sanitizeAwsJsonObject(payload),
          });
          setLastActionFeedback(result.message);
          return result.accepted === true;
        } catch (error) {
          console.error("[AdmissionsAI] report-generation route-local command failed", {
            command,
            error,
          });
          setLastActionFeedback(
            `Unable to apply report action: ${sourceLabel}.`,
          );
          return false;
        }
      }
      const reportActions = reportGenerationWorkspaceModel.actions;
      const filterState =
        reportGenerationWorkspaceModel.filterState.filterState;
      const hasSelection =
        filterState.selectedEntities.length > 0 &&
        filterState.selectedFields.length > 0;
      const isBusy =
        reportGenerationWorkspaceModel.previewLoading ||
        reportGenerationWorkspaceModel.isSavingTemplate ||
        reportGenerationWorkspaceModel.isScheduling ||
        reportGenerationWorkspaceModel.isRunningReport;

      if (isBusy) {
        setLastActionFeedback(
          "Run Reports is still loading or processing another action.",
        );
        return false;
      }
      if (
        REPORT_GENERATION_SELECTION_REQUIRED_COMMANDS.has(
          command.commandId,
        ) &&
        !hasSelection
      ) {
        setLastActionFeedback(
          "Select at least one entity and one field before running this report action.",
        );
        return false;
      }
      if (
        (command.commandId === "run_export" ||
          command.commandId === "run_export_bundle") &&
        !reportGenerationWorkspaceModel.canPreview
      ) {
        setLastActionFeedback(
          "Run Reports cannot launch an export until the preview datasource plan is allowed.",
        );
        return false;
      }

      const findEntity = (entityId: string | null | undefined) =>
        entityId
          ? reportGenerationWorkspaceModel.modules
              .flatMap((module) => module.entities)
              .find((entity) => entity.id === entityId) ?? null
          : null;
      const findField = (fieldId: string | null | undefined) =>
        fieldId
          ? reportGenerationWorkspaceModel.availableFields.find(
              (field) => field.fieldId === fieldId,
            ) ?? null
          : null;
      const findTemplate = (
        templateId: string | null | undefined,
        source?: "gallery" | "custom" | null,
      ) => {
        if (!templateId) return null;
        if (source === "gallery") {
          return (
            reportGenerationWorkspaceModel.galleryTemplates.find(
              (template) => template.id === templateId,
            ) ?? null
          );
        }
        if (source === "custom") {
          return (
            reportGenerationWorkspaceModel.myTemplates.find(
              (template) => template.id === templateId,
            ) ?? null
          );
        }
        return (
          reportGenerationWorkspaceModel.galleryTemplates.find(
            (template) => template.id === templateId,
          ) ??
          reportGenerationWorkspaceModel.myTemplates.find(
            (template) => template.id === templateId,
          ) ??
          null
        );
      };

      try {
        switch (command.commandId) {
          case "set_title": {
            const title = String(command.title ?? "").trim();
            if (!title) {
              setLastActionFeedback(
                "The assistant did not provide a report title to apply.",
              );
              return false;
            }
            reportActions.setReportTitle(title);
            break;
          }
          case "apply_template": {
            const template = findTemplate(
              command.templateId,
              command.templateSource,
            );
            if (!template) {
              setLastActionFeedback(
                "The requested report template is not available in this workspace.",
              );
              return false;
            }
            reportActions.applyTemplateById(
              template.id,
              template.source === "custom" ? "custom" : "gallery",
            );
            break;
          }
          case "select_entity":
          case "deselect_entity": {
            const entity = findEntity(command.entityId);
            if (!entity || entity.permissionResolution.granted === false) {
              setLastActionFeedback(
                "The requested report entity is not available for this actor.",
              );
              return false;
            }
            const selected = filterState.selectedEntities.includes(entity.id);
            if (
              (command.commandId === "select_entity" && !selected) ||
              (command.commandId === "deselect_entity" && selected)
            ) {
              reportActions.toggleEntity(entity.id);
            }
            break;
          }
          case "select_field":
          case "deselect_field": {
            const field = findField(command.fieldId);
            if (!field) {
              setLastActionFeedback(
                "The requested report field is not available for this actor.",
              );
              return false;
            }
            const selected = filterState.selectedFields.includes(field.fieldId);
            if (
              (command.commandId === "select_field" && !selected) ||
              (command.commandId === "deselect_field" && selected)
            ) {
              reportActions.toggleField(field.fieldId);
            }
            break;
          }
          case "add_filter_condition": {
            const field = findField(
              command.fieldId ?? command.condition?.fieldId,
            );
            if (!field) {
              setLastActionFeedback(
                "The requested filter field is not available for this report.",
              );
              return false;
            }
            reportActions.addCondition(field);
            const conditionId = command.conditionId ?? `cond-${field.fieldId}`;
            const updates = {
              ...(command.condition?.operator
                ? { operator: command.condition.operator }
                : {}),
              ...(command.condition?.value !== undefined
                ? { value: String(command.condition.value ?? "") }
                : {}),
            };
            if (Object.keys(updates).length > 0) {
              reportActions.updateCondition(conditionId, updates);
            }
            break;
          }
          case "update_filter_condition": {
            const conditionId =
              command.conditionId ??
              (command.fieldId ? `cond-${command.fieldId}` : null);
            if (!conditionId) {
              setLastActionFeedback(
                "The assistant did not identify which filter to update.",
              );
              return false;
            }
            reportActions.updateCondition(
              conditionId,
              command.conditionUpdates ?? {},
            );
            break;
          }
          case "remove_filter_condition": {
            const conditionId =
              command.conditionId ??
              (command.fieldId ? `cond-${command.fieldId}` : null);
            if (!conditionId) {
              setLastActionFeedback(
                "The assistant did not identify which filter to remove.",
              );
              return false;
            }
            reportActions.removeCondition(conditionId);
            break;
          }
          case "update_date_range": {
            const updates: Partial<typeof filterState.dateRange> = {};
            if (command.dateRange?.preset !== undefined) {
              updates.preset = String(command.dateRange.preset ?? "Custom");
            }
            if (command.dateRange?.from !== undefined) {
              updates.from = String(command.dateRange.from ?? "");
            }
            if (command.dateRange?.to !== undefined) {
              updates.to = String(command.dateRange.to ?? "");
            }
            reportActions.updateDateRange(updates);
            break;
          }
          case "set_sort":
          case "toggle_sort": {
            const field = findField(command.fieldId ?? command.sort?.fieldId);
            if (!field) {
              setLastActionFeedback(
                "The requested sort field is not available for this report.",
              );
              return false;
            }
            if (command.commandId === "set_sort") {
              const currentSort = filterState.sorts[0] ?? null;
              const desiredDirection = command.sort?.direction ?? "asc";
              const currentDirection =
                currentSort?.fieldId === field.fieldId
                  ? currentSort.direction
                  : (currentSort?.direction ?? "asc");
              reportActions.setPrimarySortField(field);
              if (currentDirection !== desiredDirection) {
                reportActions.togglePrimarySortDirection();
              }
              break;
            }
            reportActions.setPrimarySortField(field);
            reportActions.togglePrimarySortDirection();
            break;
          }
          case "clear_report":
            reportActions.clearAll();
            break;
          case "start_new_report":
            reportActions.startNewReport();
            break;
          case "save_private_template":
            reportActions.savePrivateTemplate();
            break;
          case "save_shared_template":
            reportActions.saveSharedTemplate();
            break;
          case "toggle_daily_schedule":
            reportActions.toggleDailySchedule();
            break;
          case "run_export":
            reportActions.runExport(command.exportFormat ?? "xlsx");
            break;
          case "run_export_bundle":
            reportActions.runExportBundle();
            break;
          default:
            setLastActionFeedback(
              "The assistant returned a report action this client does not support.",
            );
            return false;
        }
        setLastActionFeedback(`Applied report action: ${sourceLabel}.`);
        return true;
      } catch (error) {
        console.error("[AdmissionsAI] report-generation command failed", {
          command,
          error,
        });
        setLastActionFeedback(
          `Unable to apply report action: ${sourceLabel}.`,
        );
        return false;
      }
    },
    [
      reportGenerationWorkspaceModel.actions,
      reportGenerationWorkspaceModel.availableFields,
      reportGenerationWorkspaceModel.canPreview,
      reportGenerationWorkspaceModel.filterState.filterState,
      reportGenerationWorkspaceModel.galleryTemplates,
      reportGenerationWorkspaceModel.isRunningReport,
      reportGenerationWorkspaceModel.isSavingTemplate,
      reportGenerationWorkspaceModel.isScheduling,
      reportGenerationWorkspaceModel.modules,
      reportGenerationWorkspaceModel.myTemplates,
      reportGenerationWorkspaceModel.previewLoading,
      activeRouteAssistantContext?.activeRouteContext,
      activeRouteAssistantContext?.onAssistantPolicyEvent,
      runReportsRouteLocalMode,
      runReportsSidecarMode,
    ],
  );

  const upsertTaskLaunchConversationTurn = useCallback(
    (input: {
      draft: TaskLaunchDraft;
      taskThread: AdmissionsAssistantTaskThreadStatus;
      assistantContent: string;
    }) => {
      const proposalMessageIds = buildTaskProposalMessageIds(input.draft.id);
      const nextUserMessage: AssistantMessage<
        AdmissionsCitation,
        AdmissionsAIContextualAction
      > = {
        role: "user",
        content: input.draft.question,
        messageId: proposalMessageIds.userMessageId,
        timestamp: input.draft.createdAt,
        metadata: {
          taskThread: input.taskThread,
        } satisfies AdmissionsAssistantMessageMetadata,
      };
      const taskLaunchAssistantResponse =
        normalizeUnifiedAssistantResponseEnvelope({
          answer: input.assistantContent,
          citations: [],
          actions: [],
          module: capabilityModule,
          debug: null,
          pathname,
          taskThread: input.taskThread,
        });
      const nextAssistantMessage: AssistantMessage<
        AdmissionsCitation,
        AdmissionsAIContextualAction
      > = {
        role: "assistant",
        content: taskLaunchAssistantResponse.answer,
        presentation: taskLaunchAssistantResponse.presentation,
        messageId: proposalMessageIds.assistantMessageId,
        timestamp: input.draft.createdAt + 1,
        metadata: {
          presentation: taskLaunchAssistantResponse.presentation,
          presentationDiagnostics:
            taskLaunchAssistantResponse.presentationDiagnostics,
          taskThread: input.taskThread,
        } satisfies AdmissionsAssistantMessageMetadata,
      };
      const currentMessages = conversationMessagesRef.current;
      let sawUserMessage = false;
      let sawAssistantMessage = false;
      let changed = false;
      const nextMessages = currentMessages.map((message) => {
        if (message.messageId === proposalMessageIds.userMessageId) {
          sawUserMessage = true;
          changed = true;
          return {
            ...message,
            content: input.draft.question,
            metadata: {
              ...((message.metadata as
                | AdmissionsAssistantMessageMetadata
                | undefined) ?? {}),
              taskThread: input.taskThread,
            } satisfies AdmissionsAssistantMessageMetadata,
          };
        }
        if (message.messageId === proposalMessageIds.assistantMessageId) {
          sawAssistantMessage = true;
          changed = true;
          return {
            ...message,
            content: taskLaunchAssistantResponse.answer,
            presentation: taskLaunchAssistantResponse.presentation,
            metadata: buildAssistantMessageMetadata({
              ...message,
              content: taskLaunchAssistantResponse.answer,
              presentation: taskLaunchAssistantResponse.presentation,
              metadata: {
                ...((message.metadata as
                  | AdmissionsAssistantMessageMetadata
                  | undefined) ?? {}),
                presentation: taskLaunchAssistantResponse.presentation,
                presentationDiagnostics:
                  taskLaunchAssistantResponse.presentationDiagnostics,
                taskThread: input.taskThread,
              } satisfies AdmissionsAssistantMessageMetadata,
            }),
          };
        }
        return message;
      });
      if (!sawUserMessage) {
        nextMessages.push(nextUserMessage);
        changed = true;
      }
      if (!sawAssistantMessage) {
        nextMessages.push({
          ...nextAssistantMessage,
          metadata: buildAssistantMessageMetadata(nextAssistantMessage),
        });
        changed = true;
      }
      if (!changed) {
        return;
      }
      replaceConversationMessagesForSession(
        conversationTranscriptSessionIdRef.current ??
          currentConversationSessionId ??
          activeConversationSessionIdRef.current,
        nextMessages,
      );
    },
    [
      buildAssistantMessageMetadata,
      capabilityModule,
      currentConversationSessionId,
      pathname,
      replaceConversationMessagesForSession,
    ],
  );

  const displayedConversationMessages = useMemo(() => {
    const resolvedMessages = conversation.messages.map((message) => {
      if (!message.messageId || message.role !== "assistant") {
        return message;
      }
      const existingMetadata = toFeedbackMetadata(message) ?? {};
      const baseMetadata = {
        ...existingMetadata,
        feedbackContext: buildAssistantFeedbackContext({
          assistantEpisodeId,
          pathname,
          routeMetadataToken: resolveRouteMetadataToken(pathname),
          persona: effectivePersonaRole,
          debug: null,
        }),
      } satisfies AdmissionsAssistantMessageMetadata;
      const feedbackSubmission =
        feedbackSubmissionByMessageId[message.messageId];
      if (!feedbackSubmission) {
        return {
          ...message,
          metadata: baseMetadata,
        };
      }
      return {
        ...message,
        feedback: feedbackSubmission.feedback ?? message.feedback,
        metadata: {
          ...baseMetadata,
          feedbackSubmission,
        } satisfies AdmissionsAssistantMessageMetadata,
      };
    });

    if (pendingTaskLaunch) {
      const proposalMessageIds = buildTaskProposalMessageIds(
        pendingTaskLaunch.id,
      );
      const taskThread = projectAssistantTaskThreadState({
        threadId: pendingTaskLaunch.id,
        workflowKey: pendingTaskLaunch.proposal.workflowKey,
        title: pendingTaskLaunch.proposal.title,
        state: "proposal",
        stateLabel: "Task proposal",
        statusMessage: pendingTaskLaunch.proposal.summary,
      });
      const taskProposal = {
        proposalId: pendingTaskLaunch.id,
        question: pendingTaskLaunch.question,
        title: pendingTaskLaunch.proposal.title,
        summary: pendingTaskLaunch.proposal.summary,
        workflowKey: pendingTaskLaunch.proposal.workflowKey,
        taskClass: pendingTaskLaunch.proposal.taskClass,
        recipientEmail: pendingTaskLaunch.proposal.recipientEmail,
        familyId: pendingTaskLaunch.proposal.familyId,
        academicYear: pendingTaskLaunch.proposal.academicYear,
        disabledReason: pendingTaskLaunch.proposal.disabledReason,
        isStarting: pendingTaskLaunch.id === startingTaskProposalId,
      };
      const pendingTaskLaunchResponse =
        normalizeUnifiedAssistantResponseEnvelope({
          answer: "I can run this as a background task while chat stays live.",
          citations: [],
          actions: [],
          module: capabilityModule,
          debug: null,
          pathname,
          taskThread,
          taskProposal,
        });
      const feedbackContext = buildAssistantFeedbackContext({
        assistantEpisodeId,
        pathname,
        routeMetadataToken: resolveRouteMetadataToken(pathname),
        persona: effectivePersonaRole,
        debug: null,
      });
      resolvedMessages.push({
        role: "user",
        content: pendingTaskLaunch.question,
        messageId: proposalMessageIds.userMessageId,
        timestamp: pendingTaskLaunch.createdAt,
      });
      resolvedMessages.push({
        role: "assistant",
        content: pendingTaskLaunchResponse.answer,
        messageId: proposalMessageIds.assistantMessageId,
        timestamp: pendingTaskLaunch.createdAt + 1,
        presentation: pendingTaskLaunchResponse.presentation,
        metadata: {
          feedbackContext,
          ...buildLiveAssistantResponseRenderMetadata(
            pendingTaskLaunchResponse.presentation,
          ),
          presentation: pendingTaskLaunchResponse.presentation,
          presentationDiagnostics:
            pendingTaskLaunchResponse.presentationDiagnostics,
          taskThread,
          taskProposal,
        } satisfies AdmissionsAssistantMessageMetadata,
      });
    }

    return collapseAssistantTaskThreadReplies(resolvedMessages);
  }, [
    assistantEpisodeId,
    conversation.messages,
    effectivePersonaRole,
    feedbackSubmissionByMessageId,
    pendingTaskLaunch,
    startingTaskProposalId,
    pathname,
  ]);

  useEffect(() => {
    if (!runReportsSidecarMode) {
      return;
    }
    for (const message of displayedConversationMessages) {
      if (message.role !== "assistant" || !Array.isArray(message.actions)) {
        continue;
      }
      for (const action of message.actions) {
        const command = resolveReportGenerationActionCommand(action);
        const payload = getActionPayload(action);
        const confidenceMode =
          action.confidenceMode ?? payload.confidenceMode ?? null;
        const actionCategory =
          action.actionCategory ?? payload.actionCategory ?? null;
        if (
          !command?.autoExecute ||
          action.type !== "execute" ||
          actionCategory !== "report_generation" ||
          confidenceMode !== "full"
        ) {
          continue;
        }
        const actionKey = [
          message.messageId ?? "message",
          action.id,
          command.commandId,
        ].join("::");
        if (processedReportGenerationActionIdsRef.current.has(actionKey)) {
          continue;
        }
        processedReportGenerationActionIdsRef.current.add(actionKey);
        void executeReportGenerationCommand(command, action.label);
      }
    }
  }, [
    displayedConversationMessages,
    executeReportGenerationCommand,
    runReportsSidecarMode,
  ]);

  useEffect(() => {
    setFeedbackMessages(displayedConversationMessages);
  }, [displayedConversationMessages, setFeedbackMessages]);

  const resolvedTaskLaunchDraftsById = useMemo(() => {
    const drafts = new Map<string, TaskLaunchDraft>();
    for (const message of displayedConversationMessages) {
      const proposal = (
        message.metadata as AdmissionsAssistantMessageMetadata | undefined
      )?.taskProposal;
      if (!proposal) {
        continue;
      }
      const draft = toTaskLaunchDraftFromProposalCard(proposal, {
        createdAt:
          typeof message.timestamp === "number" ? message.timestamp : null,
      });
      if (!draft) {
        continue;
      }
      drafts.set(draft.id, draft);
    }
    if (pendingTaskLaunch) {
      drafts.set(pendingTaskLaunch.id, pendingTaskLaunch);
    }
    return drafts;
  }, [displayedConversationMessages, pendingTaskLaunch]);

  const resolveTaskLaunchDraft = useCallback(
    (proposalId: string): TaskLaunchDraft | null => {
      return resolvedTaskLaunchDraftsById.get(proposalId) ?? null;
    },
    [resolvedTaskLaunchDraftsById],
  );

  const resolveTranscriptDisclosureOpen = useCallback(
    (disclosureKey: string) =>
      Boolean(transcriptDisclosureState[disclosureKey]),
    [transcriptDisclosureState],
  );

  const handleTranscriptDisclosureToggle = useCallback(
    (disclosureKey: string, open: boolean) => {
      setTranscriptDisclosureState((current) => {
        const currentlyOpen = Boolean(current[disclosureKey]);
        if (currentlyOpen === open) {
          return current;
        }
        if (open) {
          return {
            ...current,
            [disclosureKey]: true,
          };
        }
        const next = { ...current };
        delete next[disclosureKey];
        return next;
      });
    },
    [],
  );

  const scrollConversationAnchor = useCallback(
    (
      messageId?: string | null,
      intent: AssistantTranscriptScrollIntent = "explicit-jump",
    ) => {
      if (intent !== "explicit-jump") {
        return;
      }
      window.setTimeout(() => {
        const selector = messageId
          ? `[data-message-id="${messageId}"]`
          : '[data-testid="ai-session-block-current"]';
        const target = document.querySelector(selector) as HTMLElement | null;
        target?.scrollIntoView({ block: "start", behavior: "smooth" });
      }, 40);
    },
    [],
  );

  const scrollAssistantTranscriptToBottom = useCallback(
    (intent: AssistantTranscriptScrollIntent = "append-follow-down") => {
      if (intent !== "append-follow-down") {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }
      const followTranscriptToBottom = () => {
        const chatArea = document.querySelector(
          '[data-testid="ai-chat-area"]',
        ) as HTMLElement | null;
        chatArea?.scrollTo({
          top: chatArea.scrollHeight,
          behavior: "auto",
        });
      };
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(followTranscriptToBottom);
      }
      followTranscriptToBottom();
      window.setTimeout(followTranscriptToBottom, 40);
      window.setTimeout(followTranscriptToBottom, 120);
      window.setTimeout(followTranscriptToBottom, 360);
      window.setTimeout(followTranscriptToBottom, 900);
    },
    [],
  );

  useEffect(() => {
    const pendingAnchor = pendingAnchorRef.current;
    if (
      !conversationMessagesHydrated ||
      !pendingAnchor ||
      pendingAnchor.sessionId !== currentConversationSessionId
    ) {
      return;
    }
    scrollConversationAnchor(pendingAnchor.messageId, pendingAnchor.intent);
    pendingAnchorRef.current = null;
  }, [
    conversationMessagesHydrated,
    currentConversationSessionId,
    displayedConversationMessages,
    scrollConversationAnchor,
  ]);

  const buildTaskLaunchContext = useCallback(
    (draft: TaskLaunchDraft): AdmissionsAIContext => {
      const spreadsheetSession =
        activeSpreadsheetWorkspace ?? currentSpreadsheetSession;
      const spreadsheetView =
        buildSpreadsheetViewContextFromSession(spreadsheetSession);
      const executionPathname =
        draft.explicitPromptBinding?.executionPathname ?? pathname;
      const platformModule = resolveAssistantPlatformCompatibleModule(
        draft.explicitPromptBinding?.module ??
          resolveAIAssistantModule(executionPathname),
      );
      return {
        ...(context ?? {}),
        personaRole: context?.personaRole ?? effectivePersonaRole,
        pathname: executionPathname,
        originPathname:
          draft.explicitPromptBinding?.originPathname ??
          (executionPathname !== pathname ? pathname : undefined),
        executionPathname:
          executionPathname !== pathname ? executionPathname : undefined,
        catalogScope: draft.explicitPromptBinding?.catalogScope,
        routeMetadataToken: resolveRouteMetadataToken(executionPathname),
        module: platformModule,
        capabilityModule: platformModule,
        spreadsheetView,
        assistantTask: {
          workflowKey: draft.proposal.workflowKey,
          taskClass: draft.proposal.taskClass,
          title: draft.proposal.title,
          summary: draft.proposal.summary,
          recipientEmail: draft.proposal.recipientEmail ?? null,
          familyId: draft.proposal.familyId ?? null,
          academicYear: draft.proposal.academicYear ?? null,
          workbookArtifactPath:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetSession?.workbookArtifact.sourceRef ?? null)
              : null,
          workbookTitle:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetSession?.workbookArtifact.label ?? null)
              : null,
          workbookSheetId:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetView?.activeSheetId ?? null)
              : null,
          workbookSheetName:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetView?.activeSheetName ?? null)
              : null,
          workbookVisibleRowCount:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetView?.visibleRowCount ?? null)
              : null,
          workbookTotalRowCount:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetView?.totalRowCount ?? null)
              : null,
          workbookActiveFilters:
            draft.proposal.workflowKey === "artifact.spreadsheet.modify"
              ? (spreadsheetView?.activeFilters ?? []).map(
                  (entry) => `${entry.label}: ${entry.valueLabel}`,
                )
              : [],
          launchSource: "prompt",
        },
      } as AdmissionsAIContext;
    },
    [
      activeSpreadsheetWorkspace,
      capabilityModule,
      context,
      currentSpreadsheetSession,
      effectivePersonaRole,
      pathname,
    ],
  );

  const launchBackgroundTask = useCallback(
    async (draft: TaskLaunchDraft) => {
      const draftExecutionPathname =
        draft.explicitPromptBinding?.executionPathname ?? pathname;
      const draftExecutionModule = resolveAssistantPlatformCompatibleModule(
        draft.explicitPromptBinding?.module ??
          resolveAIAssistantModule(draftExecutionPathname),
      );
      const clearStartingState = () => {
        setStartingTaskProposalId((current) =>
          current === draft.id ? null : current,
        );
      };
      const proposalMessageIds = buildTaskProposalMessageIds(draft.id);
      const queuedTaskThread = projectAssistantTaskThreadState({
        threadId: draft.id,
        workflowKey: draft.proposal.workflowKey,
        title: draft.proposal.title,
        state: "started",
        stateLabel: "Queued",
        statusMessage: "Task queued. Starting the background run.",
        progressPercent: 3,
      });
      livePromptHydrationProtectionUntilRef.current =
        Date.now() + LIVE_PROMPT_HYDRATION_PROTECTION_MS;
      markConversationSessionMutation(currentConversationSessionId);
      setAssistantSurfaceView("chat");
      setPendingTaskLaunch((current) =>
        current?.id === draft.id ? null : current,
      );
      upsertTaskLaunchConversationTurn({
        draft,
        taskThread: queuedTaskThread,
        assistantContent: `${draft.proposal.title} queued. I will keep it running in the background while you keep chatting.`,
      });
      announcedTaskProgressSignaturesRef.current[draft.id] = [
        queuedTaskThread.state,
        queuedTaskThread.stateLabel,
        queuedTaskThread.statusMessage ?? "",
        0,
      ].join("::");
      scrollAssistantTranscriptToBottom("append-follow-down");

      const failTaskLaunch = (input: {
        statusMessage: string;
        failureClass?:
          | "execution-context"
          | "policy-contract"
          | "domain-readiness"
          | "evidence-coherence"
          | "security"
          | "lineage"
          | null;
        reasonCode?: string | null;
        failureDetail?: string | null;
      }) => {
        const failedTaskThread = projectAssistantTaskThreadState({
          threadId: draft.id,
          workflowKey: draft.proposal.workflowKey,
          title: draft.proposal.title,
          state: "failed",
          stateLabel: "Start failed",
          statusMessage: input.statusMessage,
          failureClass: input.failureClass ?? "domain-readiness",
          reasonCode: input.reasonCode ?? null,
          failureDetail: input.failureDetail ?? null,
          progressPercent: 0,
        });
        announcedTaskProgressSignaturesRef.current[draft.id] = [
          failedTaskThread.state,
          failedTaskThread.stateLabel,
          failedTaskThread.statusMessage ?? "",
          0,
        ].join("::");
        upsertTaskLaunchConversationTurn({
          draft,
          taskThread: failedTaskThread,
          assistantContent: `${draft.proposal.title} could not start. ${input.statusMessage}`,
        });
        clearStartingState();
        setLastActionFeedback(input.statusMessage);
        scrollAssistantTranscriptToBottom("append-follow-down");
      };

      try {
        const sessionId =
          await resolveAssistantTaskLaunchOriginSessionIdForStart({
            draftId: draft.id,
            currentConversationSessionId,
            activeConversationSessionId: activeConversationSessionIdRef.current,
            conversationSessions,
            selectedThreadsSessionId,
            pathname: draftExecutionPathname,
            moduleId: draftExecutionModule,
            pendingSessionPromise:
              startFreshConversationSessionPromiseRef.current,
            startFreshConversationSession:
              assistantConversationSessionMode !== "unsupported" &&
              assistantActorId
                ? () => startFreshConversationSession(false)
                : null,
          });
        markConversationSessionMutation(sessionId);
        const timestamp = Date.now();
        const userMessageId = proposalMessageIds.userMessageId;
        const taskContext = sanitizeAwsJsonObject(
          buildTaskLaunchContext(draft),
        );
        const runtimePreferences: AssistantRuntimePreferences = {
          preferredRuntime: resolveAssistantPreferredRuntime(
            "auto",
            draft.question,
          ),
          browser: browserCapabilities,
        };
        const payload = await triggerMutateAssistantRun({
          input: {
            action: "start",
            question: draft.question,
            conversationHistory: sanitizeAwsJsonArray(
              normalizeAssistantConversationHistoryForRequest(
                conversation.messages.map((message) => ({
                  role: message.role,
                  content: message.content,
                })),
              ),
            ),
            context: taskContext,
            model: modelKey,
            runtimePreferences: sanitizeAwsJsonObject(runtimePreferences),
            routingHints: sanitizeAwsJsonObject({
              pathnameHint: draftExecutionPathname,
              moduleHint: draftExecutionModule,
              intentHint: draft.proposal.workflowKey,
            }),
            origin: {
              sessionId,
              threadId: draft.id,
              userMessageId,
            },
          },
        });
        const accepted = Boolean(
          (payload as { accepted?: boolean } | null)?.accepted,
        );
        if (!accepted) {
          failTaskLaunch({
            statusMessage: "Background task was not accepted.",
            failureClass: "domain-readiness",
            failureDetail: "mutateAssistantRun(start) returned accepted=false.",
          });
          return;
        }
        const threadId = draft.id;
        const run =
          (
            payload as {
              run?: {
                id?: string;
                workItemId?: string | null;
                workflowKey?: string | null;
              } | null;
            } | null
          )?.run ?? null;
        const runId = typeof run?.id === "string" ? run.id : null;
        const runWorkItemId =
          typeof run?.workItemId === "string" &&
          run.workItemId.trim().length > 0
            ? run.workItemId
            : null;
        const runWorkflowKey =
          typeof run?.workflowKey === "string" &&
          run.workflowKey.trim().length > 0
            ? run.workflowKey
            : null;
        if (runId) {
          const optimisticSeed: AssistantOptimisticTaskSeed = {
            id: runWorkItemId ?? `run:${runId}`,
            runId,
            workflowKey: runWorkflowKey ?? draft.proposal.workflowKey,
            taskClass: draft.proposal.taskClass,
            title: draft.proposal.title,
            route: draftExecutionPathname,
            launchedAt: timestamp,
          };
          setOptimisticTaskSeeds((current) => {
            const next = current.filter(
              (entry) =>
                entry.runId !== optimisticSeed.runId &&
                entry.id !== optimisticSeed.id,
            );
            next.unshift(optimisticSeed);
            return next.slice(0, 16);
          });
        }
        persistConversationTaskLaunches(
          upsertAssistantConversationTaskLaunch(
            conversationTaskLaunchesRef.current,
            {
              threadId,
              runId,
              workItemId: runWorkItemId,
              workflowKey: runWorkflowKey ?? draft.proposal.workflowKey,
              title: draft.proposal.title,
              route: draftExecutionPathname,
              launchedAt: timestamp,
            },
            timestamp,
          ),
        );
        if (runId) {
          launchedConversationTaskRunIdsRef.current.add(runId);
        }
        if (runWorkItemId) {
          launchedConversationTaskIdsRef.current.add(runWorkItemId);
        }
        const taskThread = projectAssistantTaskThreadState({
          threadId,
          runId,
          workItemId: runWorkItemId,
          workflowKey: runWorkflowKey ?? draft.proposal.workflowKey,
          title: draft.proposal.title,
          state: "started",
          stateLabel: "Queued",
          statusMessage: "Task started. Waiting for progress updates.",
          progressPercent: 8,
        });
        announcedTaskProgressSignaturesRef.current[threadId] = [
          taskThread.state,
          taskThread.stateLabel,
          taskThread.statusMessage ?? "",
          0,
        ].join("::");
        upsertTaskLaunchConversationTurn({
          draft,
          taskThread,
          assistantContent: `${draft.proposal.title} started. I will keep it running in the background while you keep chatting.`,
        });
        clearStartingState();
        setLastActionFeedback(
          `${draft.proposal.title} started in the background.`,
        );
        scrollAssistantTranscriptToBottom("append-follow-down");
      } catch (error) {
        console.error("[AdmissionsAI] Failed to launch background task", error);
        const failureDetail =
          error instanceof Error ? error.message : String(error);
        const classifiedFailure = classifyAssistantTaskFailure(failureDetail);
        failTaskLaunch({
          statusMessage:
            classifiedFailure?.statusMessage ??
            "Unable to start the background task right now.",
          failureClass: classifiedFailure?.failureClass ?? "domain-readiness",
          reasonCode: classifiedFailure?.reasonCode ?? null,
          failureDetail,
        });
      }
    },
    [
      assistantActorId,
      assistantConversationSessionMode,
      browserCapabilities,
      buildTaskLaunchContext,
      capabilityModule,
      conversation.messages,
      conversationSessions,
      currentConversationSessionId,
      markConversationSessionMutation,
      modelKey,
      pathname,
      persistConversationTaskLaunches,
      scrollAssistantTranscriptToBottom,
      selectedThreadsSessionId,
      startFreshConversationSession,
      triggerMutateAssistantRun,
      upsertTaskLaunchConversationTurn,
    ],
  );

  const submitAssistantQuestionDirect = useCallback(
    (
      question: string,
      explicitPromptBinding?: ExplicitPromptBinding,
    ): boolean => {
      pendingExplicitPromptBindingRef.current = explicitPromptBinding ?? null;
      setAssistantSurfaceView("chat");
      const previousPendingQuestion = pendingQuestionRef.current;
      const previousConversationIsLoading = conversationIsLoadingRef.current;
      const trimmedQuestion = question.trim();
      if (trimmedQuestion) {
        pendingQuestionRef.current = trimmedQuestion;
        conversationIsLoadingRef.current = true;
        if (explicitPromptBinding) {
          pendingExplicitPromptBindingByQuestionRef.current.set(
            trimmedQuestion,
            explicitPromptBinding,
          );
        } else {
          pendingExplicitPromptBindingByQuestionRef.current.delete(
            trimmedQuestion,
          );
        }
      }
      conversationTranscriptSessionIdRef.current =
        currentConversationSessionId ?? activeConversationSessionIdRef.current;
      const didSubmit = conversation.submitQuestion(question);
      if (!didSubmit) {
        pendingQuestionRef.current = previousPendingQuestion;
        conversationIsLoadingRef.current = previousConversationIsLoading;
        pendingExplicitPromptBindingRef.current = null;
        if (trimmedQuestion) {
          pendingExplicitPromptBindingByQuestionRef.current.delete(
            trimmedQuestion,
          );
        }
        return false;
      }
      livePromptHydrationProtectionUntilRef.current =
        Date.now() + LIVE_PROMPT_HYDRATION_PROTECTION_MS;
      markConversationSessionMutation(currentConversationSessionId);
      if (
        didSubmit &&
        assistantConversationSessionMode !== "unsupported" &&
        assistantActorId &&
        !currentConversationSessionId
      ) {
        void startFreshConversationSession(false).catch((error) => {
          console.warn(
            "[AdmissionsAI] Failed to start assistant conversation session for live prompt",
            error,
          );
          setLastActionFeedback(
            "Unable to start the assistant session right now.",
          );
        });
      }
      return didSubmit;
    },
    [
      assistantActorId,
      assistantConversationSessionMode,
      conversation,
      currentConversationSessionId,
      markConversationSessionMutation,
      startFreshConversationSession,
    ],
  );

  const submitAssistantQuestion = useCallback(
    (
      question: string,
      explicitPromptBinding?: ExplicitPromptBinding,
    ): boolean => {
      const executionPathname =
        explicitPromptBinding?.executionPathname ?? pathname;
      const draft = detectAssistantTaskIntent({
        question,
        pathname: executionPathname,
        context,
        spreadsheetSession: currentSpreadsheetSession?.workbookArtifact
          .sourceRef
          ? {
              workbookArtifactPath:
                currentSpreadsheetSession.workbookArtifact.sourceRef,
              title: currentSpreadsheetSession.workbookArtifact.label,
              activeSheetName:
                currentSpreadsheetSession.view?.activeSheetName ?? null,
              visibleRowCount:
                currentSpreadsheetSession.view?.visibleRowCount ?? null,
              totalRowCount:
                currentSpreadsheetSession.view?.totalRowCount ?? null,
              activeFilters: (
                currentSpreadsheetSession.view?.activeFilters ?? []
              ).map((entry) => `${entry.label}: ${entry.valueLabel}`),
            }
          : null,
      });
      if (draft) {
        pendingExplicitPromptBindingRef.current = null;
        const createdAt = Date.now();
        const taskLaunchDraft: TaskLaunchDraft = {
          id: `task-proposal-${createdAt}`,
          createdAt,
          question,
          proposal: draft,
          explicitPromptBinding: explicitPromptBinding ?? null,
        };
        setAssistantSurfaceView("chat");
        if (panelState.isOverlay && !panelState.isExpanded) {
          panelState.setExpanded(true);
        }
        if (draft.autoStart && !draft.disabledReason) {
          void launchBackgroundTask(taskLaunchDraft);
          return true;
        }
        setPendingTaskLaunch(taskLaunchDraft);
        if (
          assistantConversationSessionMode !== "unsupported" &&
          assistantActorId &&
          !currentConversationSessionId
        ) {
          void startFreshConversationSession(false).catch((error) => {
            console.warn(
              "[AdmissionsAI] Failed to warm assistant session for task proposal",
              error,
            );
          });
        }
        return true;
      }
      return submitAssistantQuestionDirect(question, explicitPromptBinding);
    },
    [
      assistantActorId,
      assistantConversationSessionMode,
      context,
      currentConversationSessionId,
      currentSpreadsheetSession,
      launchBackgroundTask,
      pathname,
      panelState,
      startFreshConversationSession,
      submitAssistantQuestionDirect,
    ],
  );

  const dismissPendingTaskLaunch = useCallback((proposalId: string) => {
    setStartingTaskProposalId((current) =>
      current === proposalId ? null : current,
    );
    setPendingTaskLaunch((current) =>
      current?.id === proposalId ? null : current,
    );
  }, []);

  const askPendingTaskLaunchInChat = useCallback(
    (proposalId: string) => {
      const resolvedDraft = resolveTaskLaunchDraft(proposalId);
      if (!resolvedDraft) {
        setLastActionFeedback(
          "This task proposal is no longer available. Submit it again.",
        );
        return;
      }
      const deferredQuestion = resolvedDraft.question;
      const explicitPromptBinding =
        resolvedDraft.explicitPromptBinding ?? undefined;
      setStartingTaskProposalId((current) =>
        current === proposalId ? null : current,
      );
      setPendingTaskLaunch((current) =>
        current?.id === proposalId ? null : current,
      );
      submitAssistantQuestionDirect(deferredQuestion, explicitPromptBinding);
    },
    [resolveTaskLaunchDraft, submitAssistantQuestionDirect],
  );

  const startPendingTaskLaunch = useCallback(
    (proposalId: string) => {
      if (startingTaskProposalId === proposalId) {
        return;
      }
      const resolvedDraft = resolveTaskLaunchDraft(proposalId);
      if (!resolvedDraft) {
        setLastActionFeedback(
          "This task proposal is no longer available. Submit it again.",
        );
        return;
      }
      setStartingTaskProposalId(proposalId);
      void launchBackgroundTask(resolvedDraft);
    },
    [launchBackgroundTask, resolveTaskLaunchDraft, startingTaskProposalId],
  );

  const resetAssistantConversation = useCallback(() => {
    const previousSessionId =
      currentConversationSessionId ?? activeConversationSessionIdRef.current;
    conversationResetEpochRef.current += 1;
    pendingFreshConversationSessionGuardRef.current = {
      epoch: conversationResetEpochRef.current,
      pendingFreshSessionId: null,
    };
    if (previousSessionId) {
      markConversationSessionMutation(previousSessionId);
    }
    resetSessionScopedUiState({
      clearWorkspaceTaskLane: true,
    });
    conversation.resetConversation();
    pendingAnchorRef.current = null;
    replaceConversationMessagesForSession(null, []);
    setCurrentConversationSessionId(null);
    setSelectedThreadsSessionId(null);
    setConversationMessagesHydrated(true);
    void startFreshConversationSession(true).catch((error) => {
      console.warn(
        "[AdmissionsAI] Failed to reset assistant conversation session",
        error,
      );
      setLastActionFeedback(
        "Unable to start a fresh assistant session right now.",
      );
      setConversationMessagesHydrated(true);
    });
  }, [
    conversation,
    currentConversationSessionId,
    markConversationSessionMutation,
    replaceConversationMessagesForSession,
    resetSessionScopedUiState,
    startFreshConversationSession,
  ]);

  const assistantConversation = useMemo(
    () => ({
      ...conversation,
      messages:
        assistantResponseFixtureState?.messages ??
        displayedConversationMessages,
      pendingQuestion:
        assistantResponseFixtureState?.pendingQuestion ??
        conversation.pendingQuestion,
      errorMessage:
        assistantResponseFixtureState?.conversationErrorMessage ??
        conversation.errorMessage,
      isLoading:
        assistantResponseFixtureState?.isLoading ?? conversation.isLoading,
      resetConversation: resetAssistantConversation,
      submitQuestion: submitAssistantQuestion,
    }),
    [
      assistantResponseFixtureState?.messages,
      assistantResponseFixtureState?.conversationErrorMessage,
      assistantResponseFixtureState?.isLoading,
      assistantResponseFixtureState?.pendingQuestion,
      conversation,
      displayedConversationMessages,
      resetAssistantConversation,
      submitAssistantQuestion,
    ],
  );
  const assistantToolRegistry = useMemo(
    () =>
      createAssistantToolRegistry({
        allowReportWorkspace: assistantReportGenerationProofMode,
        pathname,
        moduleLabel: moduleConfig.label,
        openReportWorkspace: () => openReportWorkspace("templates"),
        submitQuestion: (question) => submitAssistantQuestion(question),
        appendAssistantMessage: appendConversationMessage,
      }),
    [
      appendConversationMessage,
      assistantReportGenerationProofMode,
      moduleConfig.label,
      openReportWorkspace,
      pathname,
      submitAssistantQuestion,
    ],
  );

  const clearActiveProactivePrompt = useCallback(() => {
    if (!activeProactivePromptEntry) {
      return;
    }
    void proactiveTray
      .executeOptimisticHide(activeProactivePromptEntry.id, async () => {
        await triggerMutateAssistantWorkItems({
          input: {
            action: "dismiss",
            ids: [activeProactivePromptEntry.id],
          },
        });
      })
      .catch((error) => {
        console.warn(
          "[AdmissionsAI] Failed to dismiss proactive prompt",
          error,
        );
        setLastActionFeedback(
          "Unable to dismiss this proactive prompt right now.",
        );
      });
  }, [
    activeProactivePromptEntry,
    proactiveTray,
    triggerMutateAssistantWorkItems,
  ]);

  const submitProactivePrompt = useCallback(
    (input: {
      seedQuestion?: string;
      targetRoute?: string;
      explicitPromptBinding?: ExplicitPromptBinding;
    }) => {
      if (panelState.isOverlay && !panelState.isExpanded) {
        panelState.setExpanded(true);
      }
      const seedQuestion = String(
        input.seedQuestion ??
          activeProactivePrompt?.seedQuestion ??
          activeProactivePrompt?.summary ??
          "",
      ).trim();
      const targetRoute = String(input.targetRoute ?? "").trim();
      const explicitPromptBinding =
        input.explicitPromptBinding ??
        buildExplicitPromptBindingFromAssistantPrompt(
          activeProactivePrompt ?? null,
        );
      const didSubmit =
        seedQuestion.length > 0
          ? submitAssistantQuestion(seedQuestion, explicitPromptBinding)
          : false;

      if (targetRoute && typeof window !== "undefined") {
        window.location.assign(targetRoute);
      }

      if (didSubmit || targetRoute || seedQuestion.length === 0) {
        if (activeProactivePromptEntry) {
          void proactiveTray
            .executeOptimisticHide(activeProactivePromptEntry.id, async () => {
              await triggerMutateAssistantWorkItems({
                input: {
                  action: "open",
                  ids: [activeProactivePromptEntry.id],
                },
              });
            })
            .catch((error) => {
              console.warn(
                "[AdmissionsAI] Failed to mark proactive prompt opened",
                error,
              );
            });
        }
        return;
      }

      setLastActionFeedback(
        "Assistant is still processing. Proactive prompt remains queued.",
      );
    },
    [
      activeProactivePrompt?.promptBinding,
      activeProactivePrompt?.seedQuestion,
      activeProactivePrompt?.summary,
      activeProactivePromptEntry,
      panelState,
      proactiveTray,
      submitAssistantQuestion,
      triggerMutateAssistantWorkItems,
    ],
  );

  const handleProactiveSuggestedAction = useCallback(
    (action: {
      label?: string;
      prompt?: string | null;
      targetRoute?: string | null;
      capabilityId?: string | null;
      requiresConfirmation?: boolean;
    }) => {
      const label = String(action.label ?? "").trim() || "this action";
      if (action.requiresConfirmation && typeof window !== "undefined") {
        const confirmed = window.confirm(`Run "${label}" now?`);
        if (!confirmed) {
          return;
        }
      }
      submitProactivePrompt({
        seedQuestion: action.prompt ?? undefined,
        targetRoute: action.targetRoute ?? undefined,
        explicitPromptBinding: buildExplicitPromptBindingFromPromptAction(
          action,
          activeProactivePrompt ?? null,
        ),
      });
    },
    [activeProactivePrompt, submitProactivePrompt],
  );

  const handleAttachFile = useCallback(async (file: File) => {
    const fileName = String(file?.name ?? "").trim();
    if (!fileName) {
      return;
    }
    const fileKb = Math.max(1, Math.round((Number(file.size) || 0) / 1024));
    setLastActionFeedback(`Attached ${fileName} (${fileKb} KB).`);
  }, []);

  const modelSelectorNode = (
    <AIAssistantModelSelector
      options={MODEL_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      }))}
      value={modelKey}
      onChange={handleModelSelection}
      disabled={conversation.isLoading}
    />
  );

  const personaLabel =
    effectivePersonaRole.toLowerCase() === "parent"
      ? "Applicant Help Mode"
      : "Admin Support Mode";
  const useSchoolShellDesign = Boolean(
    forceSchoolShellDesign ??
      (layout === "sidebar" && isSchoolShellPath(pathname)),
  );
  const didNormalizeModelSelection = useRef(false);
  useEffect(() => {
    if (didNormalizeModelSelection.current) {
      return;
    }
    didNormalizeModelSelection.current = true;
    const canonicalDefault = MODEL_OPTIONS.some(
      (option) => option.value === ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY,
    )
      ? ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY
      : (MODEL_OPTIONS[0]?.value ?? ADMISSIONS_BEDROCK_DEFAULT_MODEL_KEY);
    const modelIsSelectable = MODEL_OPTIONS.some(
      (option) => option.value === modelKey,
    );
    if (!modelIsSelectable && modelKey !== canonicalDefault) {
      setModelKey(canonicalDefault);
    }
  }, [modelKey, setModelKey]);
  const headerSubtitle =
    moduleConfig.id === "general"
      ? personaLabel
      : `${personaLabel} - ${moduleConfig.label}`;
  const personaDisplay = useMemo(() => {
    const role = effectivePersonaRole;
    if (!role) {
      return "Staff";
    }
    return `${role.charAt(0).toUpperCase()}${role.slice(1)}`;
  }, [effectivePersonaRole]);

  const dataSourcesLabel = useMemo(
    () => buildDataSourcesLabel(moduleConfig, context),
    [moduleConfig, context],
  );
  const greeting = useMemo(
    () =>
      moduleConfig.getGreeting({
        ...context,
        personaRole: effectivePersonaRole,
      }),
    [context, effectivePersonaRole, moduleConfig],
  );
  const welcomeHeading = useMemo(() => {
    const firstName = resolveCognitoProfileFirstName(resolveCognitoProfile());
    return firstName ? `Welcome, ${firstName}!` : "Welcome!";
  }, [assistantActorId]);
  const inputPlaceholder = useMemo(
    () => moduleConfig.getInputPlaceholder(),
    [moduleConfig],
  );
  const inputActions = useMemo(
    () => [
      ...assistantInputActions,
      ...assistantToolRegistry.map((tool) => tool.inputAction),
      ...(showAmbientSettingsSpecialist
        ? (settingsSpecialistSnapshot?.inputActions ?? [])
        : []),
    ],
    [
      assistantToolRegistry,
      settingsSpecialistSnapshot,
      showAmbientSettingsSpecialist,
    ],
  );
  const settingsPresetPromptById = useMemo<
    Map<
      string,
      {
        id: string;
        prompt: string;
      }
    >
  >(
    () =>
      new Map<
        string,
        {
          id: string;
          prompt: string;
        }
      >(
        (settingsSpecialistSnapshot?.presetCases ?? []).map(
          (entry) => [`settings-specialist-${entry.id}`, entry] as const,
        ),
      ),
    [settingsSpecialistSnapshot],
  );

  const appendExecutionMessage = useCallback(
    (input: { message: string; navigateTo?: string | null }) => {
      setLastActionFeedback(
        input.navigateTo
          ? `${input.message} Route: ${input.navigateTo}`
          : input.message,
      );
    },
    [],
  );

  const openCapabilityActionPage = useCallback(
    (input: {
      capability: AssistantCapability;
      preview: AssistantActionPreviewResponse;
    }) => {
      const missingInputs = Array.isArray(input.preview.missingInputs)
        ? input.preview.missingInputs
        : [];
      const targetRoute =
        input.preview.targetRoute || input.capability.targetRoute;
      appendExecutionMessage({
        message:
          missingInputs.length > 0
            ? `Open the settings page to provide: ${missingInputs.join(", ")}.`
            : input.preview.message || `Open ${input.capability.title}.`,
        navigateTo: targetRoute,
      });
      if (targetRoute && typeof window !== "undefined") {
        window.location.assign(targetRoute);
      }
    },
    [appendExecutionMessage],
  );

  const buildCapabilityPromptBinding = useCallback(
    (capability: AssistantCapability): ExplicitPromptBinding => ({
      module: capability.module,
      activeCapabilityId: capability.id,
      activeScenarioKey: capability.scenarioKey ?? undefined,
      activeCapabilityMethod: capability.method ?? undefined,
      executionPathname: resolveAssistantLaunchTargetPathname({
        targetRoute: capability.targetRoute,
        routePrefixes: capability.routePrefixes,
        fallbackPathname: pathname,
      }),
      originPathname: pathname,
      catalogScope: assistantCatalogScope,
    }),
    [assistantCatalogScope, pathname],
  );

  const buildToolPromptBinding = useCallback(
    (tool: AssistantTool, capability?: AssistantCapability): ExplicitPromptBinding | undefined => {
      if (!tool.capabilityId && !tool.targetRoute) {
        return undefined;
      }
      return {
        module: capability?.module ?? tool.module,
        activeCapabilityId: tool.capabilityId ?? undefined,
        activeScenarioKey: tool.scenarioKey ?? undefined,
        activeCapabilityMethod: tool.method ?? undefined,
        executionPathname: resolveAssistantLaunchTargetPathname({
          targetRoute: tool.targetRoute,
          routePrefixes: tool.routePrefixes,
          fallbackPathname: pathname,
        }),
        originPathname: pathname,
        catalogScope: assistantCatalogScope,
      };
    },
    [assistantCatalogScope, pathname],
  );

  const runCapabilityAction = useCallback(
    async (capability: AssistantCapability) => {
      const executionPathname = resolveAssistantLaunchTargetPathname({
        targetRoute: capability.targetRoute,
        routePrefixes: capability.routePrefixes,
        fallbackPathname: pathname,
      });
      setLastActionFeedback(`Running ${capability.title}.`);
      try {
        const preview = await loadCapabilityBrowserPreview(capability);
        if (!preview) {
          appendExecutionMessage({
            message: `Unable to run action "${capability.title}" right now.`,
            navigateTo: capability.targetRoute,
          });
          return;
        }

        if (!preview.allowed) {
          const missingInputs =
            Array.isArray(preview.missingInputs) &&
            preview.missingInputs.length > 0
              ? ` Missing inputs: ${preview.missingInputs.join(", ")}.`
              : "";
          appendExecutionMessage({
            message: `${preview.message || "You are not allowed to run this action in the current persona."}${missingInputs}`,
            navigateTo: preview.targetRoute,
          });
          return;
        }

        const previewMissingInputs = Array.isArray(preview.missingInputs)
          ? preview.missingInputs
          : [];
        if (previewMissingInputs.length > 0) {
          openCapabilityActionPage({
            capability,
            preview,
          });
          return;
        }

        if (preview.requiresConfirmation) {
          const confirmed =
            typeof window === "undefined"
              ? true
              : window.confirm(
                  preview.confirmationText ?? `Run "${capability.title}" now?`,
                );
          if (!confirmed) {
            appendExecutionMessage({
              message: `Action "${capability.title}" cancelled.`,
            });
            return;
          }
        }

        const executePayload = await triggerExecuteAssistantAction({
          capabilityId: capability.id,
          pathname: executionPathname,
          module: capability.module,
          persona: effectivePersonaRole,
          context: actionContextPayload,
          confirm: preview.requiresConfirmation ? true : undefined,
        });
        const execution = coerceAssistantActionExecution(executePayload);
        const requiredInputLine =
          execution.requiredInputs.length > 0
            ? ` Required inputs: ${execution.requiredInputs.join(", ")}.`
            : "";
        const mutationLine = execution.mutationOperation
          ? ` Mutation: ${execution.mutationOperation}.`
          : "";
        const navigationTarget =
          typeof execution.navigateTo === "string" &&
          execution.navigateTo.length > 0
            ? execution.navigateTo
            : null;
        const shouldNavigateForInput =
          execution.status === "needs_input" && navigationTarget !== null;
        const needsInputLine = shouldNavigateForInput
          ? " Opening the required page to complete this action."
          : "";
        appendExecutionMessage({
          message: `${execution.message}${requiredInputLine}${mutationLine}${needsInputLine}`,
          navigateTo: navigationTarget,
        });
        if (
          shouldNavigateForInput &&
          navigationTarget &&
          typeof window !== "undefined"
        ) {
          window.location.assign(navigationTarget);
        }
      } catch (error) {
        appendExecutionMessage({
          message: `Unable to run action "${capability.title}" right now.`,
          navigateTo: capability.targetRoute,
        });
        console.error("[AdmissionsAI] assistant action failed", error);
      }
    },
    [
      actionContextPayload,
      appendExecutionMessage,
      effectivePersonaRole,
      loadCapabilityBrowserPreview,
      openCapabilityActionPage,
      pathname,
      triggerExecuteAssistantAction,
    ],
  );

  const prompts = runReportsSidecarMode
    ? reportGenerationSidecarPrompts
    : capabilityPrompts;

  const applyRunReportsRouteLocalPrompt = useCallback(
    async (definition: RunReportsRouteLocalPromptDefinition): Promise<void> => {
      const now = Date.now();
      const promptText = definition.prompt.prompt ?? definition.prompt.label;
      livePromptHydrationProtectionUntilRef.current =
        Date.now() + LIVE_PROMPT_HYDRATION_PROTECTION_MS;
      markConversationSessionMutation(currentConversationSessionId);
      setAssistantSurfaceView("chat");
      if (panelState.isOverlay && !panelState.isExpanded) {
        panelState.setExpanded(true);
      }
      appendConversationMessage({
        messageId: `run-reports-route-local-user-${definition.actionId}-${now}`,
        role: "user",
        content: promptText,
        timestamp: now,
      });

      const latestRouteAssistantContext =
        routeAssistantContext?.pathname === pathname
          ? routeAssistantContext
          : activeRouteAssistantContextRef.current;
      const activeRouteContext = latestRouteAssistantContext?.activeRouteContext;
      const policyHandler = latestRouteAssistantContext?.onAssistantPolicyEvent;
      if (!activeRouteContext || !policyHandler) {
        appendConversationMessage({
          messageId: `run-reports-route-local-unavailable-${definition.actionId}-${now}`,
          role: "assistant",
          content:
            "Run Reports workspace actions are not available until the visible route context is ready.",
          timestamp: now + 1,
          metadata: {
            statusReceipt: true,
            routeLocalDraftAction: true,
          },
        });
        return;
      }

      try {
        const result = await policyHandler({
          eventId: `ystnj-run-reports-route-local-${definition.actionId}-${now}`,
          type: "route_local_action",
          policyEventType: "route_local_action",
          routeContextRef: activeRouteContext.contextRef,
          routeRevision: activeRouteContext.revision,
          routeFamily: activeRouteContext.routeFamily,
          pathname: activeRouteContext.pathname,
          capabilityId:
            definition.prompt.provenance?.capabilityId ??
            definition.prompt.id ??
            definition.actionId,
          actionId: definition.actionId,
          artifactActionId: null,
          sourceRef: null,
          payload: sanitizeAwsJsonObject(definition.payload),
        });
        appendConversationMessage({
          messageId: `run-reports-route-local-assistant-${definition.actionId}-${now}`,
          role: "assistant",
          content: result.message,
          timestamp: now + 1,
          presentation: (result.presentation ?? undefined) as
            | AssistantPresentationModel
            | undefined,
          metadata: {
            statusReceipt: true,
            routeLocalDraftAction: true,
            routeContextRef: result.routeContextRef,
            routeRevision: result.routeRevision,
            routeLocalStatus: result.status,
          },
        });
      } catch (error) {
        console.error("[AdmissionsAI] Run Reports route-local action failed", {
          actionId: definition.actionId,
          error,
        });
        appendConversationMessage({
          messageId: `run-reports-route-local-error-${definition.actionId}-${now}`,
          role: "assistant",
          content:
            "I could not apply that Run Reports action to the current workspace.",
          timestamp: now + 1,
          metadata: {
            statusReceipt: true,
            routeLocalDraftAction: true,
          },
        });
      }
    },
    [
      appendConversationMessage,
      currentConversationSessionId,
      markConversationSessionMutation,
      panelState,
      pathname,
      routeAssistantContext,
    ],
  );

  const handlePromptClick = useCallback(
    (prompt: AssistantPromptUi) => {
      const promptId = typeof prompt.id === "string" ? prompt.id : "";
      const specialistPreset = promptId.startsWith("settings-specialist-")
        ? settingsPresetPromptById.get(
            promptId as `settings-specialist-${string}`,
          )
        : undefined;
      const provenanceCapabilityId =
        typeof prompt.provenance?.capabilityId === "string"
          ? prompt.provenance.capabilityId.trim()
          : "";
      const capability = promptId.startsWith("capability-")
        ? capabilityByPromptId.get(promptId as `capability-${string}`)
        : (provenanceCapabilityId
          ? capabilityById.get(provenanceCapabilityId)
          : undefined);
      const provenanceBinding: ExplicitPromptBinding | undefined =
        !capability &&
        (provenanceCapabilityId ||
          prompt.provenance?.scenarioKey ||
          prompt.provenance?.method)
          ? {
              module: capabilityModule,
              activeCapabilityId: provenanceCapabilityId || undefined,
              activeScenarioKey: prompt.provenance?.scenarioKey ?? undefined,
              activeCapabilityMethod:
                prompt.provenance?.method ?? undefined,
              executionPathname: resolveAssistantLaunchTargetPathname({
                routePrefixes: prompt.provenance?.routePrefixes ?? [],
                fallbackPathname: pathname,
              }),
              originPathname: pathname,
              catalogScope: assistantCatalogScope,
            }
          : undefined;
      const submitRouteStart = (
        question: string,
        binding?: ExplicitPromptBinding,
      ) => submitAssistantQuestion(question, binding);
      const reportGenerationPrompt =
        REPORT_GENERATION_SIDECAR_PROMPTS[promptId];
      const routeLocalRunReportsPrompt =
        RUN_REPORTS_ROUTE_LOCAL_PROMPT_BY_ID.get(promptId) ??
        RUN_REPORTS_ROUTE_LOCAL_PROMPTS.find((entry) => (
          entry.prompt.label === prompt.label
        ));
      if (runReportsSidecarMode && routeLocalRunReportsPrompt) {
        void applyRunReportsRouteLocalPrompt(routeLocalRunReportsPrompt);
        return;
      }
      if (runReportsSidecarMode && reportGenerationPrompt) {
        submitRouteStart(reportGenerationPrompt, {
          module: "tuition",
          activeCapabilityId: promptId,
          activeScenarioKey: promptId.replace(/^run-reports-sidecar-/, "run-reports.sidecar."),
          activeCapabilityMethod:
            prompt.provenance?.method ??
            "Use the Run Reports sidecar context and report-generation backend preview.",
        });
        return;
      }
      if (specialistPreset) {
        submitRouteStart(specialistPreset.prompt, {
          module: capabilityModule,
          activeCapabilityId: `settings-specialist:${specialistPreset.id}`,
          activeScenarioKey: specialistPreset.id,
          activeCapabilityMethod: SETTINGS_SPECIALIST_METHOD,
        });
        return;
      }
      if (!capability) {
        if (prompt.interactionMode === "chat-route-start") {
          submitRouteStart(prompt.prompt || prompt.label, provenanceBinding);
          return;
        }
        submitAssistantQuestion(prompt.prompt || prompt.label, provenanceBinding);
        return;
      }

      const binding = buildCapabilityPromptBinding(capability);

      if (capability.type === "action") {
        void runCapabilityAction(capability);
        return;
      }

      if (prompt.interactionMode === "chat-route-start") {
        submitRouteStart(capability.prompt || prompt.label, binding);
        return;
      }

      submitAssistantQuestion(capability.prompt || prompt.label, binding);
    },
    [
      buildCapabilityPromptBinding,
      assistantCatalogScope,
      capabilityById,
      capabilityByPromptId,
      capabilityModule,
      applyRunReportsRouteLocalPrompt,
      pathname,
      runCapabilityAction,
      runReportsSidecarMode,
      settingsPresetPromptById,
      submitAssistantQuestion,
    ],
  );
  const appendSettingsSpecialistGuide = useCallback(
    (caseId: string): boolean => {
      const presetCase = settingsSpecialistSnapshot?.presetCases.find(
        (entry) => entry.id === caseId,
      );
      const guide = settingsSpecialistSnapshot
        ? buildAssistantSettingsSpecialistGuide(
            settingsSpecialistSnapshot,
            caseId,
          )
        : null;
      if (!presetCase || !guide) {
        return false;
      }
      const startedAt = Date.now();
      if (panelState.isOverlay && !panelState.isExpanded) {
        panelState.setExpanded(true);
      }
      setShowCapabilityCatalog(false);
      setCapabilityCatalogQuery("");
      setAssistantSurfaceView("chat");
      appendConversationMessage({
        role: "user",
        content: guide.userPrompt,
        messageId: `user-settings-specialist-${caseId}-${startedAt}`,
        timestamp: startedAt,
      });
      appendConversationMessage({
        role: "assistant",
        content: guide.assistantSummary,
        messageId: `assistant-settings-specialist-${caseId}-${startedAt}`,
        timestamp: startedAt + 1,
        presentation: guide.presentation,
        metadata: {
          settingsIntent: true,
        } satisfies AdmissionsAssistantMessageMetadata,
      });
      return true;
    },
    [appendConversationMessage, panelState, settingsSpecialistSnapshot],
  );

  const handleSettingsSpecialistCaseClick = useCallback(
    (caseId: string) => {
      const didAppendGuide = appendSettingsSpecialistGuide(caseId);
      if (didAppendGuide) {
        return;
      }
      const presetCase = settingsSpecialistSnapshot?.presetCases.find(
        (entry) => entry.id === caseId,
      );
      if (!presetCase) {
        return;
      }
      submitAssistantQuestionDirect(presetCase.prompt, {
        module: capabilityModule,
        activeCapabilityId: `settings-specialist:${presetCase.id}`,
        activeScenarioKey: presetCase.id,
        activeCapabilityMethod: SETTINGS_SPECIALIST_METHOD,
      });
    },
    [
      appendSettingsSpecialistGuide,
      capabilityModule,
      settingsSpecialistSnapshot,
      submitAssistantQuestionDirect,
    ],
  );

  const handleAssistantInputAction = useCallback(
    (action: AssistantInputAction, value?: string) => {
      const registryAction = assistantToolRegistry.find(
        (tool) => tool.id === action.id,
      );
      if (registryAction) {
        registryAction.execute();
        return;
      }

      if (action.id === "settings-specialist-case") {
        const matchedCase = (
          settingsSpecialistSnapshot?.presetCases ?? []
        ).find((entry) => entry.id === value || entry.prompt === value);
        if (matchedCase && appendSettingsSpecialistGuide(matchedCase.id)) {
          return;
        }
      }

      if (action.id === "settings-specialist-owner-route") {
        const href = String(value ?? "").trim();
        if (
          href &&
          typeof window !== "undefined" &&
          (href.startsWith("/") || /^https?:\/\//.test(href))
        ) {
          window.location.assign(href);
          return;
        }
      }

      const prompt = resolveAssistantInputActionPrompt(action, value);
      if (prompt) {
        submitAssistantQuestion(prompt);
      }
    },
    [
      assistantToolRegistry,
      appendSettingsSpecialistGuide,
      settingsSpecialistSnapshot,
      submitAssistantQuestion,
    ],
  );

  const handleCapabilityCatalogItemClick = useCallback(
    (capability: AssistantCapability) => {
      setShowCapabilityCatalog(false);
      setCapabilityCatalogQuery("");
      if (capability.type === "action") {
        void runCapabilityAction(capability);
        return;
      }
      submitAssistantQuestion(capability.prompt || capability.title, {
        ...buildCapabilityPromptBinding(capability),
      });
    },
    [buildCapabilityPromptBinding, runCapabilityAction, submitAssistantQuestion],
  );

  const getCapabilityCatalogPrimaryAction = useCallback(
    (capability: AssistantCapability) =>
      resolveAssistantCapabilityBrowserActionState(
        capability,
        capabilityBrowserPreviewById[capability.id],
      ),
    [capabilityBrowserPreviewById],
  );

  const handleCapabilityCatalogPrimaryAction = useCallback(
    (capability: AssistantCapability) => {
      const actionState = getCapabilityCatalogPrimaryAction(capability);
      if (
        actionState.kind === "checking" ||
        actionState.kind === "unavailable"
      ) {
        return;
      }
      setShowCapabilityCatalog(false);
      setCapabilityCatalogQuery("");
      if (actionState.kind === "ask") {
        handleCapabilityCatalogItemClick(capability);
        return;
      }
      if (actionState.kind === "run") {
        void runCapabilityAction(capability);
        return;
      }
      const preview = capabilityBrowserPreviewById[capability.id]?.preview;
      if (actionState.kind === "open_page" && preview) {
        openCapabilityActionPage({
          capability,
          preview,
        });
      }
    },
    [
      capabilityBrowserPreviewById,
      getCapabilityCatalogPrimaryAction,
      handleCapabilityCatalogItemClick,
      openCapabilityActionPage,
      runCapabilityAction,
    ],
  );

  const handleToolToolbarClick = useCallback(
    (tool: AssistantTool) => {
      const capability = tool.capabilityId
        ? capabilityById.get(tool.capabilityId)
        : undefined;
      if (capability?.type === "action") {
        void runCapabilityAction(capability);
        return;
      }

      const prompt = tool.prompt ?? capability?.prompt ?? tool.title;
      if (
        tool.actionKey === "assistant.workflow.builder" &&
        tool.targetRoute &&
        typeof window !== "undefined"
      ) {
        const nextRoute = new URL(tool.targetRoute, window.location.origin);
        if (prompt && !nextRoute.searchParams.has("prompt")) {
          nextRoute.searchParams.set("prompt", prompt);
        }
        window.location.assign(
          `${nextRoute.pathname}${nextRoute.search}${nextRoute.hash}`,
        );
        return;
      }

      submitAssistantQuestion(
        prompt,
        buildToolPromptBinding(tool, capability),
      );
    },
    [
      buildToolPromptBinding,
      capabilityById,
      runCapabilityAction,
      submitAssistantQuestion,
    ],
  );

  const handleAssistantMessageAction = useCallback(
    (
      action: AdmissionsAIContextualAction,
      fallbackOnAction?: (nextAction: AdmissionsAIContextualAction) => void,
    ) => {
      const label = sanitizeEntityUiLabel(
        resolveAssistantActionEntityLabel(action),
        "this action",
      );
      const capabilityId = resolveAssistantActionCapabilityId(action);
      const capability = capabilityId
        ? capabilityById.get(capabilityId)
        : undefined;
      const prompt = resolveAssistantActionPrompt(action);
      const route = resolveAssistantActionRoute(action);
      const reportGenerationCommand =
        resolveReportGenerationActionCommand(action);

      if (reportGenerationCommand) {
        void executeReportGenerationCommand(reportGenerationCommand, label);
        return;
      }

      if (
        resolveAssistantActionRequiresConfirmation(action) &&
        typeof window !== "undefined"
      ) {
        const confirmed = window.confirm(`Run "${label}" now?`);
        if (!confirmed) {
          setLastActionFeedback(`Action "${label}" cancelled.`);
          return;
        }
      }

      const workspaceKind =
        typeof action.payload.workspaceKind === "string"
          ? action.payload.workspaceKind
          : null;
      if (workspaceKind === "spreadsheet-workbook") {
        const workbookArtifact = toArtifactWorkspaceRef(
          action.payload.workbookArtifact,
        );
        const spreadsheetDownloadArtifact = toArtifactWorkspaceRef(
          action.payload.spreadsheetDownloadArtifact,
        );
        if (workbookArtifact) {
          openSpreadsheetWorkspace({
            workbookArtifact,
            spreadsheetDownloadArtifact,
          });
          setLastActionFeedback(`Opened ${label}.`);
          return;
        }
        setLastActionFeedback(
          `Unable to open ${label} because the workbook artifact is unavailable.`,
        );
        return;
      }

	      if (workspaceKind === "chart-artifact") {
        const chartArtifact = toArtifactWorkspaceRef(
          action.payload.chartArtifact,
        );
        const chartPanels = coerceChartPanels(action.payload.chartPanels);
        if (chartArtifact) {
          openChartWorkspace({
            chartArtifact,
            chartPanels,
          });
          setLastActionFeedback(`Opened ${label}.`);
          return;
        }
        setLastActionFeedback(
          `Unable to open ${label} because the chart artifact is unavailable.`,
        );
	        return;
	      }

	      if (workspaceKind === "visualization3d-artifact") {
	        const visualization3DArtifact = toArtifactWorkspaceRef(
	          action.payload.visualization3DArtifact,
	        );
	        const visualization3DSpace = coerceVisualization3DSpace(
	          action.payload.visualization3DSpace,
	        );
	        const visualization3DPanels = coerceChartPanels(
	          action.payload.visualization3DPanels,
	        );
	        if (visualization3DArtifact) {
	          openVisualization3DWorkspace({
	            visualization3DArtifact,
	            visualization3DSpace,
	            visualization3DPanels,
	          });
	          setLastActionFeedback(`Opened ${label}.`);
	          return;
	        }
	        setLastActionFeedback(
	          `Unable to open ${label} because the 3D visualization artifact is unavailable.`,
	        );
	        return;
	      }

	      if (action.type === "execute" && capability) {
        void runCapabilityAction(capability);
        return;
      }

      if ((action.type === "followup" || action.type === "draft") && prompt) {
        const didSubmit = submitAssistantQuestion(
          prompt,
          capability
            ? {
                module: capability.module,
                activeCapabilityId: capability.id,
                activeScenarioKey: capability.scenarioKey ?? undefined,
                activeCapabilityMethod: capability.method ?? undefined,
              }
            : undefined,
        );
        setLastActionFeedback(
          didSubmit
            ? `Submitted follow-up: ${label}.`
            : `Prepared follow-up: ${label}.`,
        );
        return;
      }

      if (route && typeof window !== "undefined") {
        setLastActionFeedback(`Opening ${label}.`);
        window.location.assign(route);
        return;
      }

      fallbackOnAction?.(action);
    },
    [
	      capabilityById,
      executeReportGenerationCommand,
	      openChartWorkspace,
	      openVisualization3DWorkspace,
	      openSpreadsheetWorkspace,
      runCapabilityAction,
      submitAssistantQuestion,
    ],
  );

  const filtersLabel = useMemo(() => {
    const filters: string[] = [];
    if (context.status?.length) filters.push("status");
    if (context.grade?.length) filters.push("grade");
    if (context.assignedTo) filters.push("assigned");
    if (context.search) filters.push("search");
    if (
      context.selectedIds?.length ||
      context.activeApplicationId ||
      context.activeStudentId
    ) {
      filters.push("selected");
    }
    if (context.currentView) filters.push(`view:${context.currentView}`);
    if (filters.length === 0) {
      return "none";
    }
    const shown = filters.slice(0, 3);
    const remaining = filters.length - shown.length;
    return remaining > 0
      ? `${shown.join(", ")} +${remaining}`
      : shown.join(", ");
  }, [
    context.status,
    context.grade,
    context.assignedTo,
    context.search,
    context.selectedIds,
    context.activeApplicationId,
    context.activeStudentId,
    context.currentView,
  ]);

  const contextSummary = useMemo(
    () => ({
      ...summary,
      ...buildAdmissionsContextSummary(
        context,
        resultCount,
        moduleConfig.entityLabel,
      ),
      isLoading: isContextLoading,
      error: contextError,
      onRetry: refreshContext,
    }),
    [
      summary,
      context,
      resultCount,
      moduleConfig.entityLabel,
      isContextLoading,
      contextError,
      refreshContext,
    ],
  );
  const resolvedContextSummary = useMemo(
    () =>
      assistantResponseFixtureState?.contextError
        ? {
            ...contextSummary,
            error: assistantResponseFixtureState.contextError,
          }
        : contextSummary,
    [assistantResponseFixtureState?.contextError, contextSummary],
  );
  const authorityBanner = useMemo(() => {
    const rawOnBehalfOfLabel = context.onBehalfOf?.subjectLabel;
    const onBehalfOfLabel =
      context.onBehalfOf?.subjectType === "application" &&
      rawOnBehalfOfLabel &&
      !/^application\b/i.test(rawOnBehalfOfLabel)
        ? `Application ${rawOnBehalfOfLabel}`
        : (rawOnBehalfOfLabel ??
          (context.activeApplicationCode
            ? `Application ${context.activeApplicationCode}`
            : undefined) ??
          (context.activeApplicationId
            ? `Application ${context.activeApplicationId}`
            : undefined) ??
          context.activeStudentName ??
          undefined);

    return {
      actorLabel: context.personaName ?? `${personaDisplay} operator`,
      actorRole: effectivePersonaRole ?? "staff",
      schoolLabel: "YSTNJ",
      scopeLabel: `${moduleConfig.label} · ${pathname}`,
      scopeFreshnessLabel: isContextLoading
        ? "Context refreshing"
        : contextError
          ? "Context degraded"
          : "Context current",
      onBehalfOfLabel,
      sensitivityLabel:
        capabilityModule === "financial-aid"
          ? "FERPA-sensitive financial-aid context. Actions remain tenant-scoped and route-limited."
          : capabilityModule === "admissions"
            ? "Student and family context remains tenant-scoped and route-limited."
            : undefined,
    };
  }, [
    capabilityModule,
    context.activeApplicationCode,
    context.activeStudentName,
    context.onBehalfOf?.subjectLabel,
    context.personaName,
    contextError,
    effectivePersonaRole,
    isContextLoading,
    moduleConfig.label,
    pathname,
    personaDisplay,
  ]);
  const statusStripItems = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      value: string;
      tone: "success" | "warning" | "danger" | "info" | "neutral";
    }> = [];
    items.push({
      id: "runtime",
      label: "Runtime",
      value: runtimePolicyHint?.executionMode ?? "unknown",
      tone:
        runtimePolicyHint?.executionMode === "live"
          ? "success"
          : runtimePolicyHint?.executionMode === "shadow"
            ? "warning"
            : "neutral",
    });
    items.push({
      id: "actions",
      label: "Actions",
      value: assistantCatalogLoadErrors.capabilities
        ? "Load error"
        : assistantCapabilitiesLoading
        ? "Loading"
        : resolvedAssistantCapabilities.length > 0
          ? `${resolvedAssistantCapabilities.length} available`
          : "No authoritative actions",
      tone: assistantCatalogLoadErrors.capabilities
        ? "danger"
        : assistantCapabilitiesLoading
        ? "info"
        : resolvedAssistantCapabilities.length > 0
          ? "success"
          : "warning",
    });
    items.push({
      id: "tools",
      label: "Tools",
      value: assistantCatalogLoadErrors.tools
        ? "Load error"
        : assistantToolsLoading
        ? "Loading"
        : toolToolbarPreferences.hiddenAll
          ? "Hidden"
          : toolbarTools.length > 0
            ? `${toolbarTools.length} visible`
            : "No route tools",
      tone: assistantCatalogLoadErrors.tools
        ? "danger"
        : assistantToolsLoading
        ? "info"
        : toolToolbarPreferences.hiddenAll
          ? "neutral"
          : toolbarTools.length > 0
            ? "success"
            : "warning",
    });
    const visibleQueueCount =
      queuedAssistantWorkItems.length > 0
        ? queuedAssistantWorkItems.length
        : optimisticTaskSeeds.length;
    if (visibleQueueCount > 0) {
      items.push({
        id: "queue",
        label: "Queue",
        value: `${visibleQueueCount} pending`,
        tone: "info",
      });
    }
    if (
      context.onBehalfOf?.subjectLabel ||
      context.activeApplicationCode ||
      context.activeStudentName
    ) {
      items.push({
        id: "focus",
        label: "Focus",
        value:
          context.onBehalfOf?.subjectLabel ??
          context.activeApplicationCode ??
          context.activeStudentName ??
          "Active subject",
        tone: "info",
      });
    }
    return items;
  }, [
    assistantCatalogLoadErrors.capabilities,
    assistantCatalogLoadErrors.tools,
    assistantCapabilitiesLoading,
    assistantToolsLoading,
    context.activeApplicationCode,
    context.activeStudentName,
    context.onBehalfOf?.subjectLabel,
    optimisticTaskSeeds.length,
    queuedAssistantWorkItems.length,
    resolvedAssistantCapabilities.length,
    runtimePolicyHint?.executionMode,
    toolToolbarPreferences.hiddenAll,
    toolbarTools.length,
  ]);
  const showScopedGovernanceInline = useMemo(() => {
    const routeIsScopedDetail =
      /^\/school\/(admissions\/children|financialaid\/staff)\//.test(pathname);
    const scopedSubjectPresent = Boolean(
      context.onBehalfOf?.subjectId ||
        context.onBehalfOf?.subjectLabel ||
        context.activeApplicationId ||
        context.activeApplicationCode ||
        context.activeStudentId ||
        context.activeStudentName,
    );
    return routeIsScopedDetail || scopedSubjectPresent;
  }, [
    context.activeApplicationCode,
    context.activeApplicationId,
    context.activeStudentId,
    context.activeStudentName,
    context.onBehalfOf?.subjectId,
    context.onBehalfOf?.subjectLabel,
    pathname,
  ]);
  const mergedTaskLaneState = useMemo(() => {
    const queueItems = queuedAssistantWorkItems
      .map((item) =>
        toAssistantTaskListItemFromRecord(
          item as unknown as Record<string, unknown>,
        ),
      )
      .filter((item): item is AssistantTaskListItem => Boolean(item));
    const realtimeWorkItems = latestRealtimeWorkItems.map(
      toAssistantRealtimeWorkItemLike,
    );
    return mergeAssistantTaskLaneItems({
      pathname,
      activeItems: queueItems,
      historyItems: taskHistoryItems,
      optimisticSeeds: optimisticTaskSeeds,
      realtimeWorkItems,
      realtimeRuns: Object.values(assistantRealtimeState.runsById),
      nowEpochMs: Date.now(),
    });
  }, [
    latestRealtimeWorkItems,
    assistantRealtimeState.runsById,
    optimisticTaskSeeds,
    pathname,
    queuedAssistantWorkItems,
    taskHistoryItems,
  ]);
  const activeTaskItems = mergedTaskLaneState.activeItems;
  const recentTaskItems = mergedTaskLaneState.recentItems;
  const conversationAnnouncementTasks = useMemo(
    () =>
      selectConversationAnnouncementTaskCandidates([
        ...taskHistoryItems,
        ...activeTaskItems,
        ...recentTaskItems,
      ]),
    [activeTaskItems, recentTaskItems, taskHistoryItems],
  );
  const activeConversationAnnouncementTasks = useMemo(
    () =>
      conversationAnnouncementTasks.filter(
        (task) => !isConversationAnnouncementTaskTerminal(task),
      ),
    [conversationAnnouncementTasks],
  );
  const completedConversationAnnouncementTasks = useMemo(
    () =>
      conversationAnnouncementTasks.filter((task) =>
        isConversationAnnouncementTaskTerminal(task),
      ),
    [conversationAnnouncementTasks],
  );
  const taskShelfActiveRecords = useMemo(
    () => activeTaskItems.map(toIntellioTaskRecord),
    [activeTaskItems],
  );
  const taskShelfRecentRecords = useMemo(
    () => recentTaskItems.map(toIntellioTaskRecord),
    [recentTaskItems],
  );
  const activeSpreadsheetTask = useMemo(
    () =>
      resolveVisibleActiveSpreadsheetTask(
        activeTaskItems,
        currentSpreadsheetSession,
      ),
    [activeTaskItems, currentSpreadsheetSession],
  );
  const latestCompletedSpreadsheetArtifact = useMemo(() => {
    for (const task of recentTaskItems) {
      if (
        task.state !== "completed" ||
        !task.workflowKey ||
        !SPREADSHEET_TASK_WORKFLOWS.has(task.workflowKey)
      ) {
        continue;
      }
      const response = task.runId
        ? (assistantRealtimeState.runsById[task.runId]?.response ?? null)
        : null;
      const artifactPreview = buildArtifactPreviewFromResponse({
        task,
        response,
      });
      if (artifactPreview?.workbookArtifact) {
        return {
          task,
          artifactPreview,
        };
      }
    }
    return null;
  }, [assistantRealtimeState.runsById, recentTaskItems]);

  useEffect(() => {
    if (assistantResponseFixtureState) {
      return;
    }
    activeConversationAnnouncementTasks.forEach((task) => {
      const response = task.runId
        ? (assistantRealtimeState.runsById[task.runId]?.response ?? null)
        : null;
      const artifactPreview = buildArtifactPreviewFromResponse({
        task,
        response,
      });
      const workflow = projectAssistantTaskWorkflow({
        task,
        response,
        artifactPreview,
      });
      if (!workflow.isArtifactWorkflow) {
        return;
      }
      const matchedConversationLaunch = findAssistantConversationTaskLaunch(
        {
          id: task.id,
          runId: task.runId,
          workflowKey: task.workflowKey,
          title: task.title,
          route: task.route,
          updatedAt: task.updatedAt,
        },
        conversationTaskLaunchesRef.current,
      );
      const threadId =
        matchedConversationLaunch?.threadId ?? task.origin?.threadId ?? null;
      const shouldAnnounceInConversation =
        Boolean(threadId) &&
        (launchedConversationTaskIdsRef.current.has(task.id) ||
          (task.runId
            ? launchedConversationTaskRunIdsRef.current.has(task.runId)
            : false) ||
          Boolean(matchedConversationLaunch));
      if (!threadId || !shouldAnnounceInConversation) {
        return;
      }
      if (
        announcedTaskProgressSignaturesRef.current[threadId] ===
        TERMINAL_TASK_THREAD_PROGRESS_SIGNATURE
      ) {
        return;
      }

      const taskThread = resolveConversationTaskThreadState({
        task,
        threadId,
        response,
        artifactPreview,
      });
      const nextSignature = [
        taskThread.state,
        taskThread.stateLabel,
        taskThread.statusMessage ?? "",
        toAssistantLongRunningWorkflowProgressBucket(
          taskThread.progressPercent,
        ),
        taskThread.artifactCount ?? 0,
      ].join("::");
      if (
        announcedTaskProgressSignaturesRef.current[threadId] === nextSignature
      ) {
        return;
      }
      announcedTaskProgressSignaturesRef.current[threadId] = nextSignature;
      const taskProgressResponse = normalizeUnifiedAssistantResponseEnvelope({
        answer: buildAssistantArtifactWorkflowProgressMessage({
          title: task.title,
          workflow,
        }),
        citations: [],
        actions: [],
        module: task.route
          ? resolveAIAssistantModule(task.route)
          : capabilityModule,
        debug: null,
        pathname: task.route ?? pathname,
        taskThread,
      });
      appendConversationMessage({
        role: "assistant",
        content: taskProgressResponse.answer,
        presentation: taskProgressResponse.presentation,
        messageId: `assistant-task-progress-${threadId}-${task.updatedAt ?? Date.now()}`,
        timestamp: Date.now(),
        metadata: {
          presentation: taskProgressResponse.presentation,
          artifactPreview: artifactPreview ?? undefined,
          taskThread,
          presentationDiagnostics: taskProgressResponse.presentationDiagnostics,
        } satisfies AdmissionsAssistantMessageMetadata,
      });
    });
  }, [
    activeConversationAnnouncementTasks,
    assistantResponseFixtureState,
    appendConversationMessage,
    assistantRealtimeState.runsById,
    capabilityModule,
    pathname,
  ]);

  useEffect(() => {
    if (optimisticTaskSeeds.length === 0) {
      return;
    }
    const visibleItems = [...activeTaskItems, ...recentTaskItems];
    const now = Date.now();
    setOptimisticTaskSeeds((current) => {
      const next = current.filter(
        (seed) =>
          now - seed.launchedAt < BACKGROUND_TASK_OPTIMISTIC_RETENTION_MS &&
          !visibleItems.some(
            (item) =>
              !item.isOptimistic &&
              (item.id === seed.id || item.runId === seed.runId),
          ),
      );
      return next.length === current.length ? current : next;
    });
  }, [activeTaskItems, optimisticTaskSeeds.length, recentTaskItems]);

  useEffect(() => {
    if (!latestCompletedSpreadsheetArtifact?.artifactPreview.workbookArtifact) {
      return;
    }
    const nextTaskId = latestCompletedSpreadsheetArtifact.task.id;
    const nextUpdatedAt =
      latestCompletedSpreadsheetArtifact.task.updatedAt ?? null;
    const nextWorkbookSourceRef =
      latestCompletedSpreadsheetArtifact.artifactPreview.workbookArtifact
        .sourceRef ?? null;
    const nextDownloadSourceRef =
      latestCompletedSpreadsheetArtifact.artifactPreview
        .spreadsheetDownloadArtifact?.sourceRef ?? null;
    if (
      currentSpreadsheetSession?.sourceTaskId === nextTaskId &&
      currentSpreadsheetSession?.sourceUpdatedAt === nextUpdatedAt &&
      (currentSpreadsheetSession?.workbookArtifact.sourceRef ?? null) ===
        nextWorkbookSourceRef &&
      (currentSpreadsheetSession?.spreadsheetDownloadArtifact?.sourceRef ??
        null) === nextDownloadSourceRef
    ) {
      return;
    }
    storeSpreadsheetSession({
      workbookArtifact:
        latestCompletedSpreadsheetArtifact.artifactPreview.workbookArtifact,
      spreadsheetDownloadArtifact:
        latestCompletedSpreadsheetArtifact.artifactPreview
          .spreadsheetDownloadArtifact ?? null,
      sourceTaskId: nextTaskId,
      sourceUpdatedAt: nextUpdatedAt,
    });
  }, [
    currentSpreadsheetSession?.sourceTaskId,
    currentSpreadsheetSession?.sourceUpdatedAt,
    latestCompletedSpreadsheetArtifact,
    storeSpreadsheetSession,
  ]);

  useEffect(() => {
    if (assistantResponseFixtureState) {
      return;
    }
    const hydratingCompletedTasks = !completedTaskHydrationRef.current;
    if (!completedTaskHydrationRef.current) {
      completedTaskHydrationRef.current = true;
    }

    completedConversationAnnouncementTasks.forEach((task) => {
      const completionAnnouncementKey = buildCompletedTaskAnnouncementKey({
        id: task.id,
        runId: task.runId,
        workflowKey: task.workflowKey,
        updatedAt: task.updatedAt,
      });
      if (
        task.state !== "completed" &&
        task.state !== "failed" &&
        task.state !== "cancelled"
      ) {
        return;
      }
      const matchedConversationLaunch = findAssistantConversationTaskLaunch(
        {
          id: task.id,
          runId: task.runId,
          workflowKey: task.workflowKey,
          title: task.title,
          route: task.route,
          updatedAt: task.updatedAt,
        },
        conversationTaskLaunchesRef.current,
      );
      const existingConversationThreadId = resolveConversationThreadIdForTask(
        conversation.messages,
        task,
      );
      const shouldAnnounceInConversation =
        Boolean(existingConversationThreadId) ||
        launchedConversationTaskIdsRef.current.has(task.id) ||
        (task.runId
          ? launchedConversationTaskRunIdsRef.current.has(task.runId)
          : false) ||
        Boolean(matchedConversationLaunch);
      const announcementDisposition =
        resolveCompletedTaskAnnouncementDisposition({
          hydratingCompletedTasks,
          shouldAnnounceInConversation,
          hasPendingConversationLaunches:
            conversationTaskLaunchesRef.current.length > 0,
        });
      if (announcementDisposition === "skip-hydration") {
        announcedCompletedTaskIdsRef.current.add(completionAnnouncementKey);
        return;
      }
      if (
        announcedCompletedTaskIdsRef.current.has(completionAnnouncementKey) ||
        processingCompletedTaskIdsRef.current.has(completionAnnouncementKey)
      ) {
        return;
      }
      const response = task.runId
        ? (assistantRealtimeState.runsById[task.runId]?.response ?? null)
        : null;
      const initialArtifactPreview = buildArtifactPreviewFromResponse({
        task,
        response,
      });
      const workflow = projectAssistantTaskWorkflow({
        task,
        response,
        artifactPreview: initialArtifactPreview,
      });
      if (
        shouldDeferCompletedArtifactAnnouncement({
          task,
          workflow,
          artifactPreview: initialArtifactPreview,
        })
      ) {
        return;
      }
      processingCompletedTaskIdsRef.current.add(completionAnnouncementKey);
      void (async () => {
        try {
          let artifactPreview = initialArtifactPreview;
          const renderHints: AdmissionsAssistantMessageMetadata["renderHints"] =
            undefined;
	          if (artifactPreview?.visualization3DArtifact) {
	            const resolvedVisualization3DArtifactState =
	              await refreshVisualization3DArtifactState(
	                artifactPreview.visualization3DArtifact,
	              );
	            if (resolvedVisualization3DArtifactState) {
	              artifactPreview = {
	                ...artifactPreview,
	                visualization3DArtifact:
	                  resolvedVisualization3DArtifactState.visualization3DArtifact,
	                visualization3DSpace:
	                  resolvedVisualization3DArtifactState.visualization3DSpace,
	                visualization3DPanels:
	                  resolvedVisualization3DArtifactState.visualization3DPanels,
	              };
	            }
	          }

	          if (artifactPreview?.chartArtifact) {
            const resolvedChartArtifactState =
              await resolveAssistantChartArtifactState({
                chartArtifact: artifactPreview.chartArtifact,
                initialCharts: artifactPreview.chartPanels,
                refreshChartArtifactState,
                debugSource: "completed-task-announcement",
              });
            artifactPreview = {
              ...artifactPreview,
              chartArtifact: resolvedChartArtifactState.chartArtifact,
              chartPanels: resolvedChartArtifactState.chartPanels,
            };
          }

          if (artifactPreview?.workbookArtifact) {
            storeSpreadsheetSession({
              workbookArtifact: artifactPreview.workbookArtifact,
              spreadsheetDownloadArtifact:
                artifactPreview.spreadsheetDownloadArtifact ?? null,
              sourceTaskId: task.id,
              sourceUpdatedAt: task.updatedAt ?? null,
            });
          }
          if (announcementDisposition === "settle-silently") {
            announcedCompletedTaskIdsRef.current.add(completionAnnouncementKey);
            return;
          }
          if (announcementDisposition === "defer-until-match") {
            return;
          }
          const threadId =
            matchedConversationLaunch?.threadId ??
            existingConversationThreadId ??
            task.origin?.threadId ??
            null;
          const artifactLabel =
            !artifactPreview && task.artifacts[0]?.label
              ? ` Artifact ready: ${task.artifacts[0].label}.`
              : "";
          const taskCompletionResponse =
            normalizeUnifiedAssistantResponseEnvelope({
              answer: `${task.title} completed. ${task.resultSummary ?? task.statusMessage ?? "The task finished successfully."}${artifactLabel}`,
              citations: [],
              actions: [],
              module: task.route
                ? resolveAIAssistantModule(task.route)
                : capabilityModule,
              debug: null,
              pathname: task.route ?? pathname,
              artifactPreview,
            });
          const taskThread = threadId
            ? resolveConversationTaskThreadState({
                task,
                threadId,
                response,
                artifactPreview,
              })
            : undefined;
          if (threadId) {
            delete announcedTaskProgressSignaturesRef.current[threadId];
          }
          appendConversationMessage({
            role: "assistant",
            content: taskCompletionResponse.answer,
            presentation: taskCompletionResponse.presentation,
            messageId: `assistant-task-complete-${task.id}`,
            timestamp: Date.now(),
            metadata: {
              feedbackContext: buildAssistantFeedbackContext({
                assistantEpisodeId,
                pathname,
                routeMetadataToken: resolveRouteMetadataToken(pathname),
                persona: effectivePersonaRole,
                debug: null,
              }),
              artifactPreview: artifactPreview ?? undefined,
              renderHints,
              presentation: taskCompletionResponse.presentation,
              taskThread,
              presentationDiagnostics:
                taskCompletionResponse.presentationDiagnostics,
            } satisfies AdmissionsAssistantMessageMetadata,
          });
          if (threadId) {
            announcedTaskProgressSignaturesRef.current[threadId] =
              TERMINAL_TASK_THREAD_PROGRESS_SIGNATURE;
          }
          announcedCompletedTaskIdsRef.current.add(completionAnnouncementKey);
          if (matchedConversationLaunch) {
            persistConversationTaskLaunches(
              removeAssistantConversationTaskLaunch(
                conversationTaskLaunchesRef.current,
                matchedConversationLaunch,
              ),
            );
          }
        } finally {
          processingCompletedTaskIdsRef.current.delete(
            completionAnnouncementKey,
          );
        }
      })();
    });
  }, [
    assistantResponseFixtureState,
    appendConversationMessage,
    assistantEpisodeId,
    assistantRealtimeState.runsById,
    capabilityModule,
    completedConversationAnnouncementTasks,
    effectivePersonaRole,
    pathname,
    persistConversationTaskLaunches,
    storeSpreadsheetSession,
    conversation.messages,
  ]);

  const cancelBackgroundTask = useCallback(
    async (runId: string | null) => {
      if (!runId) {
        setLastActionFeedback(
          "This task cannot be cancelled because its run identifier is unavailable.",
        );
        return;
      }
      try {
        await triggerMutateAssistantRun({
          input: {
            action: "cancel",
            runId,
          },
        });
        setLastActionFeedback("Background task cancelled.");
      } catch (error) {
        console.error("[AdmissionsAI] Failed to cancel background task", error);
        setLastActionFeedback(
          "Unable to cancel the background task right now.",
        );
      }
    },
    [triggerMutateAssistantRun],
  );

  const confirmBackgroundTask = useCallback(
    async (runId: string | null) => {
      if (!runId) {
        setLastActionFeedback(
          "This task cannot be confirmed because its run identifier is unavailable.",
        );
        return;
      }
      try {
        await triggerMutateAssistantRun({
          input: {
            action: "confirm",
            runId,
          },
        });
        setLastActionFeedback(
          "Task confirmation accepted. Delivery is now in progress.",
        );
      } catch (error) {
        console.error(
          "[AdmissionsAI] Failed to confirm background task",
          error,
        );
        setLastActionFeedback(
          "Unable to confirm the background task right now.",
        );
      }
    },
    [triggerMutateAssistantRun],
  );

  const schoolShellRecentItems = useMemo(() => {
    const scopeLabel = isAssistantHomeRoute
      ? "Cross-module starter"
      : `${resultCount} ${moduleConfig.entityLabel} in scope`;
    const topCapability = orderedCapabilities[0];
    const topSnapshot = topCapability
      ? rankedSnapshotById.get(topCapability.id)
      : undefined;
    const uniqueSessionQuestions = dedupe(
      conversation.messages
        .filter((message) => message.role === "user")
        .map((message) => String(message.content ?? "").trim())
        .filter((content) => content.length > 0)
        .reverse(),
    );
    const persistedRecentItems = projectAssistantConversationRecentItems(
      conversation.messages,
      moduleConfig.label,
      {
        persistedSessions: conversationSessions,
        limit: 3,
      },
    );
    if (persistedRecentItems.length > 0) {
      return persistedRecentItems;
    }
    const capabilityByNormalizedPrompt = new Map<string, AssistantCapability>();
    for (const capability of orderedCapabilities) {
      for (const token of [capability.prompt, capability.title]) {
        const normalized = normalizeAssistantText(String(token ?? ""));
        if (
          normalized.length > 0 &&
          !capabilityByNormalizedPrompt.has(normalized)
        ) {
          capabilityByNormalizedPrompt.set(normalized, capability);
        }
      }
    }

    if (uniqueSessionQuestions.length > 0) {
      const sessionScopedCapabilities = dedupe(
        uniqueSessionQuestions
          .map((question) =>
            capabilityByNormalizedPrompt.get(normalizeAssistantText(question)),
          )
          .filter((capability): capability is AssistantCapability =>
            Boolean(capability),
          )
          .map((capability) => capability.id),
      )
        .map((capabilityId) =>
          orderedCapabilities.find(
            (capability) => capability.id === capabilityId,
          ),
        )
        .filter((capability): capability is AssistantCapability =>
          Boolean(capability),
        );

      if (sessionScopedCapabilities.length > 0) {
        return sessionScopedCapabilities.slice(0, 3).map((capability) => {
          const snapshotEntry = rankedSnapshotById.get(capability.id);
          const methodLine =
            snapshotEntry?.method ??
            capability.method ??
            snapshotEntry?.rationale ??
            capability.rationale;
          const likelySources = capability.likelySources?.length
            ? capability.likelySources
            : (snapshotEntry?.likelySources ?? []);
          const routePrefixes = capability.routePrefixes?.length
            ? capability.routePrefixes
            : (snapshotEntry?.routePrefixes ?? []);
          return {
            id: `recent-session-${capability.id}`,
            title: capability.title,
            subtitle: `${scopeLabel} • ${truncateRecentText(methodLine, 80)}`,
            prompt: capability.prompt || capability.title,
            provenance: {
              capabilityId: capability.id,
              sourceOrigin:
                capability.sourceOrigin ?? snapshotEntry?.sourceOrigin ?? null,
              scenarioKey:
                capability.scenarioKey ?? snapshotEntry?.scenarioKey ?? null,
              method: snapshotEntry?.method ?? capability.method ?? null,
              likelySources,
              routePrefixes,
            },
          };
        });
      }
    }

    return orderedCapabilities.slice(0, 3).map((capability) => {
      const snapshotEntry = rankedSnapshotById.get(capability.id);
      const methodLine =
        snapshotEntry?.method ??
        capability.method ??
        snapshotEntry?.rationale ??
        capability.rationale;
      const likelySources = capability.likelySources?.length
        ? capability.likelySources
        : (snapshotEntry?.likelySources ?? []);
      const routePrefixes = capability.routePrefixes?.length
        ? capability.routePrefixes
        : (snapshotEntry?.routePrefixes ?? []);
      return {
        id: `recent-capability-${capability.id}`,
        title: capability.title,
        subtitle: `${scopeLabel} • ${truncateRecentText(methodLine, 80)}`,
        prompt: capability.prompt || capability.title,
        provenance: {
          capabilityId: capability.id,
          sourceOrigin:
            capability.sourceOrigin ?? snapshotEntry?.sourceOrigin ?? null,
          scenarioKey:
            capability.scenarioKey ?? snapshotEntry?.scenarioKey ?? null,
          method: snapshotEntry?.method ?? capability.method ?? null,
          likelySources,
          routePrefixes,
        },
      };
    });
  }, [
    conversation.messages,
    conversationSessions,
    isAssistantHomeRoute,
    moduleConfig.entityLabel,
    moduleConfig.label,
    orderedCapabilities,
    rankedSnapshotById,
    resultCount,
  ]);
  const selectedThreadsSession = useMemo(
    () =>
      selectedThreadsSessionId
        ? (conversationSessions.find(
            (session) => session.id === selectedThreadsSessionId,
          ) ?? null)
        : null,
    [conversationSessions, selectedThreadsSessionId],
  );
  const selectedThreadsTimeline = useMemo(() => {
    if (!selectedThreadsSessionId) {
      return null;
    }
    if (
      selectedThreadsSessionId === currentConversationSessionId &&
      currentConversationSession &&
      conversationMessagesHydrated
    ) {
      return {
        session: currentConversationSession,
        messages: displayedConversationMessages.map((message, index) =>
          toConversationMessageRecordFromAssistantMessage(
            currentConversationSession.id,
            message,
            index + 1,
          ),
        ),
      } satisfies AssistantConversationTimelineState;
    }
    const cached =
      conversationTimelinesBySessionId[selectedThreadsSessionId] ?? null;
    if (cached) {
      return cached;
    }
    return null;
  }, [
    conversationMessagesHydrated,
    conversationTimelinesBySessionId,
    currentConversationSession,
    currentConversationSessionId,
    displayedConversationMessages,
    selectedThreadsSessionId,
  ]);
  const selectedThreadsTimelineMeta = useMemo(
    () =>
      selectedThreadsSessionId
        ? (conversationTimelineMetaBySessionId[selectedThreadsSessionId] ??
          null)
        : null,
    [conversationTimelineMetaBySessionId, selectedThreadsSessionId],
  );
  const selectedThreadsTimelineStatus = useMemo<
    "idle" | "loading" | "ready" | "error"
  >(() => {
    if (!selectedThreadsSessionId) {
      return "idle";
    }
    if (selectedThreadsTimelineMeta?.status === "error") {
      return "error";
    }
    if (selectedThreadsTimeline) {
      return "ready";
    }
    if (selectedThreadsTimelineMeta?.status === "loading") {
      return "loading";
    }
    if (selectedThreadsSessionId === currentConversationSessionId) {
      return conversationMessagesHydrated ? "ready" : "loading";
    }
    if ((selectedThreadsSession?.messageCount ?? 0) > 0) {
      return "loading";
    }
    return selectedThreadsSession ? "ready" : "idle";
  }, [
    conversationMessagesHydrated,
    currentConversationSessionId,
    selectedThreadsSession,
    selectedThreadsSessionId,
    selectedThreadsTimeline,
    selectedThreadsTimelineMeta?.status,
  ]);
  const selectedThreadsTimelineErrorMessage =
    selectedThreadsTimelineMeta?.errorMessage ?? null;
  const retrySelectedThreadsTimeline = useCallback(
    (sessionId: string) => {
      void loadConversationTimeline(sessionId).catch((error) => {
        console.warn(
          "[AdmissionsAI] Failed to retry assistant conversation timeline",
          error,
        );
      });
    },
    [loadConversationTimeline],
  );
  const handleShowFullTimeline = useCallback(() => {
    if (!currentConversationSessionId) {
      return;
    }
    setSelectedThreadsSessionId(currentConversationSessionId);
    setAssistantSurfaceView("history");
  }, [currentConversationSessionId]);
  const conversationLeadNode = useMemo(
    () => (
      <AssistantConversationSessionLead
        currentSession={currentConversationSession}
        previousSession={previousConversationSession}
        onShowFullTimeline={handleShowFullTimeline}
        onStartNewSession={() => {
          void startFreshConversationSession(true);
        }}
        onOpenPreviousSession={
          previousConversationSession
            ? (sessionId) => openConversationSession(sessionId, null, "chat")
            : null
        }
      />
    ),
    [
      currentConversationSession,
      handleShowFullTimeline,
      openConversationSession,
      previousConversationSession,
      startFreshConversationSession,
    ],
  );
  const openConversationSessionFromThreadList = useCallback(
    (
      session: IntellioAssistantConversationSessionRecord,
      messageId: string | null | undefined,
    ) => openConversationSession(session.id, messageId, "chat"),
    [openConversationSession],
  );
  const getPreferredConversationSessionMessageId = useCallback(
    (session: IntellioAssistantConversationSessionRecord): string | null =>
      session.firstUserMessageId ?? session.lastMessageId ?? null,
    [],
  );
  const handleSchoolShellRecentItemClick = useCallback(
    (item: AIAssistantRecentItem): void => {
      const previewMessageId = item.previewMessageId ?? null;
      if (item.sessionId) {
        void openConversationSession(item.sessionId, previewMessageId, "chat");
        return;
      }
      if (item.source === "current-session") {
        const currentSessionId =
          currentConversationSessionId ?? activeConversationSessionIdRef.current;
        if (currentSessionId) {
          void openConversationSession(currentSessionId, previewMessageId, "chat");
          return;
        }
      }
      const normalizedItemText = normalizeAssistantText(
        String(item.prompt ?? item.title ?? ""),
      );
      const matchingSession =
        conversationSessions.find((session) => {
          const sessionTexts = [
            session.title,
            session.routeLabel,
            session.firstUserPreview,
            session.lastAssistantPreview,
            session.lastMessagePreview,
          ]
            .map((value) => normalizeAssistantText(String(value ?? "")))
            .filter((value) => value.length > 0);
          return sessionTexts.some(
            (value) =>
              value === normalizedItemText ||
              (normalizedItemText.length > 0 &&
                value.includes(normalizedItemText)),
          );
        }) ?? null;
      if (matchingSession) {
        void openConversationSessionFromThreadList(
          matchingSession,
          getPreferredConversationSessionMessageId(matchingSession),
        );
        return;
      }
      setAssistantSurfaceView("chat");
      submitAssistantQuestion(item.prompt ?? item.title);
    },
    [
      conversationSessions,
      currentConversationSessionId,
      getPreferredConversationSessionMessageId,
      openConversationSession,
      openConversationSessionFromThreadList,
      setAssistantSurfaceView,
      submitAssistantQuestion,
    ],
  );
  const settingsSpecialistLeadNode = useMemo(
    () =>
      showAmbientSettingsSpecialist && settingsSpecialistSnapshot ? (
        <AssistantSettingsSpecialistLead
          snapshot={settingsSpecialistSnapshot}
          onPresetCaseClick={handleSettingsSpecialistCaseClick}
        />
      ) : null,
    [
      handleSettingsSpecialistCaseClick,
      settingsSpecialistSnapshot,
      showAmbientSettingsSpecialist,
    ],
  );
  const schoolShellGovernanceNode = useMemo(
    () => (
      <div className="space-y-2">
        <IntellioAuthorityBanner authority={authorityBanner} />
        {statusStripItems.length > 0 ? (
          <IntellioStatusStrip items={statusStripItems} />
        ) : null}
      </div>
    ),
    [authorityBanner, statusStripItems],
  );
  const schoolShellRuntimeSummaryNode = useMemo(
    () => (
      <div className="space-y-3">
        {schoolShellGovernanceNode}
        {conversationLeadNode}
        {settingsSpecialistLeadNode}
      </div>
    ),
    [
      conversationLeadNode,
      schoolShellGovernanceNode,
      settingsSpecialistLeadNode,
    ],
  );
  const schoolShellConversationLeadNode = useMemo(
    () =>
      showScopedGovernanceInline ? (
        <section
          className="rounded-[22px] border border-slate-200/80 bg-white px-4 py-4 shadow-[0_14px_32px_rgba(36,50,53,0.05)]"
          data-testid="ai-school-shell-governance-inline"
        >
          {schoolShellGovernanceNode}
        </section>
      ) : null,
    [schoolShellGovernanceNode, showScopedGovernanceInline],
  );
  // The standalone all-artifacts browser is intentionally disabled in normal
  // UX. Artifact behavior stays inline in conversation/history messages and in
  // explicit workspaces opened from those messages. Do not repopulate this from
  // work-item history without updating assistant visual authority and gates.
  const assistantArtifactFamilies: AssistantArtifactPanelFamily[] = [];
  const schoolShellHistoryPanelNode = useMemo(
    () => (
      <AssistantThreadsBrowser
        sessions={conversationSessions}
        currentSessionId={currentConversationSessionId}
        selectedSessionId={selectedThreadsSessionId}
        selectedTimeline={selectedThreadsTimeline}
        selectedTimelineStatus={selectedThreadsTimelineStatus}
        selectedTimelineErrorMessage={selectedThreadsTimelineErrorMessage}
        onRetrySelectedTimeline={retrySelectedThreadsTimeline}
        onSelectSession={(sessionId) => setSelectedThreadsSessionId(sessionId)}
        onOpenInChat={(sessionId, messageId) =>
          openConversationSession(sessionId, messageId, "chat")
        }
      />
    ),
    [
      conversationSessions,
      currentConversationSessionId,
      openConversationSession,
      retrySelectedThreadsTimeline,
      selectedThreadsSessionId,
      selectedThreadsTimelineErrorMessage,
      selectedThreadsTimeline,
      selectedThreadsTimelineStatus,
    ],
  );
  const schoolShellNavigatePanelNode = useMemo(
    () => (
      <div className="space-y-4">
        <section
          className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]"
          data-testid="ai-school-shell-governance-panel"
        >
          <div className="max-w-[28rem]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
              Context
            </p>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">
              Runtime and route authority
            </h3>
            <p className="mt-2 text-sm leading-6 text-irongrey/75">
              Runtime governance, tenant scope, and active route context live in
              this utility surface so the model menu stays aligned to the
              external prototype.
            </p>
          </div>
          <div className="mt-4">{schoolShellRuntimeSummaryNode}</div>
        </section>
        <section className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">
                Navigate
              </p>
              <h3 className="mt-2 text-lg font-semibold text-slate-900">
                Route-aware assistant actions
              </h3>
              <p className="mt-2 text-sm leading-6 text-irongrey/75">
                Ask from capability prompts, run allowed actions, or open the
                full capability browser for this route.
              </p>
            </div>
            {resolvedAssistantCapabilities.length > 0 ? (
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-primary/15 bg-primary/5 px-3 py-1.5 text-[12px] font-semibold text-primary shadow-[0_8px_18px_rgba(36,50,53,0.04)] transition-colors hover:bg-primary hover:text-white"
                onClick={() => setShowCapabilityCatalog(true)}
                data-testid="ai-school-shell-browse-capabilities"
              >
                Browse all
              </button>
            ) : null}
          </div>
        </section>
        <div className="space-y-3">
          {orderedCapabilities.slice(0, 6).map((capability) => {
            const likelySources = joinAssistantPromptAttributeList(
              capability.likelySources,
            );
            const routePrefixes = joinAssistantPromptAttributeList(
              capability.routePrefixes,
            );
            return (
              <article
                key={`navigate-capability-${capability.id}`}
                className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]"
                data-testid="ai-school-shell-navigate-card"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary/70">
                    {capability.type === "action"
                      ? "Route action"
                      : "Prompt path"}
                  </p>
                  <span className="rounded-full border border-slate-200 bg-[#f8faf9] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-irongrey/60">
                    {capability.module}
                  </span>
                </div>
                <h4 className="mt-1 text-sm font-semibold text-slate-900">
                  {capability.title}
                </h4>
                <p className="mt-2 text-sm leading-6 text-irongrey/75">
                  {capability.rationale ||
                    capability.method ||
                    capability.prompt ||
                    "Route-aware capability"}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-[12px] font-semibold text-white shadow-[0_10px_24px_rgba(28,59,62,0.16)] transition-colors hover:bg-primary-hover"
                    onClick={() => {
                      if (capability.type === "action") {
                        void runCapabilityAction(capability);
                        return;
                      }
                      submitAssistantQuestion(
                        capability.prompt || capability.title,
                        {
                          module: capability.module,
                          activeCapabilityId: capability.id,
                          activeScenarioKey:
                            capability.scenarioKey ?? undefined,
                          activeCapabilityMethod:
                            capability.method ?? undefined,
                        },
                      );
                    }}
                    data-testid="ai-school-shell-quick-action"
                    data-ai-prompt-surface="navigate"
                    data-ai-prompt-label={capability.title}
                    data-ai-prompt-provenance="present"
                    data-ai-prompt-capability-id={capability.id}
                    data-ai-prompt-source-origin={
                      capability.sourceOrigin ?? undefined
                    }
                    data-ai-prompt-scenario-key={
                      capability.scenarioKey ?? undefined
                    }
                    data-ai-prompt-method={capability.method ?? undefined}
                    data-ai-prompt-likely-sources={likelySources}
                    data-ai-prompt-route-prefixes={routePrefixes}
                  >
                    {capability.type === "action"
                      ? "Run action"
                      : "Ask assistant"}
                  </button>
                  {capability.targetRoute ? (
                    <span className="rounded-full border border-slate-200 bg-[#f8faf9] px-3 py-1.5 text-[11px] font-semibold text-irongrey/65">
                      {capability.targetRoute}
                    </span>
                  ) : null}
                </div>
              </article>
            );
          })}
          {showAmbientSettingsSpecialist &&
          settingsSpecialistSnapshot?.presetCases.length ? (
            <section
              className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]"
              data-testid="ai-school-shell-mixed-settings-section"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary/70">
                    {settingsSpecialistSnapshot.browserSectionLabel}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-irongrey/65">
                    Cross-module reviews stay here so route-core quick actions
                    remain stable.
                  </p>
                </div>
                <span className="rounded-full border border-slate-200 bg-[#f8faf9] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-irongrey/60">
                  {settingsSpecialistSnapshot.presetCases.length}
                </span>
              </div>
              <div className="space-y-3">
                {settingsSpecialistSnapshot.presetCases.map((entry) => (
                  <article
                    key={`mixed-settings-${entry.id}`}
                    className="rounded-[20px] border border-slate-200 bg-[#f8faf9] px-4 py-3"
                    data-testid="ai-school-shell-mixed-settings-card"
                    data-ai-settings-case-id={entry.id}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">
                          {entry.label}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-irongrey/70">
                          {entry.summary}
                        </p>
                      </div>
                      {entry.badge ? (
                        <span className="rounded-full border border-primary/15 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary/70">
                          {entry.badge}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-[12px] font-semibold text-white shadow-[0_10px_24px_rgba(28,59,62,0.16)] transition-colors hover:bg-primary-hover"
                        onClick={() =>
                          handleSettingsSpecialistCaseClick(entry.id)
                        }
                      >
                        Ask assistant
                      </button>
                      {entry.targetRoute ? (
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-irongrey/65">
                          {entry.targetRoute}
                        </span>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    ),
    [
      handleSettingsSpecialistCaseClick,
      orderedCapabilities,
      resolvedAssistantCapabilities.length,
      runCapabilityAction,
      schoolShellRuntimeSummaryNode,
      settingsSpecialistSnapshot,
      showAmbientSettingsSpecialist,
      submitAssistantQuestion,
    ],
  );
  const prototypeFilesFallbackNote = useMemo(() => {
    if (taskHistoryFallbackStatus === "live") {
      return null;
    }
    if (taskHistoryFallbackStatus === "cached") {
      return `Generated files last loaded${
        taskHistoryFallbackUpdatedAt
          ? ` from ${formatRelativeTaskTime(taskHistoryFallbackUpdatedAt)}`
          : ""
      } while assistant history reloads.`;
    }
    return "Generated files are temporarily unavailable because assistant history could not be loaded.";
  }, [taskHistoryFallbackStatus, taskHistoryFallbackUpdatedAt]);
  const prototypeThreadsFallbackNote = useMemo(() => {
    if (conversationSessionsFallbackStatus === "live") {
      return null;
    }
    if (conversationSessionsFallbackStatus === "cached") {
      return `Saved chats last loaded${
        conversationSessionsFallbackUpdatedAt
          ? ` from ${formatRelativeTaskTime(conversationSessionsFallbackUpdatedAt)}`
          : ""
      } while assistant history reloads.`;
    }
    return "Saved chats are temporarily unavailable because assistant history could not be loaded.";
  }, [
    conversationSessionsFallbackStatus,
    conversationSessionsFallbackUpdatedAt,
  ]);
  const prototypeFilesPaneNode = useMemo(
    () => (
      <div
        className="space-y-0.5 px-2 py-2"
        data-testid="ai-prototype-files-list"
      >
        {prototypeFilesFallbackNote ? (
          <div
            className="mx-1 mb-2 rounded-[14px] border border-[#D8EAE7] bg-[#F8FAF9] px-3 py-2.5 text-[11px] leading-5 text-[#58716D]"
            data-testid="ai-prototype-files-fallback-banner"
          >
            {prototypeFilesFallbackNote}
          </div>
        ) : null}
        {assistantArtifactFamilies.length > 0 ? (
          assistantArtifactFamilies.map((family) => {
            const entry = family.primaryEntry ?? family.members[0] ?? null;
            if (!entry) {
              return null;
            }
            return (
              <div
                key={family.id}
                className="group mx-1"
                data-testid="ai-prototype-file-item"
              >
                <button
                  type="button"
                  className="flex w-full min-w-0 items-center gap-2.5 rounded-[8px] px-[10px] py-3 text-left transition-colors hover:bg-[#F4F6F3]"
                  onClick={() => {
                    if (widthMode !== "full") {
                      onWidthModeChange?.("half");
                    }
                    setPrototypeUtilityPaneOpen(false);
                    entry.onOpen?.();
                  }}
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center text-[#6B7280]">
                    {renderPrototypeUtilityFileIcon(entry.title)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-xs font-semibold text-[#404F4D]"
                      style={{ fontFamily: "Mulish" }}
                    >
                      {entry.title}
                    </div>
                    <div className="text-[10px] text-[#9CA3AF]">
                      {entry.kindLabel} · {entry.updatedAtLabel ?? "Ready now"}
                    </div>
                  </div>
                </button>
              </div>
            );
          })
        ) : isAssistantHomeRoute ? (
          [
            ["admissions_policy_notes.md", "Markdown · Apr 8"],
            ["parent_handbook_2026.docx", "Document · Apr 7"],
            ["admissions_summary.pdf", "PDF · Apr 5"],
            ["enrollment_report_Q1_2026.xlsx", "Spreadsheet · Apr 7"],
          ].map(([title, meta]) => (
            <button
              key={title}
              type="button"
              className="group mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-[6px] px-[10px] py-3 text-left transition-colors hover:bg-[#F4F6F3]"
              data-testid="ai-prototype-file-item"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center text-[#6B7280]">
                <History size={15} />
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="truncate text-xs font-semibold text-[#404F4D]"
                  style={{ fontFamily: "Mulish" }}
                >
                  {title}
                </div>
                <div className="text-[10px] text-[#9CA3AF]">{meta}</div>
              </div>
            </button>
          ))
        ) : (
          <div className="rounded-[14px] border border-dashed border-slate-200 bg-white px-4 py-5 text-sm text-[#6B7280]">
            {taskHistoryFallbackStatus === "unavailable"
              ? "Generated files are temporarily unavailable."
              : "No generated files yet."}
          </div>
        )}
      </div>
    ),
    [
      assistantArtifactFamilies,
      isAssistantHomeRoute,
      onWidthModeChange,
      prototypeFilesFallbackNote,
      setPrototypeUtilityPaneOpen,
      taskHistoryFallbackStatus,
      widthMode,
    ],
  );
  const prototypeParityFilesPaneNode = useMemo(
    () => (
      <div
        className="space-y-0.5 px-2 py-2"
        data-testid="ai-prototype-files-list"
      >
        {[
          ["admissions_policy_notes.md", "12 KB · Apr 8, 2026"],
          ["parent_handbook_2026.docx", "284 KB · Apr 7, 2026"],
          ["admissions_summary.pdf", "340 KB · Apr 5, 2026"],
          ["enrollment_report_Q1_2026.xlsx", "1.2 MB · Apr 7, 2026"],
          ["enrollment_by_division.jsx", "4.1 KB · Apr 8, 2026"],
          ["enrollment_trends.jsx", "3.8 KB · Apr 8, 2026"],
          ["financial_aid_distribution.jsx", "3.2 KB · Apr 8, 2026"],
          ["student_roster.jsx", "5.6 KB · Apr 6, 2026"],
        ].map(([title, meta]) => (
          <button
            key={title}
            type="button"
            className="group mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-[8px] px-[10px] py-3 text-left transition-colors hover:bg-[#F4F6F3]"
            data-testid="ai-prototype-file-item"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center text-[#6B7280]">
              {renderPrototypeUtilityFileIcon(title)}
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="truncate text-xs font-semibold text-[#404F4D]"
                style={{ fontFamily: "Mulish" }}
              >
                {title}
              </div>
              <div className="text-[10px] text-[#9CA3AF]">{meta}</div>
            </div>
          </button>
        ))}
      </div>
    ),
    [
      conversationSessions,
      currentConversationSessionId,
      openConversationSessionFromThreadList,
      selectedThreadsSessionId,
    ],
  );
  const prototypeThreadsPaneNode = useMemo(
    () => (
      <div
        className="space-y-0.5 px-2 py-2"
        data-testid="ai-prototype-threads-list"
      >
        {prototypeThreadsFallbackNote ? (
          <div
            className="mx-1 mb-2 rounded-[14px] border border-[#D8EAE7] bg-[#F8FAF9] px-3 py-2.5 text-[11px] leading-5 text-[#58716D]"
            data-testid="ai-prototype-threads-fallback-banner"
          >
            {prototypeThreadsFallbackNote}
          </div>
        ) : null}
        {conversationSessions.length > 0 ? (
          conversationSessions.map((session, index) => {
            const preview =
              session.firstUserPreview ??
              session.lastAssistantPreview ??
              session.lastMessagePreview ??
              session.title ??
              session.routeLabel ??
              "Assistant session";
            const previewMessageId = session.firstUserPreview
              ? session.firstUserMessageId
              : session.lastAssistantPreview
                ? session.lastAssistantMessageId
                : session.lastMessagePreview
                  ? session.lastMessageId
                  : null;
            const accent =
              PROTOTYPE_THREAD_ACCENTS[index % PROTOTYPE_THREAD_ACCENTS.length];
            const isActive =
              session.id ===
              (selectedThreadsSessionId ?? currentConversationSessionId);
            return (
              <button
                key={session.id}
                type="button"
                onClick={() => {
                  void openConversationSessionFromThreadList(
                    session,
                    previewMessageId,
                  );
                }}
                className={`mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-[8px] px-[10px] py-3 text-left transition-colors ${
                  isActive ? "bg-[#F4F6F3]" : "hover:bg-[#F4F6F3]"
                }`}
                data-testid="ai-prototype-thread-item"
                data-ai-conversation-session-id={session.id}
                data-ai-conversation-preview-message-id={previewMessageId ?? ""}
              >
                <div
                  className="h-7 w-[3px] shrink-0 rounded-full"
                  style={{ background: accent }}
                />
                <div className="min-w-0 flex-1 text-xs text-[#404F4D]">
                  <div className="truncate">{preview}</div>
                </div>
                <span className="shrink-0 text-[10px] text-[#9CA3AF]">
                  {formatPrototypeTimestamp(session.updatedAt)}
                </span>
              </button>
            );
          })
        ) : (
          <div className="rounded-[14px] border border-dashed border-slate-200 bg-white px-4 py-5 text-sm text-[#6B7280]">
            {conversationSessionsFallbackStatus === "unavailable"
              ? "Saved chats are temporarily unavailable."
              : "No saved threads yet."}
          </div>
        )}
      </div>
    ),
    [
      conversationSessions,
      conversationSessionsFallbackStatus,
      currentConversationSessionId,
      openConversationSessionFromThreadList,
      prototypeThreadsFallbackNote,
      selectedThreadsSessionId,
    ],
  );
  const prototypeParityThreadsPaneNode = useMemo(() => {
    if (conversationSessions.length === 0) {
      return (
        <div
          className="space-y-0.5 px-2 py-2"
          data-testid="ai-prototype-threads-list"
        >
          {[
            [
              "Estimate enrollment yield for current cycle",
              "12:32 PM",
              "#2697A2",
            ],
            [
              "Identify pipeline bottlenecks in admissions",
              "11:48 AM",
              "#08BFAE",
            ],
            [
              "Flag at-risk applicants missing documents",
              "Yesterday",
              "#7C9CFF",
            ],
          ].map(([title, time, color]) => (
            <div
              key={title}
              className="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-[8px] bg-[#F4F6F3] px-[10px] py-3 text-left"
              data-testid="ai-prototype-thread-placeholder"
            >
              <div
                className="h-7 w-[3px] shrink-0 rounded-full"
                style={{ background: color }}
              />
              <div className="min-w-0 flex-1 text-xs text-[#404F4D]">
                <div className="truncate">{title}</div>
              </div>
              <span className="shrink-0 text-[10px] text-[#9CA3AF]">
                {time}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        className="space-y-0.5 px-2 py-2"
        data-testid="ai-prototype-threads-list"
      >
        {conversationSessions.map((session, index) => {
          const preview =
            session.firstUserPreview ??
            session.lastAssistantPreview ??
            session.lastMessagePreview ??
            session.title ??
            session.routeLabel ??
            "Assistant session";
          const previewMessageId = session.firstUserPreview
            ? session.firstUserMessageId
            : session.lastAssistantPreview
              ? session.lastAssistantMessageId
              : session.lastMessagePreview
                ? session.lastMessageId
                : null;
          const accent =
            PROTOTYPE_THREAD_ACCENTS[index % PROTOTYPE_THREAD_ACCENTS.length];
          const isActive =
            session.id ===
            (selectedThreadsSessionId ?? currentConversationSessionId);
          return (
            <button
              key={session.id}
              type="button"
              onClick={() => {
                void openConversationSessionFromThreadList(
                  session,
                  previewMessageId,
                );
              }}
              className={`mx-1 flex w-[calc(100%-0.5rem)] items-center gap-2.5 rounded-[8px] px-[10px] py-3 text-left transition-colors ${
                isActive ? "bg-[#F4F6F3]" : "hover:bg-[#F4F6F3]"
              }`}
              data-testid="ai-prototype-thread-item"
              data-ai-conversation-session-id={session.id}
              data-ai-conversation-preview-message-id={previewMessageId ?? ""}
            >
              <div
                className="h-7 w-[3px] shrink-0 rounded-full"
                style={{ background: accent }}
              />
              <div className="min-w-0 flex-1 text-xs text-[#404F4D]">
                <div className="truncate">{preview}</div>
              </div>
              <span className="shrink-0 text-[10px] text-[#9CA3AF]">
                {formatPrototypeTimestamp(session.updatedAt)}
              </span>
            </button>
          );
        })}
      </div>
    );
  }, [
    conversationSessions,
    currentConversationSessionId,
    openConversationSessionFromThreadList,
    selectedThreadsSessionId,
  ]);
  const runtimeIndicator = useMemo(() => {
    const policyMode = runtimePolicyHint?.executionMode ?? "unknown";
    return `Runtime: automatic (policy:${policyMode})`;
  }, [runtimePolicyHint?.executionMode]);
  const capabilitiesAvailabilityNote =
    assistantCatalogLoadErrors.capabilities
      ? " • Capabilities: load error"
      : !assistantCapabilitiesLoading &&
          resolvedAssistantCapabilities.length === 0 &&
          pathname.startsWith("/school/")
        ? " • Capabilities: none returned"
        : "";
  const capabilitiesLoadingNote = assistantCapabilitiesLoading
    ? " • Capabilities: loading"
    : "";
  const schoolShellMetaLine = `Sources: ${dataSourcesLabel} • Filters: ${filtersLabel} • Persona: ${personaDisplay} • ${runtimeIndicator}${capabilitiesLoadingNote}${capabilitiesAvailabilityNote}`;
  const assistantMetaLine = useSchoolShellDesign
    ? `${schoolShellMetaLine}${lastActionFeedback ? ` • Last action: ${lastActionFeedback}` : ""}`
    : `Sources: ${dataSourcesLabel} • Filters: ${filtersLabel} • Persona: ${personaDisplay}${capabilitiesAvailabilityNote}`;
  const currentSpreadsheetComposerKey = useMemo(() => {
    if (activeSpreadsheetTask?.id) {
      return `task:${activeSpreadsheetTask.id}`;
    }
    if (currentSpreadsheetSession?.workbookArtifact.sourceRef) {
      return `session:${currentSpreadsheetSession.workbookArtifact.sourceRef}`;
    }
    if (currentSpreadsheetSession?.workbookArtifact.id) {
      return `session:${currentSpreadsheetSession.workbookArtifact.id}`;
    }
    return null;
  }, [activeSpreadsheetTask?.id, currentSpreadsheetSession]);
  const spreadsheetComposerDismissed = Boolean(
    currentSpreadsheetComposerKey &&
      dismissedSpreadsheetComposerKey === currentSpreadsheetComposerKey,
  );

  const schoolShellModelMenuOptions = useMemo(
    () =>
      SCHOOL_SHELL_MODEL_OPTIONS.map((option) => {
        const meta = resolvePrototypeModelMenuMeta(option);
        return {
          value: option.value,
          label: option.label,
          badge: option.badges[0],
          description:
            meta.available === false
              ? "Unavailable"
              : `${meta.description}. ${meta.latency} latency; ${meta.context} context.`,
        };
      }),
    [],
  );
  const schoolShellModelMenuNode = useMemo(
    () => (
      <AIAssistantModelSelector
        options={schoolShellModelMenuOptions}
        value={modelKey}
        onChange={handleModelSelection}
        disabled={assistantConversation.isLoading}
        label="Model"
        variant="menu"
        menuTestId="ai-school-shell-model-menu"
        optionTestIdPrefix="ai-school-shell-model-option"
      />
    ),
    [
      assistantConversation.isLoading,
      handleModelSelection,
      modelKey,
      schoolShellModelMenuOptions,
    ],
  );

  const admissionsMessageRendererStateRef = useRef({
    askPendingTaskLaunchInChat,
    currentConversationSessionId,
    dismissPendingTaskLaunch,
	    handleAssistantMessageAction,
	    handleTranscriptDisclosureToggle,
	    refreshChartArtifactState,
	    refreshVisualization3DArtifactState,
	    resolveTranscriptDisclosureOpen,
    startPendingTaskLaunch,
  });
  admissionsMessageRendererStateRef.current = {
    askPendingTaskLaunchInChat,
    currentConversationSessionId,
    dismissPendingTaskLaunch,
	    handleAssistantMessageAction,
	    handleTranscriptDisclosureToggle,
	    refreshChartArtifactState,
	    refreshVisualization3DArtifactState,
	    resolveTranscriptDisclosureOpen,
    startPendingTaskLaunch,
  };

  const AdmissionsMessageWithThreadActions = useMemo(() => {
    const StableAdmissionsMessageWithThreadActions = (
      props: AIAssistantMessageProps<
        AdmissionsCitation,
        AdmissionsAIContextualAction
      >,
    ) => {
      const current = admissionsMessageRendererStateRef.current;
      return (
        <AdmissionsAssistantMessage
          {...props}
          onAction={(action) =>
            current.handleAssistantMessageAction(action, props.onAction)
          }
          onTaskProposalStart={current.startPendingTaskLaunch}
          onTaskProposalAskInChat={current.askPendingTaskLaunchInChat}
          onTaskProposalDismiss={current.dismissPendingTaskLaunch}
          resolveDisclosureOpen={current.resolveTranscriptDisclosureOpen}
          onDisclosureToggle={current.handleTranscriptDisclosureToggle}
          disclosureSessionId={current.currentConversationSessionId}
	          resolveChartArtifactState={current.refreshChartArtifactState}
	          resolveVisualization3DArtifactState={
	            current.refreshVisualization3DArtifactState
	          }
          googleWorkspaceBridgeStatus={{
            connected: spreadsheetBridgeConnected,
            supportedEnvironment: spreadsheetBridgeSupportedEnvironment,
            statusLabel: spreadsheetBridgeStatusLabel,
          }}
	        />
      );
    };

    StableAdmissionsMessageWithThreadActions.displayName =
      "StableAdmissionsMessageWithThreadActions";
    return StableAdmissionsMessageWithThreadActions;
  }, [
    spreadsheetBridgeConnected,
    spreadsheetBridgeStatusLabel,
    spreadsheetBridgeSupportedEnvironment,
  ]);

  const assistantComponents = useMemo(
    () => ({
      Message: AdmissionsMessageWithThreadActions,
    }),
    [AdmissionsMessageWithThreadActions],
  );

  const renderCitationDetail = useCallback(
    (citation: AdmissionsCitation) => (
      <AssistantCitationDetail citation={citation} />
    ),
    [],
  );

  const proactiveTrayNode =
    useSchoolShellDesign && activeProactivePromptEntry ? (
      <AssistantProactiveTray
        workItem={activeProactivePromptEntry}
        isCollapsed={proactiveTray.isCollapsed}
        onToggleCollapsed={() =>
          proactiveTray.toggleCollapsed(activeProactivePromptEntry.id)
        }
        onDismiss={clearActiveProactivePrompt}
        onPrimaryAction={() => submitProactivePrompt({})}
        onSuggestedAction={handleProactiveSuggestedAction}
      />
    ) : null;
  const toolToolbarNode =
    useSchoolShellDesign &&
    (toolbarTools.length > 0 || toolToolbarPreferences.hiddenAll) ? (
      <AssistantToolToolbar
        routeLabel={isAssistantHomeRoute ? "this account" : moduleConfig.label}
        tools={toolbarTools}
        isCollapsed={toolToolbarPreferences.collapsed}
        hiddenAll={toolToolbarPreferences.hiddenAll}
        onToggleCollapsed={toggleToolToolbarCollapsed}
        onHideTool={hideToolFromToolbar}
        onHideAll={hideAllToolsFromToolbar}
        onShowAll={showAllToolsInToolbar}
        onToolClick={handleToolToolbarClick}
      />
    ) : null;
  const spreadsheetComposerStatusNode =
    !useSchoolShellDesign &&
    (activeSpreadsheetTask || currentSpreadsheetSession) &&
    !spreadsheetComposerDismissed ? (
      <AssistantSpreadsheetComposerStatus
        activeTask={activeSpreadsheetTask}
        session={
          currentSpreadsheetSession
            ? {
                title: currentSpreadsheetSession.workbookArtifact.label,
                workspaceOpen: Boolean(activeSpreadsheetWorkspace),
              }
            : null
        }
        onOpenWorkspace={
          currentSpreadsheetSession && !activeSpreadsheetWorkspace
            ? () => {
                openSpreadsheetWorkspace(currentSpreadsheetSession);
              }
            : null
        }
        onDismiss={
          currentSpreadsheetComposerKey
            ? () => {
                setDismissedSpreadsheetComposerKey(
                  currentSpreadsheetComposerKey,
                );
              }
            : null
        }
      />
    ) : null;
  const capabilityCatalogButtonNode =
    resolvedAssistantCapabilities.length > 0 ||
    toolCatalogSections.length > 0 ||
    filteredSettingsSpecialistCases.length > 0 ? (
      <div
        className="flex items-center justify-between rounded-[20px] border border-primary/10 bg-white/90 px-3 py-2 shadow-[0_12px_28px_rgba(36,50,53,0.05)]"
        data-testid="ai-capability-catalog-launcher"
      >
        <div className="min-w-0 pr-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/70">
            Browser
          </p>
          <p className="truncate text-[11px] text-irongrey/70">
            {capabilityCatalogTotalCount} prompts, actions, and tools
            available to this account
          </p>
        </div>
        <button
          type="button"
          className="rounded-full border border-primary/15 bg-primary/5 px-3 py-1.5 text-[11px] font-semibold text-primary shadow-[0_8px_18px_rgba(36,50,53,0.04)] transition-colors hover:bg-primary hover:text-white"
          onClick={() => setShowCapabilityCatalog(true)}
          data-testid="ai-capability-catalog-open-button"
        >
          Browse all
        </button>
      </div>
    ) : null;
  const primaryAccessoryNode =
    spreadsheetComposerStatusNode ?? capabilityCatalogButtonNode;
  const deferredBrowserLauncherNode = spreadsheetComposerStatusNode
    ? capabilityCatalogButtonNode
    : null;
  const composerAccessoryNode =
    primaryAccessoryNode ||
    proactiveTrayNode ||
    toolToolbarNode ||
    deferredBrowserLauncherNode ? (
      <div className="space-y-2">
        {!useSchoolShellDesign && settingsSpecialistLeadNode
          ? settingsSpecialistLeadNode
          : null}
        {primaryAccessoryNode}
        {proactiveTrayNode}
        {toolToolbarNode}
        {deferredBrowserLauncherNode}
      </div>
    ) : !useSchoolShellDesign && settingsSpecialistLeadNode ? (
      <div className="space-y-2">{settingsSpecialistLeadNode}</div>
    ) : null;
  const schoolShellActionsChestNode =
    capabilityCatalogButtonNode || proactiveTrayNode || toolToolbarNode ? (
      <div className="space-y-2">
        {capabilityCatalogButtonNode}
        {proactiveTrayNode}
        {toolToolbarNode}
      </div>
    ) : null;
  const schoolShellBackgroundTasksChestNode =
    taskShelfActiveRecords.length > 0 || taskShelfRecentRecords.length > 0 ? (
      <div className="rounded-[20px] border border-slate-200 bg-white px-3 py-3 shadow-[0_10px_26px_rgba(36,50,53,0.05)]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary/70">
          Task lane
        </p>
        <p className="mt-2 text-sm text-slate-900">
          {taskShelfActiveRecords.length > 0
            ? `${taskShelfActiveRecords.length} active background task${taskShelfActiveRecords.length === 1 ? "" : "s"} running`
            : `${taskShelfRecentRecords.length} recent task${taskShelfRecentRecords.length === 1 ? "" : "s"} available`}
        </p>
        <p className="mt-1 text-xs leading-5 text-irongrey/62">
          Use the conversation and History surfaces for task detail, progress,
          inline artifact previews, and reopen actions.
        </p>
      </div>
    ) : null;
  const schoolShellContextMenuNode = useMemo(
    () => <div className="pb-1 pt-1">{schoolShellModelMenuNode}</div>,
    [schoolShellModelMenuNode],
  );
  const prototypeShellContextNavigationTabs = useMemo(
    () => [
      {
        id: "chat" as const,
        label: "Chat",
        badgeCount:
          assistantConversation.messages.length > 0
            ? assistantConversation.messages.length
            : undefined,
      },
      {
        id: "history" as const,
        label: "History",
        badgeCount:
          conversationSessions.length > 0
            ? conversationSessions.length
            : undefined,
      },
      {
        id: "navigate" as const,
        label: "Navigate",
        badgeCount:
          prompts.length > 0 ? Math.min(prompts.length, 5) : undefined,
      },
    ],
    [
      assistantConversation.messages.length,
      conversationSessions.length,
      prompts.length,
    ],
  );
  const prototypeShellModelMenuNode = useMemo(
    () => (
      <AIAssistantSchoolShellContextMenuContent
        metaLine={assistantMetaLine}
        headerActions={schoolShellContextMenuNode}
        navigationTabs={prototypeShellContextNavigationTabs}
        currentView={assistantSurfaceView}
        onActivateTab={setAssistantSurfaceView}
      />
    ),
    [
      assistantMetaLine,
      assistantSurfaceView,
      prototypeShellContextNavigationTabs,
      schoolShellContextMenuNode,
    ],
  );
  const reportGenerationWorkspaceNode =
    assistantReportGenerationProofMode &&
    reportGenerationWorkspaceModel.workspace.isOpen ? (
    <ReportGenerationWorkspace model={reportGenerationWorkspaceModel} />
  ) : null;
  const schoolShellWorkspaceView = useMemo<
    AIAssistantWorkspaceState["workspace"]
  >(() => {
    if (!useSchoolShellDesign) {
      return null;
    }
    if (
      assistantReportGenerationProofMode &&
      reportGenerationWorkspaceModel.workspace.isOpen
    ) {
      return {
        title: reportGenerationWorkspaceModel.resolvedTitle,
        subtitle: "Report builder workspace",
        backLabel: "Back to assistant",
        content: reportGenerationWorkspaceNode,
      };
    }
	    if (activeVisualization3DWorkspace) {
	      return {
	        title: activeVisualization3DWorkspace.visualization3DArtifact.label,
	        subtitle: "3D visualization workspace",
	        backLabel: "Back to chat",
	        content: (
	          <AssistantVisualization3DWorkspace
	            visualization3DArtifact={
	              activeVisualization3DWorkspace.visualization3DArtifact
	            }
	            initialSpace={activeVisualization3DWorkspace.visualization3DSpace}
	            initialPanels={activeVisualization3DWorkspace.visualization3DPanels}
	            onClose={closeVisualization3DWorkspace}
	            presentation="embedded"
	            resolveVisualization3DArtifactState={
	              refreshVisualization3DArtifactState
	            }
	          />
	        ),
	      };
	    }
	    return null;
	  }, [
	    activeVisualization3DWorkspace,
    assistantReportGenerationProofMode,
	    closeVisualization3DWorkspace,
	    refreshVisualization3DArtifactState,
    reportGenerationWorkspaceModel.resolvedTitle,
    reportGenerationWorkspaceModel.workspace.isOpen,
    reportGenerationWorkspaceNode,
    useSchoolShellDesign,
  ]);
  const prototypePreviewPane =
    useMemo<AIAssistantPrototypePreviewPane | null>(() => {
      if (!schoolShellWorkspaceView) {
        return null;
      }
      return {
        title: schoolShellWorkspaceView.title,
        content: schoolShellWorkspaceView.content,
        onBack: closeActiveWorkspace,
      };
    }, [closeActiveWorkspace, schoolShellWorkspaceView]);
  const prototypeParityInlinePreviewPane =
    useMemo<AIAssistantPrototypePreviewPane>(
      () => ({
        title: "admissions_policy_notes.md",
        content: (
          <div className="min-h-0 flex-1 overflow-auto bg-[#F8FAF9] p-4">
            <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-[0_0px_3px_2px_rgba(206,242,237,0.39)]">
              <h3
                className="text-sm font-semibold text-[#404F4D]"
                style={{ fontFamily: "Mulish" }}
              >
                Admissions Policy Notes — 2026–2027
              </h3>
              <p className="mt-2 text-xs leading-6 text-[#6B7280]">
                Last updated: April 5, 2026 · Prepared by the Admissions
                Committee
              </p>
            </div>
          </div>
        ),
        onBack: undefined,
      }),
      [],
    );
  const prototypeModelLabel = useMemo(
    () =>
      SCHOOL_SHELL_MODEL_OPTIONS.find((option) => option.value === modelKey)
        ?.label ?? String(modelKey),
    [modelKey],
  );
  const effectivePrototypeParityState =
    prototypeParityState ??
    (reportProofPreset
      ? {
          widthMode: "full" as const,
          surface: "full-chat" as const,
        }
      : null);
  const hasAssistantTranscriptActivity =
    assistantConversation.messages.length > 0 ||
    assistantConversation.pendingQuestion !== null ||
    assistantConversation.isLoading;
  const runtimePrototypeSceneState =
    effectivePrototypeParityState ??
    (fullPageAssistant
      ? {
          surface: hasAssistantTranscriptActivity
            ? ("full-chat" as const)
            : ("full-welcome" as const),
        }
      : null);
  const resolvedPrototypeFilesPaneNode = effectivePrototypeParityState
    ? prototypeParityFilesPaneNode
    : prototypeFilesPaneNode;
  const resolvedPrototypeThreadsPaneNode = effectivePrototypeParityState
    ? prototypeParityThreadsPaneNode
    : prototypeThreadsPaneNode;
  const resolvedPrototypeWidthMode =
    runtimePrototypeSceneState?.widthMode ?? widthMode;
  const handlePrototypeSurfaceChange = useCallback(
    (surface: AIAssistantPrototypeSurface) => {
      if (!effectivePrototypeParityState) {
        return;
      }
      setPrototypeParityState((current) => ({
        ...(current ?? effectivePrototypeParityState),
        surface,
      }));
    },
    [effectivePrototypeParityState],
  );
  const handlePrototypeUtilityTabChange = useCallback(
    (utilityTab: AIAssistantPrototypeUtilityTab) => {
      if (!effectivePrototypeParityState) {
        setPrototypeUtilityTab(utilityTab);
        return;
      }
      setPrototypeParityState((current) => ({
        ...(current ?? effectivePrototypeParityState),
        utilityTab,
      }));
    },
    [effectivePrototypeParityState],
  );
  const handlePrototypeUtilityPaneOpenChange = useCallback(
    (utilityPaneOpen: boolean) => {
      if (!effectivePrototypeParityState) {
        setPrototypeUtilityPaneOpen(utilityPaneOpen);
        return;
      }
      setPrototypeParityState((current) => ({
        ...(current ?? effectivePrototypeParityState),
        utilityPaneOpen,
      }));
    },
    [effectivePrototypeParityState],
  );
  const handlePrototypeWidthMenuOpenChange = useCallback(
    (open: boolean) => {
      if (!effectivePrototypeParityState) {
        return;
      }
      setPrototypeParityState((current) => ({
        ...(current ?? effectivePrototypeParityState),
        widthMenuOpen: open,
      }));
    },
    [effectivePrototypeParityState],
  );
  const resolvedPrototypePreviewPane =
    runtimePrototypeSceneState?.previewPane === "inline-summary"
      ? prototypeParityInlinePreviewPane
      : runtimePrototypeSceneState?.previewPane === "workspace"
        ? prototypePreviewPane
        : prototypePreviewPane;
  const effectivePrototypeUtilityTab =
    effectivePrototypeParityState?.utilityTab ?? prototypeUtilityTab;
  const effectivePrototypeUtilityPaneOpen =
    effectivePrototypeParityState?.utilityPaneOpen ?? prototypeUtilityPaneOpen;
  const prototypeTitleView = useMemo<PrototypeTitleView>(() => {
    if (resolvedPrototypePreviewPane) {
      return "preview";
    }
    if (effectivePrototypeUtilityPaneOpen) {
      return effectivePrototypeUtilityTab === "threads" ? "threads" : "files";
    }
    if (hasAssistantTranscriptActivity) {
      return "chat";
    }
    return "home";
  }, [
    effectivePrototypeUtilityPaneOpen,
    effectivePrototypeUtilityTab,
    hasAssistantTranscriptActivity,
    resolvedPrototypePreviewPane,
  ]);
  const showPrototypeTitleBackButton = prototypeTitleView !== "home";
  const handlePrototypeResetConversation = useCallback(() => {
    closeActiveWorkspace();
    handlePrototypeUtilityPaneOpenChange(false);
    setAssistantSurfaceView(DEFAULT_ASSISTANT_PANEL_VIEW);
    assistantConversation.resetConversation();
  }, [
    assistantConversation,
    closeActiveWorkspace,
    handlePrototypeUtilityPaneOpenChange,
  ]);
  const handlePrototypeTitleBack = useCallback(() => {
    if (prototypeTitleView === "preview") {
      if (effectivePrototypeParityState?.previewPane) {
        setPrototypeParityState((current) => ({
          ...(current ?? effectivePrototypeParityState),
          previewPane: null,
        }));
        return;
      }
      closeActiveWorkspace();
      return;
    }
    if (prototypeTitleView === "files" || prototypeTitleView === "threads") {
      handlePrototypeUtilityPaneOpenChange(false);
      return;
    }
    if (prototypeTitleView === "chat") {
      closeActiveWorkspace();
      handlePrototypeUtilityPaneOpenChange(false);
      setAssistantSurfaceView(DEFAULT_ASSISTANT_PANEL_VIEW);
      if (effectivePrototypeParityState) {
        setPrototypeParityState((current) => ({
          ...(current ?? effectivePrototypeParityState),
          surface:
            resolvedPrototypeWidthMode === "full"
              ? "full-welcome"
              : "rail-chat",
          utilityPaneOpen: false,
          previewPane: null,
        }));
      }
      assistantConversation.resetConversation();
    }
  }, [
    assistantConversation,
    closeActiveWorkspace,
    effectivePrototypeParityState,
    handlePrototypeUtilityPaneOpenChange,
    prototypeTitleView,
    resolvedPrototypeWidthMode,
  ]);
  const assistantHomeRouteCanvasVisible = Boolean(
    fullPageAssistant &&
      isAssistantHomeRoute &&
      (effectivePrototypeParityState?.surface === "full-welcome" ||
        (!effectivePrototypeParityState &&
          !hasAssistantTranscriptActivity &&
          !schoolShellWorkspaceView)),
  );
  const prototypeConversationPhase = useMemo(() => {
    if (
      effectivePrototypeParityState?.surface === "full-chat" &&
      assistantConversation.isLoading
    ) {
      return "thinking" as const;
    }
    if (assistantConversation.pendingQuestion) {
      return "thinking" as const;
    }
    const hasAssistantMessage = assistantConversation.messages.some(
      (message) => message.role === "assistant",
    );
    return hasAssistantMessage ? ("settled" as const) : ("idle" as const);
  }, [
    assistantConversation.isLoading,
    assistantConversation.messages,
    assistantConversation.pendingQuestion,
    effectivePrototypeParityState?.surface,
  ]);
  const prototypeWorkspaceKind =
    activeWorkspaceKind ??
    (resolvedPrototypePreviewPane ? "artifact-preview" : "chat");
  const { scene: prototypeShellScene, events: prototypeShellEvents } =
    useAssistantSceneController({
      widthMode: resolvedPrototypeWidthMode,
      onWidthModeChange,
      onSurfaceChange: effectivePrototypeParityState
        ? handlePrototypeSurfaceChange
        : undefined,
      onUtilityTabChange: effectivePrototypeParityState
        ? handlePrototypeUtilityTabChange
        : undefined,
      onUtilityPaneOpenChange: effectivePrototypeParityState
        ? handlePrototypeUtilityPaneOpenChange
        : undefined,
      onWidthMenuOpenChange: effectivePrototypeParityState
        ? handlePrototypeWidthMenuOpenChange
        : undefined,
      parityState: runtimePrototypeSceneState,
      previewPane: resolvedPrototypePreviewPane,
      workspaceKind: prototypeWorkspaceKind,
      conversationPhase: prototypeConversationPhase,
      onClose: onRequestClose,
      onResetConversation: assistantConversation.resetConversation,
      onWelcomeSubmit: assistantConversation.submitQuestion,
    });
  const workspaceChromeNode = null;
  const taskShelfNode =
    !useSchoolShellDesign &&
    (taskShelfActiveRecords.length > 0 || taskShelfRecentRecords.length > 0) ? (
      <IntellioTaskShelf
        activeTasks={taskShelfActiveRecords}
        recentTasks={taskShelfRecentRecords}
        completedCollapsed={completedTasksCollapsed}
        onToggleCompletedCollapsed={() =>
          setCompletedTasksCollapsed((current) => !current)
        }
        onCancelTask={(taskId) => {
          const task =
            activeTaskItems.find((entry) => entry.id === taskId) ??
            recentTaskItems.find((entry) => entry.id === taskId) ??
            null;
          void cancelBackgroundTask(task?.runId ?? null);
        }}
        onConfirmTask={(taskId) => {
          const task =
            activeTaskItems.find((entry) => entry.id === taskId) ??
            recentTaskItems.find((entry) => entry.id === taskId) ??
            null;
          void confirmBackgroundTask(task?.runId ?? null);
        }}
      />
    ) : null;
  const collapsedTaskLauncherChipNode =
    !useSchoolShellDesign &&
    panelState.isOverlay &&
    !panelState.isExpanded &&
    taskShelfActiveRecords.length > 0 ? (
      <button
        type="button"
        className="fixed bottom-24 right-5 z-[210] flex items-center gap-2 rounded-full border border-primary/20 bg-white/95 px-4 py-2 text-left shadow-[0_18px_40px_rgba(36,50,53,0.18)] backdrop-blur"
        onClick={() => panelState.setExpanded(true)}
        data-testid="ai-task-launcher-chip"
      >
        <span className="inline-flex h-2.5 w-2.5 rounded-full bg-gradient-to-r from-primary to-jungle" />
        <span className="text-[11px] font-semibold text-slate-900">
          {taskShelfActiveRecords.length} active task
          {taskShelfActiveRecords.length === 1 ? "" : "s"}
        </span>
      </button>
    ) : null;
  const spreadsheetWorkspaceNode =
    activeSpreadsheetWorkspace ? (
      <AssistantSpreadsheetWorkspace
        workbookArtifact={activeSpreadsheetWorkspace.workbookArtifact}
        spreadsheetDownloadArtifact={
          activeSpreadsheetWorkspace.spreadsheetDownloadArtifact
        }
        bridgeConnected={spreadsheetBridgeConnected}
        bridgeStatusLabel={spreadsheetBridgeStatusLabel}
        bridgeState={spreadsheetBridgeState}
        bridgeSupportedEnvironment={spreadsheetBridgeSupportedEnvironment}
        bridgePresets={spreadsheetBridgePresets}
        exportState={spreadsheetExportState}
        exportMessage={spreadsheetExportMessage}
        onExport={(input) => void exportSpreadsheetWorkspace(input)}
        onClose={closeSpreadsheetWorkspace}
        onStartBridgeAuth={() => void startSpreadsheetBridgeAuth()}
        onDisconnectBridge={() => void disconnectSpreadsheetBridge()}
        onSavePreset={(input) => void saveSpreadsheetExportPreset(input)}
        onDeletePreset={(input) => void deleteSpreadsheetExportPreset(input)}
        onSessionChange={handleSpreadsheetWorkspaceSessionChange}
      />
    ) : null;
	  const chartWorkspaceNode =
	    activeChartWorkspace ? (
	      <AssistantChartWorkspace
        chartArtifact={activeChartWorkspace.chartArtifact}
        initialCharts={activeChartWorkspace.chartPanels}
        onClose={closeChartWorkspace}
        resolveChartArtifactState={refreshChartArtifactState}
	      />
	    ) : null;
	  const visualization3DWorkspaceNode =
	    !useSchoolShellDesign && activeVisualization3DWorkspace ? (
	      <AssistantVisualization3DWorkspace
	        visualization3DArtifact={
	          activeVisualization3DWorkspace.visualization3DArtifact
	        }
	        initialSpace={activeVisualization3DWorkspace.visualization3DSpace}
	        initialPanels={activeVisualization3DWorkspace.visualization3DPanels}
	        onClose={closeVisualization3DWorkspace}
	        resolveVisualization3DArtifactState={refreshVisualization3DArtifactState}
	      />
	    ) : null;
	  const capabilityCatalogNode = (
    <AssistantCapabilityBrowserModal
      open={showCapabilityCatalog}
      query={capabilityCatalogQuery}
      totalCount={capabilityCatalogTotalCount}
      onClose={() => setShowCapabilityCatalog(false)}
      onQueryChange={setCapabilityCatalogQuery}
      sections={capabilityCatalogSections}
      toolSections={toolCatalogSections}
      promptSections={settingsSpecialistPromptSections}
      getPrimaryAction={getCapabilityCatalogPrimaryAction}
      onPrimaryAction={handleCapabilityCatalogPrimaryAction}
      onToolAction={(tool) => {
        setShowCapabilityCatalog(false);
        setCapabilityCatalogQuery("");
        handleToolToolbarClick(tool);
      }}
      onPromptAction={handleSettingsSpecialistCaseClick}
    />
  );
  const schoolShellAssistantWorkspace = useMemo<AIAssistantWorkspaceState>(
    () => ({
      defaultTab: "chat",
      chromeMode: "prototype-minimal",
      // The standalone all-artifacts panel is intentionally disabled. Artifacts
      // must stay inline in chat/history messages unless visual authority is
      // explicitly updated to reintroduce a separate browser.
      history: {
        content: schoolShellHistoryPanelNode,
      },
      navigate: {
        content: schoolShellNavigatePanelNode,
      },
      workspace: schoolShellWorkspaceView,
      composerChest: {
        collapsedRailTabs: ["history"],
        sections: [
          {
            id: "actions",
            title: "Actions",
            content: schoolShellActionsChestNode,
          },
          {
            id: "background-tasks",
            title: "Background tasks",
            content: schoolShellBackgroundTasksChestNode,
          },
        ],
      },
    }),
    [
      schoolShellActionsChestNode,
      schoolShellBackgroundTasksChestNode,
      schoolShellHistoryPanelNode,
      schoolShellNavigatePanelNode,
      schoolShellWorkspaceView,
    ],
  );
  const assistantToastNotification = useMemo(
    () =>
      assistantResponseFixtureState?.toastNotification ??
      (feedbackToastMessage
        ? {
            message: feedbackToastMessage,
            tone: "success" as const,
          }
        : null),
    [assistantResponseFixtureState?.toastNotification, feedbackToastMessage],
  );
  const assistantInputBanner =
    assistantResponseFixtureState?.inputBanner ?? null;
  const assistantStagedAttachments =
    assistantResponseFixtureState?.stagedAttachments ?? [];

  const prototypeChatAssistantNode = useSchoolShellDesign ? (
    <AIAssistant<AdmissionsCitation, AdmissionsAIContextualAction>
      branding={{
        title: "Assistant",
      }}
      strings={{
        greeting,
        inputPlaceholder,
        inputActionsTitle: "Quick actions",
      }}
      metaLine={assistantMetaLine}
      prompts={prompts}
      onPromptClick={handlePromptClick}
      conversation={assistantConversation}
      inputActions={inputActions}
      onInputAction={handleAssistantInputAction}
      onCitationClick={onCitationClick}
      citationRenderer={renderCitationDetail}
      citationTitle="Source details"
      citationDisplayMode="inline_sources"
      contextSummary={resolvedContextSummary}
      layout={layout}
      defaultExpanded={defaultExpanded}
      className={className}
      panelState={panelState}
      onRequestClose={onRequestClose}
      onAttachFile={handleAttachFile}
      composerAccessory={spreadsheetComposerStatusNode}
      conversationLeadContent={schoolShellConversationLeadNode}
      headerActions={schoolShellContextMenuNode}
      assistantWorkspace={schoolShellAssistantWorkspace}
      schoolShellView={assistantSurfaceView}
      onSchoolShellViewChange={setAssistantSurfaceView}
      variant="school-shell"
      recentItems={schoolShellRecentItems}
      stagedAttachments={assistantStagedAttachments}
      inputBanner={assistantInputBanner}
      toastNotification={assistantToastNotification}
      schoolShellModelLabel={prototypeModelLabel}
      activeRouteContext={activeRouteAssistantContext?.activeRouteContext ?? null}
      onAssistantPolicyEvent={activeRouteAssistantContext?.onAssistantPolicyEvent ?? undefined}
      onOpenArtifactInGoogleSheets={openAssistantArtifactInGoogleSheets}
      onRecentItemClick={handleSchoolShellRecentItemClick}
      showSchoolShellHeader={false}
      components={assistantComponents}
    />
  ) : null;
  const prototypeShellNode = runtimePrototypeSceneState ? (
    <AIAssistantPrototypeShell
      scene={prototypeShellScene}
      events={prototypeShellEvents}
      widthMode={resolvedPrototypeWidthMode}
      onWidthModeChange={onWidthModeChange}
      surface={runtimePrototypeSceneState.surface}
      onClose={onRequestClose}
      chatPane={prototypeChatAssistantNode}
      filesPane={resolvedPrototypeFilesPaneNode}
      threadsPane={resolvedPrototypeThreadsPaneNode}
      utilityTab={
        effectivePrototypeParityState?.utilityTab ?? prototypeUtilityTab
      }
      onUtilityTabChange={
        effectivePrototypeParityState
          ? undefined
          : handlePrototypeUtilityTabChange
      }
      utilityPaneOpen={
        effectivePrototypeParityState?.utilityPaneOpen ??
        prototypeUtilityPaneOpen
      }
      onUtilityPaneOpenChange={
        effectivePrototypeParityState
          ? undefined
          : handlePrototypeUtilityPaneOpenChange
      }
      previewPane={useSchoolShellDesign ? null : resolvedPrototypePreviewPane}
      modelLabel={prototypeModelLabel}
      modelMenu={prototypeShellModelMenuNode}
      widthMenuOpen={runtimePrototypeSceneState.widthMenuOpen}
      onResetConversation={handlePrototypeResetConversation}
      onWelcomeSubmit={assistantConversation.submitQuestion}
      assistantLabel="Assistant"
      showTitleBackButton={showPrototypeTitleBackButton}
      onTitleBack={handlePrototypeTitleBack}
      workspaceLabel={moduleConfig.label}
      welcomeHeading={welcomeHeading}
      fullPageChromeMode={fullPageAssistant ? "body-only" : "default"}
    />
  ) : (
    <AIAssistantPrototypeShell
      widthMode={resolvedPrototypeWidthMode}
      onWidthModeChange={onWidthModeChange}
      onClose={onRequestClose}
      chatPane={prototypeChatAssistantNode}
      filesPane={resolvedPrototypeFilesPaneNode}
      threadsPane={resolvedPrototypeThreadsPaneNode}
      utilityTab={prototypeUtilityTab}
      onUtilityTabChange={handlePrototypeUtilityTabChange}
      utilityPaneOpen={prototypeUtilityPaneOpen}
      onUtilityPaneOpenChange={handlePrototypeUtilityPaneOpenChange}
      previewPane={useSchoolShellDesign ? null : resolvedPrototypePreviewPane}
      modelLabel={prototypeModelLabel}
      modelMenu={prototypeShellModelMenuNode}
      onResetConversation={handlePrototypeResetConversation}
      onWelcomeSubmit={assistantConversation.submitQuestion}
      assistantLabel="Assistant"
      showTitleBackButton={showPrototypeTitleBackButton}
      onTitleBack={handlePrototypeTitleBack}
      workspaceLabel={moduleConfig.label}
      welcomeHeading={welcomeHeading}
      initialWelcomeVisible={Boolean(
        fullPageAssistant &&
          !isAssistantHomeRoute &&
          !hasAssistantTranscriptActivity &&
          resolvedPrototypeWidthMode === "full",
      )}
      fullPageChromeMode={fullPageAssistant ? "body-only" : "default"}
    />
  );
  const assistantNode = useSchoolShellDesign ? (
    fullPageAssistant && isAssistantHomeRoute ? (
      <div
        className="flex h-full min-h-full flex-1 flex-col overflow-hidden bg-[#F8FAF9]"
        data-testid="ai-assistant-route-authority-scope"
        data-extracted-from="owned:ystnj/assistant/route-authority-scope"
      >
        {assistantHomeRouteCanvasVisible ? (
          <AIAssistantPrototypeRouteCanvas
            assistantLabel="Assistant"
            quickActions={prompts.slice(0, 4)}
            recentItems={schoolShellRecentItems}
            modelLabel={prototypeModelLabel}
            modelMenu={prototypeShellModelMenuNode}
            welcomeHeading={welcomeHeading}
            onSubmit={assistantConversation.submitQuestion}
            onAttachFile={handleAttachFile}
            onRecentItemClick={handleSchoolShellRecentItemClick}
          />
        ) : (
          prototypeShellNode
        )}
      </div>
    ) : (
      prototypeShellNode
    )
  ) : (
    <AIAssistant<AdmissionsCitation, AdmissionsAIContextualAction>
      branding={{
        title: "Intellio Assistant",
        subtitle: headerSubtitle,
        icon: <Bot size={18} />,
      }}
      strings={{
        greeting,
        inputPlaceholder,
        inputActionsTitle: "Quick actions",
      }}
      metaLine={assistantMetaLine}
      prompts={prompts}
      onPromptClick={handlePromptClick}
      conversation={assistantConversation}
      inputActions={inputActions}
      onInputAction={handleAssistantInputAction}
      onCitationClick={onCitationClick}
      citationRenderer={renderCitationDetail}
      citationTitle="Source details"
      citationDisplayMode="inline_sources"
      contextSummary={resolvedContextSummary}
      layout={layout}
      defaultExpanded={defaultExpanded}
      className={className}
      panelState={panelState}
      onRequestClose={onRequestClose}
      onAttachFile={handleAttachFile}
      composerAccessory={composerAccessoryNode}
      conversationLeadContent={schoolShellConversationLeadNode}
      headerActions={modelSelectorNode}
      toastNotification={assistantToastNotification}
      activeRouteContext={activeRouteAssistantContext?.activeRouteContext ?? null}
      onAssistantPolicyEvent={activeRouteAssistantContext?.onAssistantPolicyEvent ?? undefined}
      onOpenArtifactInGoogleSheets={openAssistantArtifactInGoogleSheets}
      variant="default"
      components={assistantComponents}
    />
  );

  return (
    <DesignSystemProvider designSystem="tailwind">
      <div className="relative flex h-full min-h-0 flex-1 flex-col">
        {workspaceChromeNode}
        {taskShelfNode}
        <div className="min-h-0 flex-1">{assistantNode}</div>
        {capabilityCatalogNode}
	        {collapsedTaskLauncherChipNode}
	        {spreadsheetWorkspaceNode}
	        {chartWorkspaceNode}
	        {visualization3DWorkspaceNode}
	      </div>
    </DesignSystemProvider>
  );
}
